import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { SourceBadge } from '../components/SourceBadge';
import { LogOut, UserCheck, Users, Clock, CheckCircle, AlertCircle, Baby, User, Settings, FileText, Pill, Thermometer, ClipboardList, BarChart3, Activity, Heart, Droplets, Timer, TrendingUp, X, Zap, ArrowUp, ArrowDown, Target, Download, Calendar, FileDown, PlayCircle, Award, Hash, CalendarIcon, Undo2, History, ChevronLeft, ChevronRight, Play, Power, FastForward, Syringe, Banknote, IndianRupee, Receipt, Wallet, Check, ChevronDown, Menu, Bell, MapPin, Tv, Printer, Smile, Meh, Frown, Edit } from 'lucide-react';
import DoctorSettings from '@/components/DoctorSettings';
import LiveClock from '@/components/LiveClock';
import MedicalCertificateModal from '@/components/MedicalCertificateModal';
import PrescriptionModal from '@/components/PrescriptionModal';
import PromoSettings from '@/components/PromoSettings';
import { format } from 'date-fns';
import { safePrintWindow, escapeHtml } from '../utils/printUtils';
import { VITAL_FIELDS, isVitalAbnormal, isPaedsPatient, vitalChipClass } from '../utils/vitals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CLINIC_LOGO = 'https://customer-assets.emergentagent.com/job_bfad35dd-1d63-4524-ae59-d9c9812969d6/artifacts/e9pbdmoo_B3263100-FCB9-4C56-9C4C-F3DA7CC292D8.PNG';

const DoctorDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [daySummary, setDaySummary] = useState({ total: 0, completed: 0, missed: 0 });
  const [previousDaySummary, setPreviousDaySummary] = useState(null);
  const [showPreviousDaySummary, setShowPreviousDaySummary] = useState(false);
  const [customDateSummary, setCustomDateSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEndDaySummary, setShowEndDaySummary] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [onDutyDoctor, setOnDutyDoctor] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [showTVSettings, setShowTVSettings] = useState(false);
  const [selectedPatientForDoc, setSelectedPatientForDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('search'); // 'search', 'queue' or 'summaries'
  const [powerMode, setPowerMode] = useState(false); // Power Mode — only sticky cabin + token board, full-screen

  // Power Mode helpers — toggle full-screen and pin to Board tab
  const togglePowerMode = async () => {
    const turningOn = !powerMode;
    setPowerMode(turningOn);
    if (turningOn) {
      setActiveTab('board');
      // Auto-collapse header for max real estate
      setHeaderCollapsed(true);
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
        }
      } catch (_) { /* ignore — fullscreen denied or unsupported */ }
    } else {
      try {
        if (document.fullscreenElement && document.exitFullscreen) {
          await document.exitFullscreen();
        }
      } catch (_) { /* ignore */ }
    }
  };

  // Sync state if user exits fullscreen via ESC / system gesture
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement && powerMode) {
        setPowerMode(false);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [powerMode]);
  // Main-page search (G / P)
  const [gSearch, setGSearch] = useState('');
  const [pSearch, setPSearch] = useState('');
  const [consultationTimer, setConsultationTimer] = useState(0); // Timer in seconds
  const [showProductivity, setShowProductivity] = useState(false); // Productivity modal
  const [weeklyAverage, setWeeklyAverage] = useState(null); // Weekly average for trend
  const [performanceTab, setPerformanceTab] = useState('today'); // 'today', 'anyDay', 'weekly', 'monthly'
  const [performanceDate, setPerformanceDate] = useState(''); // For any day picker
  const [selectedWeekStart, setSelectedWeekStart] = useState(''); // For week picker
  const [selectedMonth, setSelectedMonth] = useState(''); // For month picker (YYYY-MM format)
  const [performanceData, setPerformanceData] = useState(null); // Data for selected period
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [bellExpanded, setBellExpanded] = useState(false); // Floating staff-call bell drawer state
  const bellAutoHideRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const headerRef = useRef(null);
  const stickyBarRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(192);
  const [stickyBarHeight, setStickyBarHeight] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now()); // re-renders every 30s for live wait-time mood pill
  
  // OPD Arrival / OTAR states
  const [opdStarted, setOpdStarted] = useState(false);
  const [opdArrival, setOpdArrival] = useState(null);
  const [opdAutoDetected, setOpdAutoDetected] = useState(false);
  const [otarData, setOtarData] = useState(null);
  const [opdSettings, setOpdSettings] = useState({ target_arrival_time: '18:00' });
  const [showOTARSettings, setShowOTARSettings] = useState(false);
  // Doctor clinic-arrival status (above motto bar)
  const [arrivalStreak, setArrivalStreak] = useState(null);
  const [showArrivalSettings, setShowArrivalSettings] = useState(false);
  const [arrivalTimeInput, setArrivalTimeInput] = useState('18:00');
  // Doctor punch-time log
  const [showPunchLog, setShowPunchLog] = useState(false);
  const [punchLog, setPunchLog] = useState(null);
  const [punchLogLoading, setPunchLogLoading] = useState(false);
  const [punchEditDay, setPunchEditDay] = useState(null); // {date, arrival_hhmm}
  const [punchEditValue, setPunchEditValue] = useState('');
  const [punchInValue, setPunchInValue] = useState(''); // for the inline "Punch In Now" card
  
  // Token System Settings
  const [tokenSystemMode, setTokenSystemMode] = useState('common'); // 'common' or 'segregated'
  const [showTokenSettings, setShowTokenSettings] = useState(false);

  // Auto-call system state
  const [autoCallEnabled, setAutoCallEnabled] = useState(false);
  const [suggestedNext, setSuggestedNext] = useState(null);
  const [autoCallLoading, setAutoCallLoading] = useState(false);
  const [highlightFever, setHighlightFever] = useState(false);
  const [selectedReviewDays, setSelectedReviewDays] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  // Auto-collapse the action header during consultation; user can pin it open
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [headerPinned, setHeaderPinned] = useState(false);

  // Past records state
  const [showPastRecords, setShowPastRecords] = useState(false);
  const [pastRecordsDate, setPastRecordsDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [pastPatients, setPastPatients] = useState([]);
  const [pastSummary, setPastSummary] = useState(null);
  const [loadingPastRecords, setLoadingPastRecords] = useState(false);

  // Nursing Revenue State
  const [showNursingRevenue, setShowNursingRevenue] = useState(false);
  const [nursingRevenueTab, setNursingRevenueTab] = useState('today'); // 'today', 'day', 'week', 'month'
  const [nursingDate, setNursingDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [nursingWeekStart, setNursingWeekStart] = useState('');
  const [nursingMonth, setNursingMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [nursingData, setNursingData] = useState(null);
  const [nursingLoading, setNursingLoading] = useState(false);

  // Finance & Consultation Fee State
  const [feeSettings, setFeeSettings] = useState({ regular_fee: 200, review_fee: 50, er_fee: 250 });
  const [showFinance, setShowFinance] = useState(false);
  const [editingErFeePatientId, setEditingErFeePatientId] = useState(null);
  const [editingErFeeValue, setEditingErFeeValue] = useState('');
  const [financeTab, setFinanceTab] = useState('today');
  const [financeDate, setFinanceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [financeWeekStart, setFinanceWeekStart] = useState('');
  const [financeMonth, setFinanceMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [financeYear, setFinanceYear] = useState(() => new Date().getFullYear());
  const [financeData, setFinanceData] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  // Losses state
  const [lossesPeriod, setLossesPeriod] = useState('daily'); // daily, weekly, monthly, annual
  const [lossesDateStr, setLossesDateStr] = useState('');
  const [lossesData, setLossesData] = useState(null);
  const [lossesLoading, setLossesLoading] = useState(false);

  // P&L state (separate modal)
  const [showPnl, setShowPnl] = useState(false);
  const [pnlPeriod, setPnlPeriod] = useState('daily');
  const [pnlDateStr, setPnlDateStr] = useState('');
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  // P&L Attended-list view
  const [pnlView, setPnlView] = useState('summary'); // 'summary' | 'attended'
  const [attendedList, setAttendedList] = useState(null);
  const [attendedLoading, setAttendedLoading] = useState(false);
  const [attendedDate, setAttendedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendedFilter, setAttendedFilter] = useState('all'); // all | with_rx | no_rx | with_nursing | nursing_walkin
  const [attendedSearch, setAttendedSearch] = useState('');
  const [showFeeSettings, setShowFeeSettings] = useState(false);

  // Diabetic Registry state
  const [showDiabeticRegistry, setShowDiabeticRegistry] = useState(false);
  const [diabeticList, setDiabeticList] = useState([]);
  const [diabeticLoading, setDiabeticLoading] = useState(false);
  const [diabeticSearch, setDiabeticSearch] = useState('');
  const [selectedDiabetic, setSelectedDiabetic] = useState(null);

  // Add-to-DM modal state (for adding existing patient to DM registry)
  const [showAddToDmModal, setShowAddToDmModal] = useState(false);
  const [addToDmPatient, setAddToDmPatient] = useState(null);
  const [addToDmSearch, setAddToDmSearch] = useState('');
  const [addToDmResults, setAddToDmResults] = useState([]);
  const [addToDmLoading, setAddToDmLoading] = useState(false);

  // Camp patients state
  const [showCampEntry, setShowCampEntry] = useState(false);
  const [campPatients, setCampPatients] = useState([]);
  const [campFilterType, setCampFilterType] = useState('');
  const [campStartDate, setCampStartDate] = useState('');
  const [campEndDate, setCampEndDate] = useState('');

  // Review reminders state
  const [showReviews, setShowReviews] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Notifications (leave + inventory pending)
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingLeave, setPendingLeave] = useState([]);
  const [pendingInventory, setPendingInventory] = useState([]);
  const [notifCount, setNotifCount] = useState(0);

  // Leave & Inventory Summary
  const [showLISummary, setShowLISummary] = useState(false);
  const [liSummaryPeriod, setLISummaryPeriod] = useState('day');
  const [liSummaryData, setLISummaryData] = useState(null);
  const [liSummaryLoading, setLISummaryLoading] = useState(false);

  // Motto state
  const [motto, setMotto] = useState('');
  const [editingMotto, setEditingMotto] = useState(false);
  const [mottoInput, setMottoInput] = useState('');

  // MRD (Medical Records) state
  const [showMRD, setShowMRD] = useState(false);
  const [mrdQuery, setMrdQuery] = useState('');
  const [mrdResults, setMrdResults] = useState(null);
  const [mrdLoading, setMrdLoading] = useState(false);
  const [mrdSelectedProfile, setMrdSelectedProfile] = useState(null);

  const CAMP_TYPES = [
    { value: 'respiratory', label: 'Respiratory', prefix: 'RC' },
    { value: 'diabetic', label: 'Diabetic', prefix: 'DC' },
    { value: 'anemia', label: 'Anemia', prefix: 'AC' },
    { value: 'thyroid', label: 'Thyroid', prefix: 'TC' },
    { value: 'cholesterol', label: 'Cholesterol', prefix: 'CC' },
  ];

  const fetchQueue = async () => {
    try {
      const response = await axios.get(`${API}/patients/queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching queue:', error);
    }
  };

  const fetchMotto = async () => {
    try {
      const response = await axios.get(`${API}/settings/doctor-motto`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMotto(response.data.motto || '');
    } catch { /* ignore */ }
  };

  const saveMotto = async () => {
    try {
      await axios.put(`${API}/settings/doctor-motto`, { motto: mottoInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMotto(mottoInput);
      setEditingMotto(false);
      toast.success('Updated!');
    } catch { toast.error('Failed to update'); }
  };

  // MRD search
  const searchMRD = async () => {
    if (mrdQuery.trim().length < 2) { toast.error('Enter at least 2 characters'); return; }
    setMrdLoading(true);
    setMrdSelectedProfile(null);
    try {
      const response = await axios.get(`${API}/mrd/search?q=${encodeURIComponent(mrdQuery.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMrdResults(response.data);
      if (!response.data.profiles?.length) toast('No records found');
    } catch { toast.error('Search failed'); }
    finally { setMrdLoading(false); }
  };

  const fetchDaySummary = async () => {
    try {
      const response = await axios.get(`${API}/patients/day-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDaySummary(response.data);
    } catch (error) {
      console.error('Error fetching day summary:', error);
    }
  };

  const fetchOnDutyDoctor = async () => {
    try {
      const response = await axios.get(`${API}/doctors/on-duty`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnDutyDoctor(response.data);
    } catch (error) {
      console.error('Error fetching on-duty doctor:', error);
    }
  };

  const fetchWeeklyAverage = async () => {
    try {
      const response = await axios.get(`${API}/patients/weekly-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Calculate average excluding today (last 6 days)
      const dailyBreakdown = response.data.daily_breakdown || [];
      if (dailyBreakdown.length > 1) {
        // Exclude today (last item) for fair comparison
        const pastDays = dailyBreakdown.slice(0, -1);
        const totalPastDays = pastDays.reduce((sum, day) => sum + day.total, 0);
        const avgPastDays = totalPastDays / pastDays.length;
        setWeeklyAverage(avgPastDays);
      }
    } catch (error) {
      console.error('Error fetching weekly summary:', error);
    }
  };

  // Fetch performance data for different periods
  const fetchPerformanceData = async (period, dateStr = null, year = null, month = null) => {
    setPerformanceLoading(true);
    try {
      let endpoint = '';
      if (period === 'anyDay' && dateStr) {
        endpoint = `${API}/patients/summary-by-date?date_str=${dateStr}`;
      } else if (period === 'weekly' && dateStr) {
        // Weekly for specific week
        endpoint = `${API}/patients/weekly-summary-for-date?start_date=${dateStr}`;
      } else if (period === 'weekly') {
        // Current week
        endpoint = `${API}/patients/weekly-summary`;
      } else if (period === 'monthly' && year && month) {
        // Specific month
        endpoint = `${API}/patients/monthly-summary-for-month?year=${year}&month=${month}`;
      } else if (period === 'monthly') {
        // Current month
        endpoint = `${API}/patients/monthly-summary`;
      }
      
      if (endpoint) {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPerformanceData(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch performance data');
      console.error('Error fetching performance data:', error);
    } finally {
      setPerformanceLoading(false);
    }
  };

  // Fetch OPD arrival status for today
  const fetchOpdStatus = async () => {
    try {
      const response = await axios.get(`${API}/opd/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpdStarted(response.data.started);
      setOpdArrival(response.data.arrival);
      setOpdAutoDetected(response.data.auto_detected || false);
    } catch (error) {
      console.error('Error fetching OPD status:', error);
    }
  };

  // Fetch OTAR data
  const fetchOTARData = async () => {
    try {
      const response = await axios.get(`${API}/opd/otar?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOtarData(response.data);
    } catch (error) {
      console.error('Error fetching OTAR data:', error);
    }
  };

  // Fetch OPD settings
  const fetchOpdSettings = async () => {
    try {
      const response = await axios.get(`${API}/opd/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpdSettings(response.data);
      setArrivalTimeInput(response.data.target_arrival_time || '18:00');
    } catch (error) {
      console.error('Error fetching OPD settings:', error);
    }
  };

  // Schedule auto-hide of expanded bell drawer after period of inactivity
  const scheduleBellAutoHide = (ms = 4000) => {
    if (bellAutoHideRef.current) clearTimeout(bellAutoHideRef.current);
    bellAutoHideRef.current = setTimeout(() => setBellExpanded(false), ms);
  };

  // Toggle / open the floating staff-call drawer
  const openBellDrawer = () => {
    setBellExpanded(true);
    scheduleBellAutoHide(5000);
  };

  const closeBellDrawer = () => {
    if (bellAutoHideRef.current) clearTimeout(bellAutoHideRef.current);
    setBellExpanded(false);
  };

  // Ring bell to summon staff for CBG / Temp / Attention
  const ringBell = async (bellType) => {
    try {
      await axios.post(`${API}/bell/ring`, { type: bellType }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const labels = { cbg: 'CBG', temp: 'Temperature', attention: 'Attention' };
      toast.success(`🔔 Staff notified — ${labels[bellType] || bellType}`);
      // Briefly keep drawer open then auto-collapse
      scheduleBellAutoHide(1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to ring bell');
    }
  };

  const fetchArrivalStreak = async () => {
    try {
      const r = await axios.get(`${API}/opd/streak`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArrivalStreak(r.data);
    } catch (_) { /* ignore */ }
  };

  const fetchPunchLog = async () => {
    setPunchLogLoading(true);
    try {
      const r = await axios.get(`${API}/opd/punch-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPunchLog(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load punch log');
    } finally {
      setPunchLogLoading(false);
    }
  };

  const openPunchLog = () => {
    setShowPunchLog(true);
    fetchPunchLog();
  };

  const punchInNow = async (hhmm) => {
    try {
      const body = hhmm ? { arrival_hhmm: hhmm } : {};
      await axios.post(`${API}/opd/punch`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(hhmm ? `✓ Punched in at ${hhmm}` : '✓ Punched in');
      setPunchInValue('');
      await fetchArrivalStreak();
      await fetchPunchLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to punch in');
    }
  };

  const savePunchEdit = async () => {
    if (!punchEditDay || !punchEditValue) return;
    try {
      await axios.patch(`${API}/opd/arrival/${punchEditDay.date}`,
        { arrival_hhmm: punchEditValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✓ ${punchEditDay.date} updated to ${punchEditValue}`);
      setPunchEditDay(null);
      setPunchEditValue('');
      await fetchPunchLog();
      await fetchArrivalStreak();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const saveArrivalSettings = async () => {
    const v = (arrivalTimeInput || '').trim();
    if (!/^\d{2}:\d{2}$/.test(v)) {
      toast.error("Use HH:MM 24-hour format (e.g. 09:00 or 18:00)");
      return;
    }
    try {
      await axios.put(`${API}/opd/settings`,
        { target_arrival_time: v },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpdSettings({ target_arrival_time: v });
      toast.success(`Clinic time set to ${v}`);
      setShowArrivalSettings(false);
      fetchArrivalStreak();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  // Start OPD - mark doctor arrival
  const handleStartOPD = async () => {
    try {
      const response = await axios.post(`${API}/opd/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpdStarted(true);
      setOpdArrival(response.data.arrival);
      toast.success('OPD Started! Arrival marked.');
      fetchOTARData(); // Refresh OTAR data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start OPD');
    }
  };

  // Update OPD target time
  const handleUpdateTargetTime = async (newTime) => {
    try {
      await axios.put(`${API}/opd/settings`, 
        { target_arrival_time: newTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpdSettings({ target_arrival_time: newTime });
      toast.success('Target arrival time updated');
      setShowOTARSettings(false);
    } catch (error) {
      toast.error('Failed to update target time');
    }
  };

  // Fetch Token System Settings
  const fetchTokenSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/token-system`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTokenSystemMode(response.data.mode);
    } catch (error) {
      console.error('Error fetching token system settings:', error);
    }
  };

  // Update Token System Mode
  const handleUpdateTokenSystem = async (newMode) => {
    try {
      await axios.put(`${API}/settings/token-system`, 
        { mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTokenSystemMode(newMode);
      toast.success(`Token system changed to ${newMode === 'common' ? 'Common (A-001, A-002...)' : 'Segregated (G-001/P-001)'}`);
      setShowTokenSettings(false);
    } catch (error) {
      toast.error('Failed to update token system');
    }
  };

  // Export to CSV
  const exportToCSV = async (period) => {
    try {
      let dataToExport = [];
      let filename = '';
      
      if (period === 'today') {
        dataToExport = patients;
        filename = `patients_today_${new Date().toISOString().split('T')[0]}.csv`;
      } else if (period === 'weekly' && performanceData?.daily_breakdown) {
        // For weekly, we need to fetch all patients for those dates
        const response = await axios.get(`${API}/patients/export/weekly`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        dataToExport = response.data.patients || [];
        filename = `patients_weekly_${performanceData.period?.replace(/ to /g, '_to_')}.csv`;
      } else if (period === 'monthly' && performanceData?.weekly_breakdown) {
        const response = await axios.get(`${API}/patients/export/monthly`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        dataToExport = response.data.patients || [];
        filename = `patients_monthly_${new Date().toISOString().slice(0, 7)}.csv`;
      } else if (period === 'anyDay' && performanceDate) {
        const response = await axios.get(`${API}/patients/export/date?date_str=${performanceDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        dataToExport = response.data.patients || [];
        filename = `patients_${performanceDate}.csv`;
      }

      if (dataToExport.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Create CSV content
      const headers = ['Token', 'Name', 'Age', 'Sex', 'Date', 'Status', 'Urgent', 'Fever', 'Review', 'DM', 'HTN', 'Consultation Duration'];
      const csvRows = [headers.join(',')];
      
      dataToExport.forEach(p => {
        const duration = p.entered_cabin_at && p.completed_at 
          ? Math.floor((new Date(p.completed_at) - new Date(p.entered_cabin_at)) / 1000)
          : '';
        const durationStr = duration ? `${Math.floor(duration/60)}:${(duration%60).toString().padStart(2,'0')}` : '';
        
        const row = [
          p.token_number || '',
          `"${(p.name || 'Nameless').replace(/"/g, '""')}"`,
          p.age || '',
          p.sex || '',
          p.date || '',
          p.status || '',
          p.urgent ? 'Yes' : 'No',
          p.fever ? 'Yes' : 'No',
          p.review ? 'Yes' : 'No',
          p.diabetes ? 'Yes' : 'No',
          p.htn ? 'Yes' : 'No',
          durationStr
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      toast.success(`Exported ${dataToExport.length} patients to CSV`);
    } catch (error) {
      toast.error('Failed to export data');
      console.error('Export error:', error);
    }
  };

  // Export to PDF (using print)
  const exportToPDF = (period) => {
    const printContent = document.getElementById('performance-print-area');
    if (!printContent) {
      toast.error('Nothing to print');
      return;
    }
    
    const printStyles = `body { font-family: Arial, sans-serif; padding: 20px; }
      h1 { color: #1e40af; margin-bottom: 5px; }
      h2 { color: #374151; margin-top: 20px; }
      .header { border-bottom: 2px solid #1e40af; padding-bottom: 10px; margin-bottom: 20px; }
      .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
      .stat-box { border: 1px solid #e5e7eb; padding: 15px; border-radius: 8px; text-align: center; }
      .stat-value { font-size: 24px; font-weight: bold; color: #1e40af; }
      .stat-label { font-size: 12px; color: #6b7280; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-size: 12px; }
      th { background: #f3f4f6; font-weight: bold; }
      .footer { margin-top: 30px; text-align: center; color: #6b7280; font-size: 12px; }
      @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }`;
    const periodLabel = period === 'today' ? 'Today' : period === 'weekly' ? 'Weekly' : period === 'monthly' ? 'Monthly' : performanceDate;
    const body = `<div class="header">
      <h1>K.M Multi-Speciality Clinic, Koliyanur</h1>
      <p>Performance Report - ${escapeHtml(periodLabel)}</p>
      <p>Generated: ${new Date().toLocaleString('en-IN')}</p>
    </div>
    ${printContent.innerHTML}
    <div class="footer"><p>K.M Multi-Speciality Clinic - Confidential Report</p></div>`;
    safePrintWindow('Performance Report - K.M Multi-Speciality Clinic', `<style>${printStyles}</style>${body}`);
  };

  const fetchPreviousDaySummary = async () => {
    try {
      const response = await axios.get(`${API}/patients/previous-day-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviousDaySummary(response.data);
    } catch (error) {
      console.error('Error fetching previous day summary:', error);
    }
  };

  const fetchCustomDateSummary = async (date) => {
    if (!date) return;
    try {
      const response = await axios.get(`${API}/patients/summary-by-date?date_str=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomDateSummary(response.data);
      toast.success(`Summary loaded for ${date}`);
    } catch (error) {
      toast.error('Failed to fetch summary for selected date');
      console.error('Error fetching custom date summary:', error);
    }
  };

  // Track header + in-cabin sticky bar bottom edge (viewport-relative) for floating drawers (P&L)
  // Uses getBoundingClientRect so it accounts for both DOM size AND scroll position (sticky bar
  // pins to viewport top when scrolled, drawer must follow).
  const [topOffset, setTopOffset] = useState(192);
  useEffect(() => {
    const updateAll = () => {
      const headerEl = headerRef.current;
      const stickyEl = stickyBarRef.current;
      // Effective bottom = max of (header bottom, sticky bar bottom) so drawer starts below whichever is lower
      let bottom = 0;
      if (headerEl) {
        const r = headerEl.getBoundingClientRect();
        if (r.bottom > bottom) bottom = r.bottom;
      }
      if (stickyEl) {
        const r = stickyEl.getBoundingClientRect();
        if (r.bottom > bottom) bottom = r.bottom;
      }
      setTopOffset(Math.max(0, bottom));
      setHeaderHeight(headerEl?.offsetHeight || 192);
      setStickyBarHeight(stickyEl?.offsetHeight || 0);
    };
    updateAll();
    const ro = new ResizeObserver(updateAll);
    if (headerRef.current) ro.observe(headerRef.current);
    if (stickyBarRef.current) ro.observe(stickyBarRef.current);
    window.addEventListener('scroll', updateAll, { passive: true });
    window.addEventListener('resize', updateAll);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updateAll);
      window.removeEventListener('resize', updateAll);
    };
  }, [headerCollapsed, patients]);

  useEffect(() => {
    fetchQueue();
    fetchDaySummary();
    fetchOnDutyDoctor();
    fetchWeeklyAverage();
    fetchOpdStatus();
    fetchOTARData();
    fetchOpdSettings();
    fetchArrivalStreak();
    fetchPunchLog();
    fetchTokenSystemSettings();
    fetchSuggestedNext();
    fetchFeeSettings();
    fetchMotto();
    fetchNotifications();
    fetchQueueSuggestion();
    const interval = setInterval(() => {
      fetchQueue();
      fetchDaySummary();
      fetchOnDutyDoctor();
      fetchNotifications();
      fetchQueueSuggestion();
      if (autoCallEnabled) {
        fetchSuggestedNext();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [autoCallEnabled]);

  // Live tick for wait-time mood pill (refresh every 30s)
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Refresh arrival streak every 60s (so flashy late-counter stays current)
  useEffect(() => {
    const id = setInterval(() => fetchArrivalStreak(), 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch suggested next patient for auto-call
  const fetchSuggestedNext = async () => {
    try {
      const response = await axios.get(`${API}/patients/suggest-next`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestedNext(response.data.has_next ? response.data.patient : null);
    } catch (error) {
      console.error('Error fetching suggested next:', error);
    }
  };

  // Auto-call next patient (alternating P/G or sequential)
  const handleAutoCallNext = async () => {
    if (!autoCallEnabled) {
      toast.error('Auto-call is disabled. Turn it ON first.');
      return;
    }
    
    setAutoCallLoading(true);
    try {
      const response = await axios.post(
        `${API}/patients/auto-call-next`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.called) {
        toast.success(`Called: ${response.data.patient.token_number} - ${response.data.patient.name || 'Nameless'}`);
        fetchQueue();
        fetchSuggestedNext();
      } else {
        toast.info(response.data.message || 'No patients waiting');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to call next patient');
    } finally {
      setAutoCallLoading(false);
    }
  };

  // Fetch past records
  const fetchPastRecords = async (dateStr) => {
    setLoadingPastRecords(true);
    try {
      const [patientsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/patients/export/date?date_str=${dateStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/patients/summary-by-date?date_str=${dateStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPastPatients(patientsRes.data.patients || []);
      setPastSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching past records:', error);
      toast.error('Failed to fetch past records');
    } finally {
      setLoadingPastRecords(false);
    }
  };

  // Change past records date
  const changePastDate = (days) => {
    const current = new Date(pastRecordsDate);
    current.setDate(current.getDate() + days);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (current >= today) return; // Don't go to future or today
    const newDate = current.toISOString().split('T')[0];
    setPastRecordsDate(newDate);
    fetchPastRecords(newDate);
  };

  // Open past records modal
  const openPastRecords = () => {
    setShowPastRecords(true);
    fetchPastRecords(pastRecordsDate);
  };

  // Consultation Timer Effect
  useEffect(() => {
    const currentPatientInCabin = patients.find(p => p.status === 'in_cabin');
    
    if (currentPatientInCabin && currentPatientInCabin.entered_cabin_at) {
      // Start/update timer
      const startTime = new Date(currentPatientInCabin.entered_cabin_at).getTime();
      
      const updateTimer = () => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setConsultationTimer(elapsed);
      };
      
      updateTimer(); // Initial update
      timerIntervalRef.current = setInterval(updateTimer, 1000);
      
      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    } else {
      // No patient in cabin, reset timer
      setConsultationTimer(0);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
  }, [patients]);

  // Format seconds to MM:SS
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Nursing Revenue Functions
  const fetchNursingRevenue = async (tab, dateVal) => {
    setNursingLoading(true);
    try {
      let response;
      if (tab === 'today' || tab === 'day') {
        const dateToUse = tab === 'today' ? new Date().toISOString().split('T')[0] : dateVal;
        response = await axios.get(`${API}/nursing/summary?date_str=${dateToUse}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'week') {
        response = await axios.get(`${API}/nursing/summary/weekly?week_start=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'month') {
        response = await axios.get(`${API}/nursing/summary/monthly?month=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setNursingData(response?.data || null);
    } catch (error) {
      console.error('Error fetching nursing revenue:', error);
      toast.error('Failed to load nursing revenue');
    } finally {
      setNursingLoading(false);
    }
  };

  const openNursingRevenue = () => {
    setShowNursingRevenue(true);
    setNursingRevenueTab('today');
    fetchNursingRevenue('today', null);
  };

  const getWeekStartDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  };

  // Consultation Fee Functions
  const fetchFeeSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/consultation-fees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeSettings(response.data);
    } catch (error) {
      console.error('Error fetching fee settings:', error);
    }
  };

  const setConsultationType = async (patientId, consultationType) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/consultation-type`,
        { consultation_type: consultationType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const label = consultationType === 'review' ? `Review (₹${feeSettings.review_fee})` :
                     consultationType === 'exempted' ? 'Fee Exempted (₹0)' :
                     `Regular (₹${feeSettings.regular_fee})`;
      toast.success(`Marked as ${label}`);
      fetchQueue();
    } catch (error) {
      toast.error('Failed to set consultation type');
    }
  };

  const updateFeeSettings = async (regularFee, reviewFee, erFee) => {
    try {
      await axios.put(
        `${API}/settings/consultation-fees`,
        { regular_fee: regularFee, review_fee: reviewFee, er_fee: erFee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeeSettings({ regular_fee: regularFee, review_fee: reviewFee, er_fee: erFee });
      toast.success('Fee settings updated');
      setShowFeeSettings(false);
    } catch (error) {
      toast.error('Failed to update fee settings');
    }
  };

  const updatePatientFee = async (patientId, fee) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/update-fee`,
        { consultation_fee: parseFloat(fee) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Fee updated to ₹${fee}`);
      setEditingErFeePatientId(null);
      setEditingErFeeValue('');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fee');
    }
  };

  // Finance Functions
  const fetchFinanceData = async (tab, dateVal) => {
    setFinanceLoading(true);
    try {
      let response;
      if (tab === 'today') {
        response = await axios.get(`${API}/finance/daily`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'day') {
        response = await axios.get(`${API}/finance/daily?date_str=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'week') {
        response = await axios.get(`${API}/finance/weekly?week_start=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'month') {
        response = await axios.get(`${API}/finance/monthly?month=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (tab === 'year') {
        response = await axios.get(`${API}/finance/annual?year=${dateVal}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setFinanceData(response?.data || null);
    } catch (error) {
      console.error('Error fetching finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setFinanceLoading(false);
    }
  };

  const openFinance = () => {
    setShowFinance(true);
    setFinanceTab('today');
    fetchFinanceData('today', null);
  };

  // Losses fetch
  const fetchLossesData = async (period, dateVal) => {
    setLossesLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (dateVal) params.append('date_str', dateVal);
      const response = await axios.get(`${API}/finance/losses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLossesData(response?.data || null);
    } catch (error) {
      console.error('Error fetching losses:', error);
      toast.error('Failed to load losses data');
    } finally {
      setLossesLoading(false);
    }
  };

  // P&L fetch
  const fetchPnlData = async (period, dateVal) => {
    setPnlLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (dateVal) params.append('date_str', dateVal);
      const response = await axios.get(`${API}/finance/pnl?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPnlData(response?.data || null);
    } catch (error) {
      console.error('Error fetching P&L:', error);
      toast.error('Failed to load P&L data');
    } finally {
      setPnlLoading(false);
    }
  };

  const openPnl = () => {
    setShowPnl(true);
    setPnlPeriod('daily');
    setPnlDateStr('');
    setPnlView('summary');
    fetchPnlData('daily', null);
  };

  // Fetch attended list (doctor + nursing combined)
  const fetchAttendedList = async (d) => {
    setAttendedLoading(true);
    try {
      const params = new URLSearchParams();
      if (d) params.append('date_str', d);
      const r = await axios.get(`${API}/finance/attended-list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendedList(r.data);
    } catch (error) {
      console.error('Error fetching attended list:', error);
      toast.error('Failed to load attended list');
    } finally {
      setAttendedLoading(false);
    }
  };

  // Print attended list as a clean A4 sheet
  const printAttendedList = () => {
    if (!attendedList?.rows?.length) {
      toast.error('No data to print');
      return;
    }
    const rows = attendedList.rows;
    const s = attendedList.summary || {};
    const html = `
      <html><head><title>Attended Patients · ${attendedList.date}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; color: #1f2937; }
        h2 { margin: 0 0 6px; color: #7f1d1d; }
        .sub { color: #6b7280; font-size: 12px; margin-bottom: 14px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #d1d5db; padding: 5px 7px; text-align: left; vertical-align: top; }
        th { background: #fef2f2; color: #7f1d1d; font-weight: 700; }
        .rx { font-family: monospace; font-weight: bold; color: #047857; }
        .no-rx { color: #b91c1c; font-weight: bold; }
        .er { background: #fef2f2; }
        .walkin { background: #f0fdf4; }
        .totals { margin-top: 14px; padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; font-size: 12px; }
        .totals b { color: #7f1d1d; }
      </style></head><body>
      <h2>Attended Patients — Pharmacy Reconcile Sheet</h2>
      <div class="sub">Date: <b>${attendedList.date}</b> · Generated: ${new Date().toLocaleString()}</div>
      <table>
        <thead><tr>
          <th>#</th><th>Rx</th><th>Token</th><th>Patient</th><th>Type</th><th>Fee ₹</th>
          <th>Nursing Services</th><th>Nursing ₹</th><th>Total ₹</th><th>Purchased ✓</th>
        </tr></thead>
        <tbody>
          ${rows.map((r, i) => {
            const services = (r.nursing_services || []).map(x => `${x.name}${x.qty > 1 ? `×${x.qty}` : ''} (₹${Math.round(x.cost * x.qty)})`).join(', ');
            const rxCell = r.prescription_number
              ? `<span class="rx">${r.prescription_number}</span>`
              : (r.rx_skipped ? `<span class="no-rx">—</span>` : '');
            const rowClass = r.kind === 'nursing_walkin' ? 'walkin' : (r.is_er ? 'er' : '');
            return `<tr class="${rowClass}">
              <td>${i + 1}</td>
              <td>${rxCell}</td>
              <td>${r.token_number || (r.kind === 'nursing_walkin' ? 'Walk-in' : '—')}</td>
              <td>${r.name}${r.age ? ` (${r.age}${r.sex ? '/' + r.sex : ''})` : ''}</td>
              <td>${(r.consultation_type || '').toUpperCase()}</td>
              <td>${r.consultation_fee ? Math.round(r.consultation_fee) : '—'}</td>
              <td>${services || '—'}</td>
              <td>${r.nursing_total ? Math.round(r.nursing_total) : '—'}</td>
              <td><b>${Math.round(r.total_billed || 0)}</b></td>
              <td>&nbsp;&nbsp;&nbsp;&nbsp;</td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>
      <div class="totals">
        <div>Doctor patients: <b>${s.doctor_patients || 0}</b> · Nursing walk-ins: <b>${s.nursing_walkins || 0}</b></div>
        <div>With Rx (pharmacy-eligible): <b>${s.with_rx || 0}</b> · Rx-eligible but no Rx: <b>${s.rx_eligible_no_rx || 0}</b></div>
        <div>Consultation total: <b>₹${Math.round(s.consultation_total || 0)}</b> · Nursing total: <b>₹${Math.round(s.nursing_total || 0)}</b> · Grand total: <b>₹${Math.round(s.grand_total || 0)}</b></div>
        <div style="margin-top: 8px; color: #6b7280; font-size: 11px;">
          ✓ Tick each Rx as you find it in the pharmacy register. Unticked Rx = pharmacy missed (patient lost).
        </div>
      </div>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up blocked'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const printFinancePDF = () => {
    if (!financeData) return;
    const period = financeTab === 'today' ? 'Today' :
                   financeTab === 'day' ? `Day · ${financeDate}` :
                   financeTab === 'week' ? `Week · ${financeData.period}` :
                   financeTab === 'month' ? `Month · ${financeData.month_name || financeMonth}` :
                   financeTab === 'year' ? `Annual · ${financeData.year}` : '';
    const grand = financeData.tally ? (financeData.tally.final_override?.grand_total || 0) : (financeData.grand_total || 0);
    const cons = financeData.tally ? (financeData.tally.final_override?.consultation_total || 0) : (financeData.doctor_revenue?.total_revenue || 0);
    const nur = financeData.tally ? (financeData.tally.final_override?.nursing_total || 0) : (financeData.nursing_revenue?.net_revenue || financeData.nursing_revenue?.total_revenue || 0);
    const isReconciled = !!financeData.tally;
    const signedBy = financeData.tally?.signed_by || '';
    const reconciledChip = financeData.reconciled_days !== undefined
      ? `<span style="background:#10b981;color:#fff;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:bold">Reconciled ${financeData.reconciled_days}/${financeData.total_days} days</span>` : '';

    const renderBreakdownRows = () => {
      if (financeData.daily_breakdown) {
        return Object.entries(financeData.daily_breakdown).map(([d, info]) => `
          <tr>
            <td style="border:1px solid #ddd;padding:6px">${escapeHtml(d)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;">₹${Math.round(info.doctor_revenue)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;">₹${Math.round(info.nursing_revenue)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;font-weight:bold;">₹${Math.round(info.total)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:center;">${info.patients || 0}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:center;color:${info.reconciled ? '#10b981' : '#9ca3af'};font-weight:bold">${info.reconciled ? '✓' : '—'}</td>
          </tr>`).join('');
      }
      if (financeData.weekly_breakdown) {
        return Object.entries(financeData.weekly_breakdown).map(([wk, info]) => `
          <tr>
            <td style="border:1px solid #ddd;padding:6px">${escapeHtml(wk)}<div style="font-size:10px;color:#666">${escapeHtml(info.period || '')}</div></td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;">₹${Math.round(info.doctor_revenue)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;">₹${Math.round(info.nursing_revenue)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;font-weight:bold;">₹${Math.round(info.total)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:center;">${info.patients || 0}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:center;">${info.reconciled_days || 0}/${info.days || 7}</td>
          </tr>`).join('');
      }
      if (financeData.monthly_breakdown) {
        return Object.entries(financeData.monthly_breakdown).map(([mk, info]) => `
          <tr>
            <td style="border:1px solid #ddd;padding:6px">${escapeHtml(info.month_name)} <span style="color:#999">${escapeHtml(mk)}</span></td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;">₹${Math.round(info.doctor_revenue)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;">₹${Math.round(info.nursing_revenue)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:right;font-weight:bold;">₹${Math.round(info.total)}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:center;">${info.patients || 0}</td>
            <td style="border:1px solid #ddd;padding:6px;text-align:center;">${info.reconciled_days || 0}</td>
          </tr>`).join('');
      }
      return '';
    };

    const breakdownHeader =
      financeData.daily_breakdown
        ? `<thead><tr><th>Date</th><th>Consultation</th><th>Nursing</th><th>Total</th><th>Patients</th><th>Recon.</th></tr></thead>`
        : financeData.weekly_breakdown
          ? `<thead><tr><th>Week</th><th>Consultation</th><th>Nursing</th><th>Total</th><th>Patients</th><th>Recon. days</th></tr></thead>`
          : financeData.monthly_breakdown
            ? `<thead><tr><th>Month</th><th>Consultation</th><th>Nursing</th><th>Total</th><th>Patients</th><th>Recon. days</th></tr></thead>`
            : '';

    const breakdownTable = breakdownHeader ? `
      <h3>Breakdown</h3>
      <table style="width:100%;border-collapse:collapse;font-size:12px">
        ${breakdownHeader.replace(/<th>/g, '<th style="background:#7f1d1d;color:#fff;padding:6px;border:1px solid #ddd;text-align:left">')}
        <tbody>${renderBreakdownRows()}</tbody>
      </table>` : '';

    const html = `
      <style>
        body{font-family:Arial,sans-serif;margin:20px;color:#222}
        h1{color:#7f1d1d;margin:0 0 4px;font-size:22px}
        h3{color:#7f1d1d;border-bottom:2px solid #7f1d1d;padding-bottom:4px;margin:14px 0 8px}
        .meta{color:#555;font-size:12px;margin-bottom:10px}
        .totals{display:flex;gap:10px;margin:10px 0}
        .tile{flex:1;padding:10px;border:1px solid #e5e7eb;border-radius:8px;text-align:center;background:#fafafa}
        .tile .lbl{font-size:10px;text-transform:uppercase;color:#888;letter-spacing:1px}
        .tile .val{font-size:22px;font-weight:bold;color:#7f1d1d;margin-top:2px}
        .grand{background:#7f1d1d;color:#fff!important}
        .grand .val{color:#fff}
        .sign{margin-top:18px;padding:8px 12px;background:#ecfdf5;border:1px solid #6ee7b7;border-radius:6px;font-size:12px;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px}
        .sign b{font-family:cursive,Brush Script MT,sans-serif;font-size:16px;color:#065f46}
        .footer{margin-top:24px;font-size:10px;color:#888;text-align:right}
        @media print{ body{margin:10px} }
      </style>
      <h1>K.M Multi-Speciality Clinic — Finance Report</h1>
      <div class="meta">
        Period: <b>${escapeHtml(period)}</b> &nbsp;|&nbsp; Generated: ${new Date().toLocaleString('en-IN')}
        &nbsp;|&nbsp; Source: ${isReconciled ? '<b style="color:#065f46">Nurse-reconciled</b>' : 'System computed'}
        ${reconciledChip ? ' &nbsp;|&nbsp; ' + reconciledChip : ''}
      </div>

      <div class="totals">
        <div class="tile"><div class="lbl">Consultation</div><div class="val">₹${Math.round(cons)}</div></div>
        <div class="tile"><div class="lbl">Nursing (net)</div><div class="val">₹${Math.round(nur)}</div></div>
        <div class="tile grand"><div class="lbl">Grand Total</div><div class="val">₹${Math.round(grand)}</div></div>
      </div>

      ${signedBy ? `<div class="sign"><span>Reconciled & signed by:</span> <b>${escapeHtml(signedBy)}</b></div>` : ''}

      ${breakdownTable}

      <div class="footer">Auto-generated · K.M Doctor Dashboard</div>
    `;
    safePrintWindow(`Finance_${period.replace(/[^a-zA-Z0-9]/g, '_')}`, html);
  };

  // Diabetic Registry function
  const fetchDiabeticRegistry = async (search = '') => {
    setDiabeticLoading(true);
    try {
      const url = search ? `${API}/diabetic/search?q=${encodeURIComponent(search)}` : `${API}/diabetic/registry`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setDiabeticList(response.data);
    } catch (error) {
      toast.error('Failed to load diabetic registry');
    } finally {
      setDiabeticLoading(false);
    }
  };

  // Add existing patient to DM registry (Doctor)
  const openAddToDmModal = (patient) => {
    setAddToDmPatient(patient);
    setAddToDmSearch('');
    setAddToDmResults([]);
    setShowAddToDmModal(true);
  };

  const searchAddToDm = async (query) => {
    setAddToDmSearch(query);
    if (!query.trim()) { setAddToDmResults([]); return; }
    setAddToDmLoading(true);
    try {
      const response = await axios.get(`${API}/diabetic/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddToDmResults(response.data);
    } catch { setAddToDmResults([]); }
    finally { setAddToDmLoading(false); }
  };

  const linkPatientToDm = async (patientId, diabeticId) => {
    try {
      await axios.post(`${API}/diabetic/link-patient/${patientId}/${diabeticId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Patient linked to DM registry & visit recorded');
      setShowAddToDmModal(false);
      setAddToDmPatient(null);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link patient');
    }
  };

  const registerPatientAsDm = async (patientId) => {
    try {
      const response = await axios.post(`${API}/diabetic/register-from-patient/${patientId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Registered in DM Registry: ${response.data.diabetic_id}`);
      setShowAddToDmModal(false);
      setAddToDmPatient(null);
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register in DM registry');
    }
  };

  // Camp patients (read-only for doctor)
  const fetchCampPatients = async (filterType, startDate, endDate) => {
    try {
      let url = `${API}/camp-patients?`;
      if (startDate && endDate) url += `start_date=${startDate}&end_date=${endDate}`;
      if (filterType) url += `&camp_type=${filterType}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setCampPatients(response.data.entries || []);
    } catch { /* ignore */ }
  };

  const downloadCampPDF = async () => {
    let url = `${API}/camp-patients/export?`;
    if (campStartDate && campEndDate) url += `start_date=${campStartDate}&end_date=${campEndDate}`;
    if (campFilterType) url += `&camp_type=${campFilterType}`;
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = response.data;
      const rows = data.entries.map((e, i) => `<tr>
        <td style="border:1px solid #ccc;padding:6px">${i + 1}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.serial_number)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.date)}</td>
        <td style="border:1px solid #ccc;padding:6px;font-weight:bold">${escapeHtml(e.name) || '-'}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.age)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.sex)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.contact_number) || '-'}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.place) || '-'}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml((CAMP_TYPES.find(c => c.value === e.camp_type) || {}).label || e.camp_type)}</td>
        <td style="border:1px solid #ccc;padding:6px">${escapeHtml(e.notes) || '-'}</td>
      </tr>`).join('');
      const styles = 'body{font-family:Arial,sans-serif;padding:20px}table{border-collapse:collapse;width:100%}th{background:#1a5632;color:white;padding:8px;border:1px solid #ccc;text-align:left}h1{color:#1a5632}h3{color:#555}';
      const body = `<h1>K.M. Multi Speciality Clinic, Koliyanur</h1>
        <h2>${escapeHtml(data.title)}</h2><h3>Period: ${escapeHtml(data.period)} | Total: ${data.total_patients}</h3>
        <table><thead><tr><th>#</th><th>Serial</th><th>Date</th><th>Name</th><th>Age</th><th>Sex</th><th>Phone</th><th>Place</th><th>Camp</th><th>Notes</th></tr></thead>
        <tbody>${rows}</tbody></table>
        <p style="margin-top:20px;color:#888;font-size:12px">Generated on ${new Date().toLocaleString('en-IN')}</p>`;
      safePrintWindow(`Camp Report - ${data.title}`, `<style>${styles}</style>${body}`);
    } catch { /* ignore */ }
  };

  // Review reminders
  const fetchReviews = async () => {
    setReviewLoading(true);
    try {
      const response = await axios.get(`${API}/reviews/upcoming?days_ahead=3`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewData(response.data);
    } catch { /* ignore */ }
    finally { setReviewLoading(false); }
  };

  // Notifications (pending leave + inventory)
  const fetchNotifications = async () => {
    try {
      const [countRes, leaveRes, invRes] = await Promise.all([
        axios.get(`${API}/notifications/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leave-requests?status=pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory-requests?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNotifCount(countRes.data.total);
      setPendingLeave(leaveRes.data);
      setPendingInventory(invRes.data);
    } catch { /* ignore */ }
  };

  const handleApproveReject = async (type, id, status) => {
    try {
      const endpoint = type === 'leave' ? 'leave-requests' : 'inventory-requests';
      await axios.patch(`${API}/${endpoint}/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${status === 'approved' ? 'Approved' : 'Rejected'}`);
      fetchNotifications();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // Leave & Inventory Summary fetcher
  const fetchLISummary = async (period) => {
    setLISummaryLoading(true);
    try {
      const res = await axios.get(`${API}/leave-inventory-summary?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLISummaryData(res.data);
    } catch (error) {
      toast.error('Failed to load summary');
    } finally {
      setLISummaryLoading(false);
    }
  };

  const printLISummaryPDF = () => {
    if (!liSummaryData) return;
    const periodLabel = liSummaryPeriod === 'day' ? 'Daily' : liSummaryPeriod === 'week' ? 'Weekly' : 'Monthly';
    const ls = liSummaryData.leave_stats || {};
    const is_ = liSummaryData.inventory_stats || {};

    const leaveRows = (liSummaryData.leaves || []).map((r, i) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;text-align:center">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:6px;font-weight:bold">${escapeHtml(r.staff_name) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(r.leave_date) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${r.day_type === 'half' ? `Half · ${escapeHtml(r.half_session) || ''}` : 'Full'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(r.reason) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(r.replacement) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;text-transform:capitalize;font-weight:bold;color:${r.status === 'approved' ? '#15803d' : r.status === 'rejected' ? '#b91c1c' : '#a16207'}">${escapeHtml(r.status) || 'pending'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(r.created_by) || '-'}</td>
      </tr>`).join('');

    const invRows = (liSummaryData.inventory || []).map((r, i) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;text-align:center">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:6px;font-weight:bold">${escapeHtml(r.item_name) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:center">${escapeHtml(r.quantity) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(r.notes) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;text-transform:capitalize;font-weight:bold;color:${r.status === 'approved' ? '#15803d' : r.status === 'rejected' ? '#b91c1c' : '#a16207'}">${escapeHtml(r.status) || 'pending'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml((r.created_at || '').slice(0, 10))}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(r.created_by) || '-'}</td>
      </tr>`).join('');

    const html = `
      <style>
        body{font-family:Arial,sans-serif;margin:20px;color:#222}
        h1{color:#7f1d1d;margin:0 0 4px;font-size:22px}
        h2{color:#7f1d1d;margin:18px 0 8px;font-size:16px;border-bottom:2px solid #7f1d1d;padding-bottom:4px}
        .meta{color:#555;font-size:12px;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:8px}
        th{background:#7f1d1d;color:#fff;padding:6px;border:1px solid #ddd;text-align:left}
        .stats{display:flex;gap:10px;flex-wrap:wrap;margin:6px 0 14px}
        .stat{padding:6px 10px;background:#f3f4f6;border-radius:6px;font-size:12px}
        .stat b{color:#7f1d1d}
        .empty{color:#999;font-style:italic;padding:8px;text-align:center}
        @media print{ body{margin:10px} }
      </style>
      <h1>Leave & Inventory Summary</h1>
      <div class="meta">
        Period: <b>${periodLabel}</b> &nbsp;|&nbsp;
        From: <b>${escapeHtml(liSummaryData.from_date)}</b> to <b>${escapeHtml(liSummaryData.to_date)}</b> &nbsp;|&nbsp;
        Generated: ${new Date().toLocaleString('en-IN')}
      </div>

      <h2>Staff Leave Requests</h2>
      <div class="stats">
        <div class="stat">Total: <b>${ls.total || 0}</b></div>
        <div class="stat">Approved: <b style="color:#15803d">${ls.approved || 0}</b></div>
        <div class="stat">Rejected: <b style="color:#b91c1c">${ls.rejected || 0}</b></div>
        <div class="stat">Pending: <b style="color:#a16207">${ls.pending || 0}</b></div>
      </div>
      ${leaveRows ? `<table>
        <thead><tr><th>#</th><th>Staff</th><th>Leave Date</th><th>Duration</th><th>Reason</th><th>Replacement</th><th>Status</th><th>Requested By</th></tr></thead>
        <tbody>${leaveRows}</tbody>
      </table>` : '<div class="empty">No leave requests in this period.</div>'}

      <h2>Inventory Requests</h2>
      <div class="stats">
        <div class="stat">Total: <b>${is_.total || 0}</b></div>
        <div class="stat">Total Qty: <b>${is_.total_qty || 0}</b></div>
        <div class="stat">Approved: <b style="color:#15803d">${is_.approved || 0}</b></div>
        <div class="stat">Rejected: <b style="color:#b91c1c">${is_.rejected || 0}</b></div>
        <div class="stat">Pending: <b style="color:#a16207">${is_.pending || 0}</b></div>
      </div>
      ${invRows ? `<table>
        <thead><tr><th>#</th><th>Item</th><th>Qty</th><th>Notes</th><th>Status</th><th>Requested On</th><th>Requested By</th></tr></thead>
        <tbody>${invRows}</tbody>
      </table>` : '<div class="empty">No inventory requests in this period.</div>'}
    `;
    safePrintWindow(`Leave_Inventory_${periodLabel}_Summary`, html);
  };

  const printReviewPDF = () => {
    if (!reviewData) return;
    const all = [...(reviewData.overdue || []), ...(reviewData.upcoming || [])];
    const rows = all.map((p, i) => {
      const isOverdue = reviewData.overdue?.some(o => o.id === p.id);
      return `<tr style="${isOverdue ? 'background:#fff5f5' : ''}">
        <td style="border:1px solid #ddd;padding:6px">${i + 1}</td>
        <td style="border:1px solid #ddd;padding:6px;font-weight:bold">${escapeHtml(p.name) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.age)} ${escapeHtml(p.sex)}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.contact_number) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.place) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px">${escapeHtml(p.diabetic_id) || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;font-weight:bold;color:${isOverdue ? '#dc2626' : '#1d4ed8'}">${p.review_date}${isOverdue ? ' (OVERDUE)' : ''}</td>
        <td style="border:1px solid #ddd;padding:6px">${p.date || '-'}</td>
        <td style="border:1px solid #ddd;padding:6px;width:80px"></td>
      </tr>`;
    }).join('');
    const styles = `body{font-family:Arial,sans-serif;padding:20px}h1{color:#7f1d1d}table{border-collapse:collapse;width:100%;font-size:10pt}th{background:#7f1d1d;color:white;padding:8px;border:1px solid #ddd;text-align:left}.stat{display:inline-block;padding:4px 12px;border-radius:4px;font-weight:bold;margin-right:8px}`;
    const body = `<h1>K.M. Multi Speciality Clinic, Koliyanur</h1>
      <h2>Review Reminder Call List</h2>
      <p>Generated: ${new Date().toLocaleString('en-IN')}</p>
      <p><span class="stat" style="background:#fee2e2;color:#dc2626">Overdue: ${reviewData.overdue_count}</span>
         <span class="stat" style="background:#dbeafe;color:#1d4ed8">Upcoming (3 days): ${reviewData.upcoming_count}</span></p>
      <table><thead><tr><th>#</th><th>Name</th><th>Age/Sex</th><th>Phone</th><th>Place</th><th>DM ID</th><th>Review Date</th><th>Last Visit</th><th>Called?</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <p style="margin-top:15px;color:#888;font-size:9pt">Please call and remind patients about their upcoming review appointment.</p>`;
    safePrintWindow('Review Reminders - K.M Clinic', `<style>${styles}</style>${body}`);
  };

  // Calculate waiting time from token creation and return color status
  const getWaitingTimeInfo = (patient) => {
    if (!patient.created_at || patient.status !== 'waiting') return null;
    // Only show wait-time indicator for walk-in patients. Online (phone/attender)
    // patients are self-scheduled — their wait timer isn't the clinic's flow signal.
    if ((patient.arrival_source || 'walk_in') !== 'walk_in') return null;
    
    const createdTime = new Date(patient.created_at).getTime();
    const now = Date.now();
    const waitingMins = Math.floor((now - createdTime) / (1000 * 60));
    
    let color, bgColor, borderColor, label;
    if (waitingMins <= 30) {
      color = 'text-green-700';
      bgColor = 'bg-green-100';
      borderColor = 'border-green-400';
      label = 'On Time';
    } else if (waitingMins <= 60) {
      color = 'text-yellow-700';
      bgColor = 'bg-yellow-100';
      borderColor = 'border-yellow-400';
      label = 'Delayed';
    } else {
      color = 'text-red-700';
      bgColor = 'bg-red-100';
      borderColor = 'border-red-400';
      label = 'Critical';
    }
    
    return {
      mins: waitingMins,
      color,
      bgColor,
      borderColor,
      label,
      display: waitingMins < 60 ? `${waitingMins}m` : `${Math.floor(waitingMins/60)}h ${waitingMins%60}m`
    };
  };

  // Calculate consultation duration for completed patients
  const getConsultationDuration = (patient) => {
    if (patient.entered_cabin_at && patient.completed_at) {
      const start = new Date(patient.entered_cabin_at).getTime();
      const end = new Date(patient.completed_at).getTime();
      const durationSecs = Math.floor((end - start) / 1000);
      return formatTimer(durationSecs);
    }
    return null;
  };

  const scrollToFeverPatients = () => {
    setHighlightFever(true);
    const feverEl = document.querySelector('[data-fever-patient]');
    if (feverEl) {
      feverEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => setHighlightFever(false), 3000);
  };

  // Inline prescription prompt state (used for setting Daily Rx Start from anywhere)
  const [showRxPrompt, setShowRxPrompt] = useState(false);
  const [rxPromptValue, setRxPromptValue] = useState('');
  const [rxAnchorInfo, setRxAnchorInfo] = useState(null); // {date, anchor, last_assigned, next_to_assign, is_set}
  // Queue counters per series (A=Adult, P=Paeds) — used elsewhere if needed
  const [queueSuggestion, setQueueSuggestion] = useState(null);

  const fetchQueueSuggestion = async () => {
    try {
      const r = await axios.get(`${API}/queue/next-suggestion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueueSuggestion(r.data);
    } catch (_) { /* ignore */ }
  };

  const skipWaitingPatient = async (pid) => {
    if (!window.confirm('Skip this patient? They will move to back of queue (2 skips = no-show).')) return;
    try {
      const r = await axios.patch(`${API}/patients/${pid}/skip`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const strikes = r.data?.skip_count || 0;
      const isNoShow = r.data?.status === 'no_show';
      toast.success(isNoShow ? 'Marked as NO-SHOW' : `Skipped (strike ${strikes}/2)`);
      fetchQueue();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to skip'); }
  };

  const fetchRxAnchor = async () => {
    try {
      const r = await axios.get(`${API}/prescription/daily-start`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRxAnchorInfo(r.data);
    } catch (_) { /* ignore */ }
  };

  const openRxAnchorPrompt = async () => {
    await fetchRxAnchor();
    setRxPromptValue('');
    setShowRxPrompt(true);
  };

  const saveRxAnchor = async () => {
    const v = (rxPromptValue || '').trim().replace(/\s+/g, '');
    if (!v) {
      toast.error("Enter a number like A2571 (or use 'Clear' to remove)");
      return;
    }
    if (!/^[A-Za-z]+\d{1,5}$/.test(v)) {
      toast.error('Format must be letter(s) + 1–5 digits, e.g. A2571');
      return;
    }
    try {
      await axios.post(
        `${API}/prescription/daily-start`,
        { start_number: v },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Today's Rx start set to ${v}`);
      setShowRxPrompt(false);
      setRxPromptValue('');
      await fetchRxAnchor();
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Rx start');
    }
  };

  const clearRxAnchor = async () => {
    try {
      await axios.post(
        `${API}/prescription/daily-start`,
        { start_number: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rx start cleared');
      setShowRxPrompt(false);
      await fetchRxAnchor();
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  // Fetch anchor on mount + when queue changes
  useEffect(() => {
    if (token) fetchRxAnchor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reset skip-Rx flag whenever the in-cabin patient changes
  useEffect(() => {
    setSkipRxForCabin(false);
  }, [patients.find(p => p.status === 'in_cabin')?.id]);

  // Skip Rx toggle for current in-cabin patient (resets when cabin patient changes)
  const [skipRxForCabin, setSkipRxForCabin] = useState(false);

  const handleComplete = async (patientId, reviewDays = null) => {
    await doCompleteRequest(patientId, reviewDays, null, skipRxForCabin);
    setSkipRxForCabin(false); // reset after completing
  };

  const doCompleteRequest = async (patientId, reviewDays, rxNumber, skipRx = false) => {
    try {
      const params = new URLSearchParams();
      if (reviewDays) {
        const d = new Date(); d.setDate(d.getDate() + reviewDays);
        params.append('review_date', d.toISOString().split('T')[0]);
      }
      if (rxNumber) params.append('prescription_number', rxNumber);
      if (skipRx) params.append('skip_rx', 'true');
      const url = `${API}/patients/${patientId}/complete${params.toString() ? `?${params.toString()}` : ''}`;
      const resp = await axios.patch(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      const rxAssigned = resp?.data?.prescription_number;
      const p = patients.find(x => x.id === patientId);
      const ctype = p?.consultation_type || 'regular';
      if (skipRx) {
        toast.success('Patient completed (Rx skipped)');
      } else if (!rxAssigned && (ctype === 'regular' || ctype === 'review' || ctype === 'exempted')) {
        // No Rx anchor set yet → gentle nudge to set one
        toast.warning("Set today's Rx start to enable auto-numbering", { duration: 4000 });
      } else {
        toast.success(reviewDays ? `Completed — Review in ${reviewDays} days${rxAssigned ? ` · Rx ${rxAssigned}` : ''}` : `Patient completed${rxAssigned ? ` · Rx ${rxAssigned}` : ''}`);
      }
      setSelectedReviewDays(null);
      fetchQueue();
      fetchDaySummary();
      fetchRxAnchor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    }
  };

  // Manual Rx edit (per-patient)
  const updatePatientRx = async (patientId, newRx) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/prescription`,
        { prescription_number: newRx || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newRx ? `Rx set to ${newRx}` : 'Rx cleared');
      fetchQueue();
      fetchDaySummary();
      fetchRxAnchor();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Rx');
    }
  };

  const handleBackToWaiting = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/back-to-waiting`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient sent back to waiting');
      fetchQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleCallPatient = async (patientId) => {
    try {
      await axios.patch(
        `${API}/patients/${patientId}/enter-cabin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Patient called to cabin');
      fetchQueue();
      fetchDaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to call patient');
    }
  };

  const waitingPatients = patients.filter(p => p.status === 'waiting');
  const inCabinPatients = patients.filter(p => p.status === 'in_cabin');

  // Auto-collapse header when a consultation starts; auto-expand when cabin empty.
  // Respects user pin (headerPinned overrides auto behaviour).
  useEffect(() => {
    if (headerPinned) return;
    setHeaderCollapsed(inCabinPatients.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCabinPatients.length, headerPinned]);
  const inERPatients = patients.filter(p => p.status === 'in_er'); // ER patients needing immediate attention
  const completedPatients = patients.filter(p => p.status === 'completed');
  const currentPatient = inCabinPatients[0] || null;

  // Helper function to parse age string to years
  const parseAgeToYears = (ageStr) => {
    if (!ageStr) return 0;
    const age = String(ageStr).trim().toLowerCase();
    if (age.endsWith('d')) {
      // Age in days
      const days = parseInt(age.slice(0, -1));
      return isNaN(days) ? 0 : days / 365;
    } else if (age.endsWith('m')) {
      // Age in months
      const months = parseInt(age.slice(0, -1));
      return isNaN(months) ? 0 : months / 12;
    } else if (age.endsWith('y')) {
      // Age in years with 'y' suffix
      const years = parseInt(age.slice(0, -1));
      return isNaN(years) ? 0 : years;
    } else {
      // Age in years (number only)
      const years = parseInt(age);
      return isNaN(years) ? 0 : years;
    }
  };

  // Helper function to get age in days
  const parseAgeToDays = (ageStr) => {
    if (!ageStr) return 0;
    const age = String(ageStr).trim().toLowerCase();
    if (age.endsWith('d')) {
      return parseInt(age.slice(0, -1)) || 0;
    } else if (age.endsWith('m')) {
      return (parseInt(age.slice(0, -1)) || 0) * 30;
    } else if (age.endsWith('y')) {
      return (parseInt(age.slice(0, -1)) || 0) * 365;
    } else {
      return (parseInt(age) || 0) * 365;
    }
  };

  // Format age for display (e.g., "15d" -> "15d", "8m" -> "8m", "35" -> "35y")
  const formatAge = (ageStr) => {
    if (!ageStr) return '0';
    const age = String(ageStr).trim().toLowerCase();
    if (age.endsWith('d') || age.endsWith('m') || age.endsWith('y')) {
      return age;
    }
    return `${age}y`;
  };

  // Calculate average waiting time for patients still waiting
  const getAverageWaitingTime = () => {
    const waitingPts = patients.filter(p => p.status === 'waiting' || p.status === 'queued_next');
    if (waitingPts.length === 0) return null;
    
    const now = Date.now();
    let totalWaitMins = 0;
    
    waitingPts.forEach(p => {
      if (p.created_at) {
        const created = new Date(p.created_at).getTime();
        const waitMins = Math.floor((now - created) / (1000 * 60));
        totalWaitMins += waitMins;
      }
    });
    
    const avgMins = Math.round(totalWaitMins / waitingPts.length);
    return {
      avgMins,
      count: waitingPts.length,
      formatted: avgMins < 60 ? `${avgMins}m` : `${Math.floor(avgMins / 60)}h ${avgMins % 60}m`,
      color: avgMins <= 30 ? 'green' : avgMins <= 60 ? 'yellow' : 'red'
    };
  };

  const avgWaitingTime = getAverageWaitingTime();

  // Get age symbol based on age category
  const getAgeSymbol = (ageStr) => {
    const days = parseAgeToDays(ageStr);
    const years = parseAgeToYears(ageStr);
    
    if (days < 28) return { symbol: '👶', label: 'Newborn', color: 'text-pink-600' };
    if (years < 1) return { symbol: '🍼', label: 'Infant', color: 'text-blue-500' };
    if (years < 3) return { symbol: '🧒', label: 'Toddler', color: 'text-green-500' };
    if (years < 12) return { symbol: '👦', label: 'Child', color: 'text-orange-500' };
    return null; // Adults don't get symbol
  };

  // Segregate by age: Under 5 (<5 years), 5-11 years, Adults (>=12 years)
  const under5Waiting = waitingPatients.filter(p => parseAgeToYears(p.age) < 5);
  const pediatricWaiting = waitingPatients.filter(p => parseAgeToYears(p.age) >= 5 && parseAgeToYears(p.age) < 12);
  const adultWaiting = waitingPatients.filter(p => parseAgeToYears(p.age) >= 12);
  const under5Completed = completedPatients.filter(p => parseAgeToYears(p.age) < 5);
  const pediatricCompleted = completedPatients.filter(p => parseAgeToYears(p.age) >= 5 && parseAgeToYears(p.age) < 12);
  const adultCompleted = completedPatients.filter(p => parseAgeToYears(p.age) >= 12);
  // Combined Paeds (<12) — used by Queue tab cards
  const paedsWaiting = waitingPatients.filter(p => parseAgeToYears(p.age) < 12);
  const paedsCompleted = completedPatients.filter(p => parseAgeToYears(p.age) < 12);

  // ── Average waiting time for WALK-IN patients only (the "mood" pill on sticky) ──
  // Online (phone/attender) patients are excluded — they self-schedule and their wait is
  // not the clinic's flow indicator. Falls back to currently-waiting walk-ins.
  const walkinWaitingPatients = waitingPatients.filter(
    p => (p.arrival_source || 'walk_in') === 'walk_in'
  );
  const avgWalkinWaitMin = walkinWaitingPatients.length === 0
    ? null
    : Math.round(
        walkinWaitingPatients.reduce((sum, p) => {
          const t = p.created_at ? new Date(p.created_at).getTime() : nowTick;
          return sum + Math.max(0, nowTick - t);
        }, 0) / walkinWaitingPatients.length / 60000
      );
  let waitMoodIcon = Smile;
  let waitMoodColor = 'text-emerald-600 bg-emerald-50 border-emerald-200';
  let waitMoodLabel = 'Smooth flow';
  if (avgWalkinWaitMin !== null) {
    if (avgWalkinWaitMin > 60) {
      waitMoodIcon = Frown;
      waitMoodColor = 'text-red-700 bg-red-50 border-blue-200 animate-pulse';
      waitMoodLabel = 'Long wait';
    } else if (avgWalkinWaitMin > 30) {
      waitMoodIcon = Meh;
      waitMoodColor = 'text-orange-600 bg-orange-50 border-orange-200';
      waitMoodLabel = 'Moderate wait';
    }
  }

  // Stats for sticky bar - current live stats with separate age groups
  const liveStats = {
    total: patients.length,
    waiting: waitingPatients.length,
    inER: inERPatients.length,
    completed: completedPatients.length,
    under5: under5Waiting.length + under5Completed.length,
    age5to11: pediatricWaiting.length + pediatricCompleted.length,
    adults: adultWaiting.length + adultCompleted.length,
    fever: patients.filter(p => p.fever).length,
    urgent: patients.filter(p => p.urgent).length,
    review: patients.filter(p => p.review).length,
    dm: patients.filter(p => p.diabetes).length,
    htn: patients.filter(p => p.htn).length,
  };

  // Filter patients based on search query
  const filteredUnder5Waiting = under5Waiting.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredPediatricWaiting = pediatricWaiting.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredPaedsWaiting = paedsWaiting.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredPaedsCompleted = paedsCompleted.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredAdultWaiting = adultWaiting.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredUnder5Completed = under5Completed.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredPediatricCompleted = pediatricCompleted.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  const filteredAdultCompleted = adultCompleted.filter(patient => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (patient.name || '').toLowerCase().includes(query) ||
      patient.token_number.toLowerCase().includes(query)
    );
  });

  // Calculate productivity stats - must be after completedPatients is defined
  const getProductivityStats = () => {
    const completedWithTime = completedPatients.filter(p => p.entered_cabin_at && p.completed_at);
    
    // Total patients who came today (got a token) - includes waiting, in_cabin, and completed
    const totalCame = patients.length;
    // Missed = patients still in waiting status (not yet seen)
    const missedCount = waitingPatients.length;
    // Missed ratio as percentage
    const missedRatio = totalCame > 0 ? ((missedCount / totalCame) * 100).toFixed(1) : 0;
    
    // Calculate trend vs weekly average
    let trendPercent = null;
    let trendDirection = 'neutral'; // 'up', 'down', 'neutral'
    if (weeklyAverage !== null && weeklyAverage > 0) {
      const diff = totalCame - weeklyAverage;
      trendPercent = ((diff / weeklyAverage) * 100).toFixed(0);
      trendDirection = diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral';
    }
    
    // Calculate Patient Waiting Time Metrics (for patients who entered cabin)
    const patientsWithWaitTime = patients.filter(p => p.created_at && p.entered_cabin_at);
    let waitingMetrics = {
      greenCount: 0,   // <30 mins
      yellowCount: 0,  // 31-60 mins
      redCount: 0,     // >60 mins
      avgWaitingMins: 0,
      totalWithWaitData: 0
    };
    
    if (patientsWithWaitTime.length > 0) {
      let totalWaitMins = 0;
      patientsWithWaitTime.forEach(p => {
        const created = new Date(p.created_at).getTime();
        const entered = new Date(p.entered_cabin_at).getTime();
        const waitMins = Math.floor((entered - created) / (1000 * 60));
        totalWaitMins += waitMins;
        
        if (waitMins <= 30) {
          waitingMetrics.greenCount++;
        } else if (waitMins <= 60) {
          waitingMetrics.yellowCount++;
        } else {
          waitingMetrics.redCount++;
        }
      });
      
      waitingMetrics.avgWaitingMins = Math.round(totalWaitMins / patientsWithWaitTime.length);
      waitingMetrics.totalWithWaitData = patientsWithWaitTime.length;
    }
    
    if (completedWithTime.length === 0) {
      return {
        firstPatientTime: null,
        lastPatientTime: null,
        averageTime: 0,
        highestTime: 0,
        lowestTime: 0,
        totalPatients: 0,
        totalConsultationTime: 0,
        patientsPerHour: 0,
        highestPatient: null,
        lowestPatient: null,
        totalCame,
        missedCount,
        missedRatio,
        trendPercent,
        trendDirection,
        weeklyAvg: weeklyAverage,
        waitingMetrics
      };
    }

    // Get consultation durations in seconds
    const durations = completedWithTime.map(p => {
      const start = new Date(p.entered_cabin_at).getTime();
      const end = new Date(p.completed_at).getTime();
      return {
        patient: p,
        durationSecs: Math.floor((end - start) / 1000),
        enteredAt: new Date(p.entered_cabin_at),
        completedAt: new Date(p.completed_at)
      };
    });

    // Sort by entered time to get first and last
    const sortedByEntry = [...durations].sort((a, b) => a.enteredAt - b.enteredAt);
    const firstPatientTime = sortedByEntry[0]?.enteredAt;
    const lastPatientTime = sortedByEntry[sortedByEntry.length - 1]?.completedAt;

    // Calculate average, highest, lowest
    const totalDuration = durations.reduce((sum, d) => sum + d.durationSecs, 0);
    const averageTime = Math.floor(totalDuration / durations.length);
    
    const sortedByDuration = [...durations].sort((a, b) => b.durationSecs - a.durationSecs);
    const highestTime = sortedByDuration[0]?.durationSecs || 0;
    const lowestTime = sortedByDuration[sortedByDuration.length - 1]?.durationSecs || 0;

    // Calculate patients per hour
    const workingTimeMs = lastPatientTime - firstPatientTime;
    const workingTimeHours = workingTimeMs / (1000 * 60 * 60);
    const patientsPerHour = workingTimeHours > 0 ? (durations.length / workingTimeHours).toFixed(1) : 0;

    return {
      firstPatientTime,
      lastPatientTime,
      averageTime,
      highestTime,
      lowestTime,
      totalPatients: durations.length,
      totalConsultationTime: totalDuration,
      patientsPerHour,
      highestPatient: sortedByDuration[0]?.patient,
      lowestPatient: sortedByDuration[sortedByDuration.length - 1]?.patient,
      totalCame,
      missedCount,
      missedRatio,
      trendPercent,
      trendDirection,
      weeklyAvg: weeklyAverage,
      waitingMetrics
    };
  };

  const productivityStats = getProductivityStats();

  // Calculate overall productivity grade based on multiple metrics
  const getProductivityGrade = (stats, otarPercentage = null) => {
    if (!stats || stats.totalCame === 0) return null;
    
    let scores = [];
    
    // 1. Completion Rate Score (weight: 30%)
    const completionRate = stats.totalCame > 0 ? 
      ((stats.totalCame - stats.missedCount) / stats.totalCame) * 100 : 0;
    if (completionRate >= 95) scores.push({ metric: 'Completion', score: 4, label: 'Excellent' });
    else if (completionRate >= 85) scores.push({ metric: 'Completion', score: 3, label: 'Good' });
    else if (completionRate >= 70) scores.push({ metric: 'Completion', score: 2, label: 'Average' });
    else scores.push({ metric: 'Completion', score: 1, label: 'Poor' });
    
    // 2. Patients Per Hour Score (weight: 25%)
    const pph = parseFloat(stats.patientsPerHour) || 0;
    if (pph >= 8) scores.push({ metric: 'Speed', score: 4, label: 'Excellent' });
    else if (pph >= 5) scores.push({ metric: 'Speed', score: 3, label: 'Good' });
    else if (pph >= 3) scores.push({ metric: 'Speed', score: 2, label: 'Average' });
    else if (pph > 0) scores.push({ metric: 'Speed', score: 1, label: 'Poor' });
    
    // 3. Average Wait Time Score (weight: 25%)
    if (stats.waitingMetrics && stats.waitingMetrics.avgWaitingMins !== undefined) {
      const avgWait = stats.waitingMetrics.avgWaitingMins;
      if (avgWait <= 15) scores.push({ metric: 'Wait Time', score: 4, label: 'Excellent' });
      else if (avgWait <= 30) scores.push({ metric: 'Wait Time', score: 3, label: 'Good' });
      else if (avgWait <= 45) scores.push({ metric: 'Wait Time', score: 2, label: 'Average' });
      else scores.push({ metric: 'Wait Time', score: 1, label: 'Poor' });
    }
    
    // 4. Punctuality/OTAR Score (weight: 20%)
    if (otarPercentage !== null) {
      if (otarPercentage >= 90) scores.push({ metric: 'Punctuality', score: 4, label: 'Excellent' });
      else if (otarPercentage >= 75) scores.push({ metric: 'Punctuality', score: 3, label: 'Good' });
      else if (otarPercentage >= 50) scores.push({ metric: 'Punctuality', score: 2, label: 'Average' });
      else scores.push({ metric: 'Punctuality', score: 1, label: 'Poor' });
    }
    
    // 5. Volume Trend Score (bonus)
    if (stats.trendDirection === 'up' && stats.trendPercent > 10) {
      scores.push({ metric: 'Trend', score: 4, label: 'Excellent' });
    } else if (stats.trendDirection === 'up') {
      scores.push({ metric: 'Trend', score: 3, label: 'Good' });
    } else if (stats.trendDirection === 'neutral') {
      scores.push({ metric: 'Trend', score: 2, label: 'Average' });
    } else if (stats.trendDirection === 'down') {
      scores.push({ metric: 'Trend', score: 1, label: 'Poor' });
    }
    
    if (scores.length === 0) return null;
    
    const avgScore = scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
    
    let overallGrade, gradeColor, gradeIcon;
    if (avgScore >= 3.5) {
      overallGrade = 'EXCELLENT';
      gradeColor = 'from-green-500 to-emerald-500';
      gradeIcon = '🌟';
    } else if (avgScore >= 2.5) {
      overallGrade = 'GOOD';
      gradeColor = 'from-blue-500 to-cyan-500';
      gradeIcon = '👍';
    } else if (avgScore >= 1.5) {
      overallGrade = 'AVERAGE';
      gradeColor = 'from-yellow-500 to-amber-500';
      gradeIcon = '📊';
    } else {
      overallGrade = 'NEEDS IMPROVEMENT';
      gradeColor = 'from-red-500 to-orange-500';
      gradeIcon = '⚠️';
    }
    
    return {
      grade: overallGrade,
      color: gradeColor,
      icon: gradeIcon,
      avgScore: avgScore.toFixed(1),
      breakdown: scores
    };
  };

  const todayGrade = getProductivityGrade(productivityStats, otarData?.otar_percentage);

  // Calculate grade for weekly/monthly performance data
  const getPerformanceDataGrade = (data) => {
    if (!data || !data.total_patients) return null;
    
    let scores = [];
    
    // Completion Rate
    const completionRate = data.total_patients > 0 ? 
      ((data.total_completed || 0) / data.total_patients) * 100 : 0;
    if (completionRate >= 95) scores.push({ metric: 'Completion', score: 4, label: 'Excellent' });
    else if (completionRate >= 85) scores.push({ metric: 'Completion', score: 3, label: 'Good' });
    else if (completionRate >= 70) scores.push({ metric: 'Completion', score: 2, label: 'Average' });
    else scores.push({ metric: 'Completion', score: 1, label: 'Poor' });
    
    // Volume (patients per day average)
    const days = data.daily_breakdown?.length || data.weekly_breakdown?.length || 7;
    const avgPerDay = data.total_patients / days;
    if (avgPerDay >= 15) scores.push({ metric: 'Volume', score: 4, label: 'Excellent' });
    else if (avgPerDay >= 10) scores.push({ metric: 'Volume', score: 3, label: 'Good' });
    else if (avgPerDay >= 5) scores.push({ metric: 'Volume', score: 2, label: 'Average' });
    else scores.push({ metric: 'Volume', score: 1, label: 'Poor' });
    
    // Missed ratio
    const missedRatio = data.total_patients > 0 ? 
      ((data.total_missed || 0) / data.total_patients) * 100 : 0;
    if (missedRatio <= 5) scores.push({ metric: 'Missed', score: 4, label: 'Excellent' });
    else if (missedRatio <= 15) scores.push({ metric: 'Missed', score: 3, label: 'Good' });
    else if (missedRatio <= 30) scores.push({ metric: 'Missed', score: 2, label: 'Average' });
    else scores.push({ metric: 'Missed', score: 1, label: 'Poor' });
    
    if (scores.length === 0) return null;
    
    const avgScore = scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
    
    let overallGrade, gradeColor, gradeIcon;
    if (avgScore >= 3.5) {
      overallGrade = 'EXCELLENT';
      gradeColor = 'from-green-500 to-emerald-500';
      gradeIcon = '🌟';
    } else if (avgScore >= 2.5) {
      overallGrade = 'GOOD';
      gradeColor = 'from-blue-500 to-cyan-500';
      gradeIcon = '👍';
    } else if (avgScore >= 1.5) {
      overallGrade = 'AVERAGE';
      gradeColor = 'from-yellow-500 to-amber-500';
      gradeIcon = '📊';
    } else {
      overallGrade = 'NEEDS IMPROVEMENT';
      gradeColor = 'from-red-500 to-orange-500';
      gradeIcon = '⚠️';
    }
    
    return {
      grade: overallGrade,
      color: gradeColor,
      icon: gradeIcon,
      avgScore: avgScore.toFixed(1),
      breakdown: scores
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'in_cabin': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Power Mode exit FAB — visible only in power mode */}
      {powerMode && (
        <button
          onClick={togglePowerMode}
          data-testid="power-mode-exit-fab"
          title="Exit Power Mode"
          className="fixed top-2 right-2 z-[100] h-9 px-3 rounded-full text-white text-xs font-bold flex items-center gap-1.5 shadow-lg active:scale-95 transition-transform"
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
            boxShadow: '0 4px 14px rgba(30,58,138,0.40)',
          }}
        >
          <Zap className="h-3.5 w-3.5" />
          <X className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Exit Power</span>
        </button>
      )}

      {/* Floating Nurse-Call Bell Overlay — collapsed FAB by default; expands on click and auto-hides */}
      <div
        className="fixed bottom-24 right-4 z-[80] flex flex-col items-end gap-1.5"
        data-testid="floating-bell-overlay"
        onMouseEnter={() => { if (bellExpanded && bellAutoHideRef.current) clearTimeout(bellAutoHideRef.current); }}
        onMouseLeave={() => { if (bellExpanded) scheduleBellAutoHide(2500); }}
      >
        {bellExpanded ? (
          <>
            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-500 bg-white/95 backdrop-blur px-2 py-0.5 rounded-full shadow border border-slate-200">
              <span>Call Staff</span>
              <button
                onClick={closeBellDrawer}
                className="text-slate-400 hover:text-slate-700 transition"
                title="Hide"
                data-testid="bell-collapse-btn"
                aria-label="Collapse bell drawer"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center gap-1.5 bg-slate-900/90 backdrop-blur p-1.5 rounded-full shadow-xl border-2 border-slate-700 animate-in fade-in slide-in-from-right-2 duration-200">
              <button
                onClick={() => ringBell('cbg')}
                title="Call staff for CBG check"
                data-testid="bell-cbg-btn"
                className="h-11 w-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 flex items-center justify-center text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform border border-amber-200/60"
                aria-label="Call CBG"
              >
                🩸
              </button>
              <button
                onClick={() => ringBell('temp')}
                title="Call staff for Temperature check"
                data-testid="bell-temp-btn"
                className="h-11 w-11 rounded-full bg-gradient-to-br from-orange-400 to-red-500 hover:from-orange-300 hover:to-red-400 flex items-center justify-center text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform border border-orange-200/60"
                aria-label="Call Temp"
              >
                🌡️
              </button>
              <button
                onClick={() => ringBell('attention')}
                title="Call staff (general attention)"
                data-testid="bell-attention-btn"
                className="h-11 w-11 rounded-full bg-gradient-to-br from-red-500 to-rose-700 hover:from-red-400 hover:to-rose-600 flex items-center justify-center text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform border border-blue-200/60"
                aria-label="Call Attention"
              >
                🛎️
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={openBellDrawer}
            title="Call Staff"
            data-testid="bell-fab-btn"
            aria-label="Open staff call drawer"
            className="h-12 w-12 rounded-full text-white flex items-center justify-center shadow-xl border-2 border-white/70 hover:scale-110 active:scale-95 transition-transform"
            style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
              boxShadow: '0 6px 18px rgba(30,58,138,0.35)',
            }}
          >
            <Bell className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Header - Maroon Theme — auto-collapses during consultation */}
      {!powerMode && (
      <header
        ref={headerRef}
        className={`text-slate-800 shadow-sm transition-all duration-300 ease-in-out overflow-hidden relative ${headerCollapsed ? 'max-h-12 md:max-h-12' : 'max-h-[600px]'}`}
        data-testid="doctor-header"
        style={{
          background:
            'radial-gradient(circle at 20% 20%, rgba(30,58,138,0.10), transparent 45%), radial-gradient(circle at 80% 80%, rgba(22,163,74,0.10), transparent 50%), linear-gradient(135deg, #f8fafc 0%, #ecfdf5 100%)',
          borderBottom: '1px solid rgba(30,58,138,0.10)',
          boxShadow: '0 2px 12px rgba(30,58,138,0.06)',
        }}
      >
        {/* Soft brand top accent line */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(30,58,138,0.35), rgba(22,163,74,0.35), transparent)' }} />
        {headerCollapsed ? (
          // Minimal slim bar when collapsed: brand + bell + expand handle
          <div className="max-w-7xl mx-auto px-3 md:px-6 py-1.5 flex items-center justify-between gap-2 relative">
            <div className="flex items-center gap-2 min-w-0">
              <img src={CLINIC_LOGO} alt="K.M" className="h-7 w-7 object-contain" style={{ mixBlendMode: 'multiply' }} />
              <span
                className="font-bold text-sm md:text-base tracking-tight bg-clip-text"
                style={{
                  fontFamily: 'Manrope, sans-serif',
                  color: 'transparent',
                  backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                  WebkitBackgroundClip: 'text',
                }}
              >
                K.M
              </span>
              <span className="text-[11px] text-slate-500 truncate hidden sm:inline">{onDutyDoctor?.name || user?.username || 'Doctor'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={togglePowerMode}
                title={powerMode ? 'Exit Power Mode' : 'Power Mode — fullscreen Board + Cabin'}
                data-testid="power-mode-toggle-collapsed"
                className={`h-8 px-2 text-xs font-bold ${powerMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-slate-700 hover:bg-emerald-100'}`}
              >
                <Zap className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setShowNotifications(true); fetchNotifications(); }}
                className="relative text-slate-700 hover:bg-emerald-100 h-8 w-8 p-0" data-testid="notif-bell-collapsed">
                <Bell className="h-4 w-4" />
                {notifCount > 0 && <span className="absolute top-0 right-0 bg-emerald-500 text-white text-[9px] font-black rounded-full w-3.5 h-3.5 flex items-center justify-center">{notifCount}</span>}
              </Button>
              <Button variant="ghost" size="sm"
                onClick={() => { setHeaderCollapsed(false); setHeaderPinned(true); }}
                className="text-slate-700 hover:bg-emerald-100 h-8 px-2 text-xs"
                title="Show menu"
                data-testid="header-expand-button"
              >
                <ChevronDown className="h-4 w-4" />
                <span className="hidden md:inline ml-1">Menu</span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 md:gap-4 min-w-0">
                  <img
                    src={CLINIC_LOGO}
                    alt="K.M Multi Speciality Clinic"
                    className="h-12 w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
                    data-testid="doctor-header-logo"
                    style={{ mixBlendMode: 'multiply', filter: 'drop-shadow(0 4px 12px rgba(30,58,138,0.18))' }}
                  />
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 md:gap-3">
                      <h1
                        className="text-xl md:text-2xl font-bold tracking-tight bg-clip-text"
                        style={{
                          fontFamily: 'Manrope, sans-serif',
                          color: 'transparent',
                          backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                          WebkitBackgroundClip: 'text',
                        }}
                      >
                        K.M
                      </h1>
                      <span className="text-xs md:text-sm font-medium text-slate-500">Doctor Dashboard</span>
                    </div>
                    <div className="mt-0.5 space-y-0.5">
                      <h2 className="text-sm md:text-base font-black tracking-wide text-slate-800" data-testid="doctor-name-header">
                        {onDutyDoctor?.name || user?.username || 'Doctor'}
                      </h2>
                      <p className="text-xs text-slate-500">Multi-Speciality Clinic, Koliyanur</p>
                      <LiveClock className="text-xs text-slate-500" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
              {/* Mobile menu toggle */}
              <div className="flex items-center justify-between md:hidden">
                <div className="flex items-center gap-1">
                  {/* Notification bell - always visible */}
                  <Button variant="ghost" onClick={() => { setShowNotifications(true); fetchNotifications(); }}
                    className="relative text-slate-700 hover:bg-emerald-100" data-testid="notif-bell-mobile">
                    <Bell className="h-5 w-5" />
                    {notifCount > 0 && <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs font-black rounded-full w-5 h-5 flex items-center justify-center">{notifCount}</span>}
                  </Button>
                </div>
                <Button variant="ghost" onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
                  className="text-slate-700 hover:bg-emerald-100" data-testid="header-menu-toggle">
                  {headerMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              </div>
              {/* Desktop: always visible. Mobile: toggle */}
              <div className={`flex items-center gap-1 md:gap-2 flex-wrap ${headerMenuOpen ? '' : 'hidden md:flex'}`}>
              {/* Row 1: Primary Actions */}
              <Button
                onClick={() => setShowProductivity(true)}
                variant="ghost"
                data-testid="productivity-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <TrendingUp className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Productivity</span>
              </Button>
              <Button
                onClick={openFinance}
                variant="ghost"
                data-testid="daily-finance-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Wallet className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Finance</span>
              </Button>
              <Button
                onClick={openNursingRevenue}
                variant="ghost"
                data-testid="nursing-revenue-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Banknote className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Nursing</span>
              </Button>
              </div>
              {/* Row 2: Settings & Navigation */}
              <div className={`flex items-center gap-1 md:gap-2 flex-wrap ${headerMenuOpen ? '' : 'hidden md:flex'}`}>
              <Button
                onClick={() => setShowFeeSettings(true)}
                variant="ghost"
                data-testid="fee-settings-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
                title="Consultation Fee Settings"
              >
                <IndianRupee className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Fees</span>
              </Button>
              <Button
                onClick={() => { setShowDiabeticRegistry(true); fetchDiabeticRegistry(); }}
                variant="ghost"
                data-testid="diabetic-registry-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Activity className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">DM Registry</span>
              </Button>
              <Button
                onClick={() => { setShowCampEntry(true); fetchCampPatients(campFilterType, campStartDate, campEndDate); }}
                variant="ghost"
                data-testid="doctor-camp-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Users className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Camp</span>
              </Button>
              <Button
                onClick={() => setShowMRD(true)}
                variant="ghost"
                data-testid="mrd-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <FileText className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">MRD</span>
              </Button>
              <Button
                onClick={() => { setShowReviews(true); fetchReviews(); }}
                variant="ghost"
                data-testid="review-reminders-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Calendar className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Reviews</span>
              </Button>
              <Button
                onClick={() => { setShowNotifications(true); fetchNotifications(); }}
                variant="ghost"
                data-testid="notif-bell-desktop"
                className="relative hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Bell className="mr-1 md:mr-2 h-4 w-4" />
                {notifCount > 0 && <span className="absolute -top-1 right-0 bg-emerald-500 text-white text-xs font-black rounded-full w-4 h-4 flex items-center justify-center">{notifCount}</span>}
                <span className="hidden md:inline">Approvals</span>
              </Button>
              <Button
                onClick={() => { setShowLISummary(true); fetchLISummary(liSummaryPeriod); }}
                variant="ghost"
                data-testid="li-summary-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
                title="Leave & Inventory Summary"
              >
                <ClipboardList className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">L/I Summary</span>
              </Button>
              <Button
                onClick={() => setShowTVSettings(true)}
                variant="ghost"
                data-testid="tv-settings-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
                title="TV Display & Footer settings"
              >
                <Tv className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">TV Display</span>
              </Button>
              <Button
                onClick={openPnl}
                variant="ghost"
                data-testid="pnl-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
                title="Profit & Loss tracker"
              >
                <TrendingUp className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">P&amp;L</span>
              </Button>
              <Button
                onClick={() => navigate('/reports')}
                variant="ghost"
                data-testid="reports-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <BarChart3 className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Reports</span>
              </Button>
              <Button
                onClick={() => setShowTokenSettings(true)}
                variant="ghost"
                data-testid="token-settings-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
                title="Token System Settings"
              >
                <Hash className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Tokens</span>
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="ghost"
                data-testid="settings-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <Settings className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Doctors</span>
              </Button>
              <Button
                onClick={openPastRecords}
                variant="ghost"
                data-testid="past-records-button"
                className="hover:bg-blue-100 text-blue-900 text-xs md:text-sm"
              >
                <History className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">History</span>
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                data-testid="logout-button"
                className="hover:bg-red-50 text-red-700 text-xs md:text-sm"
              >
                <LogOut className="mr-1 md:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Logout</span>
              </Button>
              </div>
            </div>
          </div>
        </div>
            {/* Collapse handle - visible only when expanded */}
            <button
              onClick={() => { setHeaderCollapsed(true); setHeaderPinned(false); }}
              className="w-full flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-slate-900 hover:bg-emerald-100 py-1 transition-colors"
              data-testid="header-collapse-button"
              title="Collapse header"
            >
              <ChevronDown className="h-3 w-3 rotate-180" />
              <span>Hide menu</span>
            </button>
            <button
              onClick={togglePowerMode}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-white py-1.5 transition-all"
              data-testid="power-mode-toggle"
              title={powerMode ? 'Exit Power Mode' : 'Power Mode — fullscreen Board + Cabin'}
              style={{
                background: powerMode ? '#1e40af' : 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
              }}
            >
              <Zap className="h-3.5 w-3.5" />
              <span>{powerMode ? 'Exit Power Mode' : 'Power Mode'}</span>
            </button>
          </>
        )}
      </header>
      )}

      {/* Doctor Clinic Arrival Status — above the motto bar (auto-hidden while patient in cabin to declutter) */}
      {!powerMode && inCabinPatients.length === 0 && (() => {
        const targetTime = opdSettings?.target_arrival_time || '18:00';
        const rec = arrivalStreak?.today_record || opdArrival;
        const consecutiveLate = arrivalStreak?.consecutive_late_days || 0;
        const lateWeek = arrivalStreak?.late_days_this_week || 0;

        // Determine status
        let bannerCls = 'bg-slate-50 border-slate-200 text-slate-600';
        let icon = '⏰';
        let mainText;
        let subText = `Clinic time ${targetTime}`;
        let dataStatus = 'not-arrived';

        if (rec && rec.arrival_time) {
          const arrTime = new Date(rec.arrival_time);
          const hh = arrTime.getHours().toString().padStart(2, '0');
          const mm = arrTime.getMinutes().toString().padStart(2, '0');
          const [tH, tM] = targetTime.split(':').map(Number);
          const diffMin = (arrTime.getHours() * 60 + arrTime.getMinutes()) - (tH * 60 + tM);

          if (diffMin < -10) {
            bannerCls = 'bg-emerald-100 border-emerald-300 text-emerald-800';
            icon = '🌟';
            mainText = `EARLY · ${hh}:${mm} (${Math.abs(diffMin)} min before)`;
            dataStatus = 'early';
          } else if (diffMin <= 0) {
            bannerCls = 'bg-emerald-50 border-emerald-200 text-emerald-700';
            icon = '✓';
            mainText = `ON TIME · ${hh}:${mm}`;
            dataStatus = 'on-time';
          } else if (diffMin <= 15) {
            bannerCls = 'bg-amber-100 border-amber-300 text-amber-800';
            icon = '⚠️';
            mainText = `SLIGHTLY LATE · ${hh}:${mm} (+${diffMin} min)`;
            dataStatus = 'slightly-late';
          } else {
            bannerCls = 'bg-red-100 border-red-400 text-blue-800 animate-pulse';
            icon = '🚨';
            mainText = `LATE · ${hh}:${mm} (+${diffMin} min)`;
            dataStatus = 'late';
          }
        } else {
          mainText = 'OPD not started yet';
        }

        // Streak suffix
        let streakSuffix = null;
        if (consecutiveLate >= 2) {
          streakSuffix = (
            <span className="font-black ml-2 px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] animate-pulse" data-testid="arrival-streak-badge">
              {consecutiveLate} days late in a row!
            </span>
          );
        } else if (lateWeek >= 3) {
          streakSuffix = (
            <span className="font-bold ml-2 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[11px]" data-testid="arrival-week-badge">
              {lateWeek} late days this week
            </span>
          );
        }

        return (
          <div
            className={`py-1.5 px-4 flex items-center justify-center gap-2 border-y text-xs font-semibold ${bannerCls}`}
            data-testid="doctor-arrival-banner"
            data-status={dataStatus}
          >
            <span className="text-base leading-none">{icon}</span>
            <span data-testid="arrival-main-text">{mainText}</span>
            <span className="opacity-60">·</span>
            <span className="opacity-80">{subText}</span>
            {streakSuffix}
            <button
              onClick={openPunchLog}
              className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/70 hover:bg-white text-[10px] font-bold border border-current/30 transition-colors"
              title="View daily punch log + weekly vs monthly performance"
              data-testid="punch-log-btn"
            >
              <History className="h-3 w-3" />
              Log
            </button>
            <button
              onClick={() => { setArrivalTimeInput(targetTime); setShowArrivalSettings(true); }}
              className="ml-1 p-0.5 rounded hover:bg-black/10 transition-colors"
              title="Set clinic arrival time"
              data-testid="arrival-settings-btn"
            >
              <Settings className="h-3 w-3 opacity-60" />
            </button>
          </div>
        );
      })()}

      {/* Punctuality coaching banner + Punch-In card (auto-hidden while patient in cabin) */}
      {!powerMode && inCabinPatients.length === 0 && punchLog && (punchLog.coaching?.should_nudge || !punchLog.has_manual_today) && (
        <div className="px-3 py-1.5 flex flex-wrap items-center justify-center gap-2" data-testid="punctuality-coach-bar">
          {punchLog.coaching?.should_nudge && (
            <div
              className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm ${
                punchLog.coaching.tone === 'danger'
                  ? 'bg-red-50 border-blue-200 text-blue-800'
                  : 'bg-amber-50 border-amber-300 text-amber-800'
              }`}
              data-testid="punctuality-coach-banner"
            >
              <span className="text-base leading-none">{punchLog.coaching.tone === 'danger' ? '🚨' : '⏰'}</span>
              <span><b>{punchLog.coaching.headline}.</b> {punchLog.coaching.message}</span>
            </div>
          )}
          {!punchLog.has_manual_today && (
            <div className="flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-300 px-2 py-0.5" data-testid="punch-in-card">
              <span className="text-xs font-bold text-blue-800">Punch in:</span>
              <input
                type="time"
                value={punchInValue}
                onChange={(e) => setPunchInValue(e.target.value)}
                className="text-xs px-1 py-0.5 border border-blue-200 rounded bg-white font-mono"
                data-testid="punch-in-time"
              />
              <button
                onClick={() => punchInNow(punchInValue)}
                className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                data-testid="punch-in-set-btn"
                title="Punch in for the time entered"
              >
                Set
              </button>
              <button
                onClick={() => punchInNow(null)}
                className="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="punch-in-now-btn"
                title="Punch in right now"
              >
                Now
              </button>
            </div>
          )}
        </div>
      )}

      {/* Motivational Motto + Live Target Counter — above sticky (auto-hidden while patient in cabin) */}
      {!powerMode && inCabinPatients.length === 0 && (
      <div className="bg-gradient-to-r from-blue-900/5 via-transparent to-emerald-700/5 py-1.5 px-4 flex items-center justify-center gap-3">
        {editingMotto ? (
          <div className="flex items-center gap-2 bg-white rounded-full px-4 py-1 border shadow-sm w-full max-w-md">
            <input type="text" value={mottoInput} onChange={(e) => setMottoInput(e.target.value)}
              placeholder="Your target or motto..." className="flex-1 text-sm font-medium text-center border-none outline-none bg-transparent"
              autoFocus data-testid="motto-input"
              onKeyDown={(e) => { if (e.key === 'Enter') saveMotto(); if (e.key === 'Escape') setEditingMotto(false); }} />
            <button onClick={saveMotto} className="text-green-600 hover:text-green-800" data-testid="motto-save"><Check className="h-4 w-4" /></button>
            <button onClick={() => setEditingMotto(false)} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
          </div>
        ) : motto ? (
          <div onClick={() => { setMottoInput(motto); setEditingMotto(true); }}
            className="cursor-pointer group flex items-center gap-2 hover:opacity-80 transition-opacity" title="Click to edit" data-testid="motto-display">
            <Target className="h-3.5 w-3.5 text-blue-800 opacity-60" />
            <span className="text-sm font-bold text-blue-900 italic">{motto}</span>
            <span className="text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">edit</span>
          </div>
        ) : (
          <button onClick={() => { setMottoInput(''); setEditingMotto(true); }}
            className="text-xs text-slate-400 hover:text-slate-400" data-testid="motto-add-button">+ Set target</button>
        )}
        <div
          className="flex items-center gap-1 text-white px-3 py-1 rounded-full text-sm font-black"
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
            boxShadow: '0 4px 14px rgba(30,58,138,0.30)',
          }}
          data-testid="live-target-counter"
        >
          <span>{daySummary?.total || 0}</span>
          <span className="opacity-60 font-normal">/</span>
          <span className="opacity-80">80</span>
        </div>
      </div>
      )}

      {/* Current Patient - STICKY header — clean Login-theme look */}
      <div
        ref={stickyBarRef}
        className="sticky top-0 z-40 pt-1 pb-1 relative overflow-hidden"
        data-testid="doctor-sticky-bar"
        style={{
          background:
            'radial-gradient(circle at 0% 50%, rgba(30,58,138,0.06), transparent 35%), radial-gradient(circle at 100% 50%, rgba(22,163,74,0.07), transparent 40%), linear-gradient(180deg, #ffffff 0%, #ecfdf5 100%)',
          borderBottom: '1px solid rgba(30,58,138,0.12)',
          boxShadow: '0 4px 12px rgba(30,58,138,0.06)',
        }}
      >
        {/* Brand accent line under the bar */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(30,58,138,0.35), rgba(22,163,74,0.35), transparent)' }} />
        <style>{`
          /* Token "boot-up" flicker — 3 quick brightness pulses, ~0.6s (kept; recoloured to brand) */
          @keyframes tokenBoot {
            0%   { filter: brightness(0.4); opacity: 0.55; }
            12%  { filter: brightness(1.8); opacity: 1; }
            24%  { filter: brightness(0.65); opacity: 0.75; }
            42%  { filter: brightness(1.7); opacity: 1; }
            56%  { filter: brightness(0.8); opacity: 0.9; }
            74%  { filter: brightness(1.6); opacity: 1; }
            100% { filter: brightness(1); opacity: 1; }
          }
          .cockpit-token-boot { animation: tokenBoot 0.7s steps(24, end) 1 both; will-change: filter, opacity; }
          @media (prefers-reduced-motion: reduce) { .cockpit-token-boot { animation: none !important; } }
        `}</style>
        <div className="max-w-7xl mx-auto px-2 md:px-6 relative">
          {inCabinPatients.length > 0 ? (
            <>
            <Card
              className="p-2.5 md:p-3.5 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
                border: '1px solid rgba(30,58,138,0.18)',
                boxShadow: '0 4px 16px rgba(30,58,138,0.08)',
              }}
            >
              {/* Row 1: Patient identity + timers */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0 flex-1 flex-wrap">
                  <span
                    className="text-white text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.18em] px-2 py-1 rounded-md shadow-sm flex-shrink-0"
                    style={{
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                      boxShadow: '0 2px 8px rgba(30,58,138,0.25)',
                    }}
                  >
                    ● In Cabin
                  </span>
                  <span
                    key={`tok-${inCabinPatients[0].id}`}
                    className="cockpit-token-boot text-2xl md:text-4xl font-black leading-none tracking-tight flex-shrink-0 bg-clip-text"
                    style={{
                      fontFamily: 'Arial Black, sans-serif',
                      color: 'transparent',
                      backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                      WebkitBackgroundClip: 'text',
                      filter: 'drop-shadow(0 2px 6px rgba(30,58,138,0.25))',
                    }}
                  >
                    {inCabinPatients[0].token_number}
                  </span>
                  <span className="text-base md:text-xl font-semibold text-slate-900 truncate">
                    {inCabinPatients[0].name || 'Unknown'}
                  </span>

                  {/* Demographics + place chip */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(inCabinPatients[0].age || inCabinPatients[0].sex) && (
                      <span className="text-sm text-slate-600 font-medium">
                        {inCabinPatients[0].age}{inCabinPatients[0].sex ? ` · ${inCabinPatients[0].sex}` : ''}
                      </span>
                    )}
                    {inCabinPatients[0].place && (
                      <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-medium px-2 py-0.5 rounded-full" data-testid="doc-cabin-place">
                        <MapPin className="h-3 w-3 text-slate-500" />
                        {inCabinPatients[0].place}
                      </span>
                    )}
                    {inCabinPatients[0].contact_number && (
                      <span className="hidden md:inline text-xs text-slate-400 tabular-nums">
                        {inCabinPatients[0].contact_number}
                      </span>
                    )}
                  </div>

                  {/* Condition badges */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <SourceBadge patient={inCabinPatients[0]} className="text-[10px] px-1.5 py-0.5" />
                    {inCabinPatients[0].urgent && (
                      <span className="inline-flex items-center text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full shadow-sm">URG</span>
                    )}
                    {inCabinPatients[0].fever && (
                      <span className="inline-flex items-center text-[10px] font-bold text-white bg-orange-500 px-2 py-0.5 rounded-full shadow-sm">
                        <Thermometer className="h-3 w-3 mr-0.5" /> FEVER
                      </span>
                    )}
                    {inCabinPatients[0].review && (
                      <span className="inline-flex items-center text-[10px] font-bold text-white bg-indigo-500 px-2 py-0.5 rounded-full shadow-sm">REVIEW</span>
                    )}
                    {inCabinPatients[0].diabetes && (
                      <span className="inline-flex items-center text-[10px] font-bold text-white bg-purple-500 px-2 py-0.5 rounded-full shadow-sm">DM</span>
                    )}
                    {inCabinPatients[0].htn && (
                      <span className="inline-flex items-center text-[10px] font-bold text-white bg-rose-500 px-2 py-0.5 rounded-full shadow-sm">HTN</span>
                    )}
                    {/* Vitals chips — captured by staff at entry */}
                    {VITAL_FIELDS.some(f => inCabinPatients[0][f.key]) && (
                      <span className="inline-flex items-center gap-1 flex-wrap" data-testid="doc-cabin-vitals">
                        {VITAL_FIELDS.filter(f => inCabinPatients[0][f.key]).map(f => {
                          const bad = isVitalAbnormal(f.key, inCabinPatients[0][f.key], isPaedsPatient(inCabinPatients[0]));
                          return (
                            <span key={f.key} className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${vitalChipClass(bad)}`} data-testid={`doc-vital-${f.key}`} data-abnormal={bad ? 'true' : 'false'} title={bad ? 'Outside safe range' : undefined}>
                              {f.label} {inCabinPatients[0][f.key]}<span className={`font-medium ml-0.5 ${bad ? 'text-red-100' : 'text-teal-600'}`}>{f.unit}</span>
                            </span>
                          );
                        })}
                      </span>
                    )}
                    {!inCabinPatients[0].diabetes && inCabinPatients[0].name && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-purple-600 hover:bg-purple-50 text-[10px] font-bold rounded-full border border-purple-300"
                        onClick={() => openAddToDmModal(inCabinPatients[0])}
                        title="Add to Diabetic Registry"
                        data-testid="add-to-dm-cabin"
                      >
                        <Activity className="h-3 w-3 mr-0.5" />
                        +DM
                      </Button>
                    )}
                  </div>
                </div>

                {/* Right side: timers */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {inCabinPatients[0].created_at && inCabinPatients[0].entered_cabin_at && (() => {
                    const waitedMin = Math.floor((new Date(inCabinPatients[0].entered_cabin_at) - new Date(inCabinPatients[0].created_at)) / 60000);
                    let MoodIcon = Smile;
                    let moodClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                    let moodLabel = 'Smooth';
                    if (waitedMin > 60) {
                      MoodIcon = Frown;
                      moodClass = 'bg-red-50 text-red-700 border-blue-200';
                      moodLabel = 'Long wait';
                    } else if (waitedMin > 30) {
                      MoodIcon = Meh;
                      moodClass = 'bg-orange-50 text-orange-700 border-orange-200';
                      moodLabel = 'Moderate wait';
                    }
                    return (
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold border ${moodClass}`}
                        title={`This patient waited ${waitedMin} min — ${moodLabel}`}
                        data-testid="incabin-wait-mood"
                      >
                        <MoodIcon className="h-3 w-3" />
                        <span className="tabular-nums">Waited {waitedMin}m</span>
                      </span>
                    );
                  })()}
                  <span className={`inline-flex items-center gap-1 text-white px-2 py-1 rounded-full text-xs font-bold shadow-sm tabular-nums ${
                    consultationTimer > 300
                      ? 'bg-red-600 animate-pulse'
                      : consultationTimer > 180
                        ? 'bg-amber-500'
                        : 'bg-blue-800'
                  }`}>
                    <Timer className="h-3 w-3" />
                    {formatTimer(consultationTimer)}
                  </span>
                </div>
              </div>

              {/* Row 2: Fee selector + actions + DONE */}
              <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                {/* Consultation Fee Pills */}
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm" role="group">
                  <button
                    onClick={() => setConsultationType(inCabinPatients[0].id, 'regular')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      (inCabinPatients[0].consultation_type || 'regular') === 'regular'
                        ? 'bg-red-700 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="fee-regular-btn"
                  >
                    Regular
                  </button>
                  <button
                    onClick={() => setConsultationType(inCabinPatients[0].id, 'review')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      inCabinPatients[0].consultation_type === 'review'
                        ? 'bg-amber-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="fee-review-btn"
                  >
                    Review
                  </button>
                  <button
                    onClick={() => setConsultationType(inCabinPatients[0].id, 'exempted')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                      inCabinPatients[0].consultation_type === 'exempted'
                        ? 'bg-slate-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="fee-exempted-btn"
                  >
                    Exempt
                  </button>
                </div>

                {/* Action buttons - icon-led, refined */}
                <Button
                  onClick={() => { setSelectedPatientForDoc(inCabinPatients[0]); setShowCertificateModal(true); }}
                  variant="outline"
                  size="sm"
                  className="border-slate-200 text-slate-700 hover:bg-slate-50 h-9 px-2.5 text-xs font-medium rounded-lg"
                  data-testid="sticky-certificate-btn"
                >
                  <FileText className="h-3.5 w-3.5 mr-1" /> Cert
                </Button>
                <Button
                  onClick={() => { setSelectedPatientForDoc(inCabinPatients[0]); setShowPrescriptionModal(true); }}
                  variant="outline"
                  size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 h-9 px-2.5 text-xs font-medium rounded-lg"
                  data-testid="sticky-prescription-btn"
                >
                  <Pill className="h-3.5 w-3.5 mr-1" /> Rx
                </Button>
                <Button
                  onClick={() => handleBackToWaiting(inCabinPatients[0].id)}
                  variant="outline"
                  size="sm"
                  className="border-orange-200 text-orange-600 hover:bg-orange-50 h-9 px-2.5 text-xs font-medium rounded-lg"
                  data-testid="sticky-change-btn"
                  title="Send back to waiting"
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" /> Change
                </Button>

                {/* Review days - DM only */}
                {inCabinPatients[0].diabetes && (
                  <select
                    value={selectedReviewDays || ''}
                    onChange={(e) => setSelectedReviewDays(e.target.value ? parseInt(e.target.value) : null)}
                    className="h-9 rounded-lg border border-purple-200 px-2 text-xs font-semibold text-purple-800 bg-purple-50/60 focus:outline-none focus:ring-2 focus:ring-purple-300"
                    data-testid="review-days-select"
                    title="Set review date"
                  >
                    <option value="">Review</option>
                    <option value="7">7d</option>
                    <option value="10">10d</option>
                    <option value="15">15d</option>
                    <option value="30">1m</option>
                    <option value="60">2m</option>
                    <option value="90">3m</option>
                  </select>
                )}

                {/* Skip Rx toggle — for non-ER patients */}
                {(inCabinPatients[0].consultation_type || 'regular') !== 'er' && (
                  <button
                    onClick={() => setSkipRxForCabin(v => !v)}
                    className={`ml-auto inline-flex items-center gap-1.5 px-3 h-11 md:h-12 rounded-xl border-2 text-xs md:text-sm font-bold transition-colors ${
                      skipRxForCabin
                        ? 'bg-amber-500 text-white border-amber-600 hover:bg-amber-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                    }`}
                    title={skipRxForCabin ? 'Rx will be SKIPPED for this patient — tap to undo' : 'Skip Rx assignment for this patient'}
                    data-testid="skip-rx-toggle"
                  >
                    <FileText className="h-4 w-4" />
                    {skipRxForCabin ? 'Skip Rx ✓' : 'Skip Rx'}
                  </button>
                )}

                {/* CLOSE CONSULTATION — brand-themed action */}
                <Button
                  onClick={() => handleComplete(inCabinPatients[0].id, selectedReviewDays)}
                  className="ml-auto text-white font-black text-base md:text-xl px-7 md:px-10 h-12 md:h-14 rounded-lg flex items-center gap-2 uppercase tracking-[0.18em] border transition-all hover:translate-y-[2px]"
                  style={{
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #16a34a 100%)',
                    borderColor: 'rgba(22,163,74,0.40)',
                    boxShadow:
                      '0 4px 0 rgba(30,58,138,0.45), 0 6px 18px rgba(30,58,138,0.30), inset 0 1px 0 rgba(255,255,255,0.20)',
                  }}
                  data-testid="sticky-complete-btn"
                  title="Close consultation (mark patient as done)"
                >
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6" />
                  CLOSE
                </Button>
              </div>

              {/* Row 3: Queued Next pills (only if any) */}
              {patients.filter(p => p.status === 'queued_next').length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-1.5 flex-wrap">
                  {patients
                    .filter(p => p.status === 'queued_next')
                    .sort((a, b) => (a.queued_at || '').localeCompare(b.queued_at || ''))
                    .map((qp, idx) => (
                      <span key={qp.id} className="inline-flex items-center gap-1.5 bg-white border border-purple-200 rounded-full pl-2 pr-2.5 py-1 shadow-sm">
                        <Zap className="h-3.5 w-3.5 text-purple-500" />
                        <span className="font-semibold text-purple-600 text-[11px] uppercase tracking-wide">{idx === 0 ? 'Next' : 'Then'}</span>
                        <span className="font-bold text-purple-800 text-sm tabular-nums">{qp.token_number}</span>
                        <span className="text-purple-700 text-sm font-medium truncate max-w-[120px]">{qp.name || 'Nameless'}</span>
                        {qp.place && (
                          <span className="hidden md:inline-flex items-center text-slate-500 text-xs gap-0.5">
                            <MapPin className="h-3 w-3" />{qp.place}
                          </span>
                        )}
                      </span>
                    ))}
                </div>
              )}
            </Card>

            {/* Fever Waiting Alert - under in-cabin section */}
            {patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next')).length > 0 && (
              <div
                onClick={scrollToFeverPatients}
                className="mt-2 flex items-center gap-2 bg-orange-500 text-white rounded-lg px-4 py-2.5 shadow-lg animate-pulse cursor-pointer hover:bg-orange-600 transition-colors"
                data-testid="fever-waiting-alert"
                title="Click to find fever patients"
              >
                <Thermometer className="h-5 w-5 flex-shrink-0" />
                <span className="font-black text-sm">
                  {patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next')).length} FEVER patient{patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next')).length > 1 ? 's' : ''} waiting
                </span>
                <span className="text-xs opacity-80 ml-auto">Tap to locate</span>
              </div>
            )}
            </>
          ) : (
            <Card className="p-4 md:p-6 border-2 border-blue-100 shadow-lg rounded-xl bg-white">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 text-slate-400">
                  <Clock className="h-6 w-6 md:h-8 md:w-8" />
                  <div>
                    <p className="font-bold text-blue-800 text-sm md:text-base">No patient in cabin</p>
                    <p className="text-xs md:text-sm text-red-400">Call next patient from the queue</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => {
                      setSelectedPatientForDoc(null);
                      setShowCertificateModal(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="text-xs h-9 rounded-lg"
                    data-testid="no-token-certificate-btn"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden md:inline">Certificate</span>
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedPatientForDoc(null);
                      setShowPrescriptionModal(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="text-xs h-9 rounded-lg"
                    data-testid="no-token-prescription-btn"
                  >
                    <Pill className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden md:inline">Prescription</span>
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Indicators row — INSIDE sticky, BELOW in-cabin display */}
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid="doc-indicators-row">
            {/* Daily Rx Start pill */}
            <button
              onClick={openRxAnchorPrompt}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors ${
                rxAnchorInfo?.next_to_assign
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                  : 'bg-rose-50 border-rose-300 text-rose-700 border-dashed hover:bg-rose-100 animate-pulse'
              }`}
              title={rxAnchorInfo?.next_to_assign ? 'Today\'s Rx — click to edit' : "Set today's Rx start"}
              data-testid="rx-anchor-pill"
            >
              <FileText className="h-3 w-3" />
              {rxAnchorInfo?.next_to_assign ? `Next Rx ${rxAnchorInfo.next_to_assign}` : 'Set Rx Start'}
            </button>
            <span className="h-4 w-px bg-slate-200" />
            {/* Auto-Call icon-only toggle */}
            <Button
              onClick={() => setAutoCallEnabled(!autoCallEnabled)}
              variant="ghost"
              size="sm"
              className={`h-7 w-7 p-0 rounded-full shadow-sm transition-colors ${autoCallEnabled ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}
              title={`Auto-Call ${autoCallEnabled ? 'ON — click to disable' : 'OFF — click to enable'}`}
              data-testid="auto-call-toggle"
            >
              <Power className="h-3.5 w-3.5" />
            </Button>
            {autoCallEnabled && inCabinPatients.length === 0 && (
              <Button
                onClick={handleAutoCallNext}
                disabled={autoCallLoading}
                size="sm"
                className="h-7 w-7 p-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                title="Call Next Patient"
                data-testid="auto-call-next-button"
              >
                <Play className="h-3.5 w-3.5" />
              </Button>
            )}
            {autoCallEnabled && suggestedNext && inCabinPatients.length === 0 && (
              <span className="inline-flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 text-xs">
                <FastForward className="h-3 w-3 text-amber-600" />
                <span className="font-bold text-amber-700 tabular-nums">{suggestedNext.token_number}</span>
                <span className="text-amber-600 truncate max-w-[100px]">{suggestedNext.name || 'Nameless'}</span>
              </span>
            )}

            <span className="h-4 w-px bg-slate-200" />

            {/* Walk-in average wait mood pill — green/orange/red smiley */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${waitMoodColor}`}
              title={
                avgWalkinWaitMin === null
                  ? 'No walk-in patients currently waiting'
                  : `Average walk-in wait: ${avgWalkinWaitMin} min — ${waitMoodLabel}`
              }
              data-testid="walkin-wait-mood"
            >
              {(() => { const Icon = waitMoodIcon; return <Icon className="h-3.5 w-3.5" />; })()}
              <span className="tabular-nums">
                {avgWalkinWaitMin === null ? '—' : `${avgWalkinWaitMin}m`}
              </span>
            </span>

            {/* Refined symbol chips */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold" title="Total today">
              <Users className="h-3 w-3" />
              <span className="tabular-nums">{liveStats.total}</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-xs font-semibold" title="Completed">
              <CheckCircle className="h-3 w-3" />
              <span className="tabular-nums">{liveStats.completed}</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-semibold" title="Waiting">
              <Clock className="h-3 w-3" />
              <span className="tabular-nums">{liveStats.waiting}</span>
            </span>
            {liveStats.inER > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-blue-100 text-xs font-bold animate-pulse" title="In ER">
                <AlertCircle className="h-3 w-3" />
                <span className="tabular-nums">{liveStats.inER}</span>
                <span className="hidden sm:inline">ER</span>
              </span>
            )}

            <span className="h-4 w-px bg-slate-200" />

            {/* Age groups — Paeds (<12) + Adults (12+) */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-100 text-xs font-semibold" title="Paediatric (<12 years)">
              <Baby className="h-3 w-3" />
              <span className="tabular-nums">{liveStats.under5 + liveStats.age5to11}</span>
              <span className="hidden md:inline opacity-70 ml-0.5">Paeds</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100 text-xs font-semibold" title="Adults (12+ years)">
              <User className="h-3 w-3" />
              <span className="tabular-nums">{liveStats.adults}</span>
              <span className="hidden md:inline opacity-70 ml-0.5">Adults</span>
            </span>

            <span className="h-4 w-px bg-slate-200" />

            {/* Conditions */}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-100 text-xs font-semibold" title="Fever cases">
              <Thermometer className="h-3 w-3" />
              <span className="tabular-nums">{liveStats.fever}</span>
            </span>
            {liveStats.urgent > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 text-xs font-semibold" title="Urgent">
                <Zap className="h-3 w-3" />
                <span className="tabular-nums">{liveStats.urgent}</span>
              </span>
            )}
            {liveStats.review > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-semibold" title="Review">
                <ClipboardList className="h-3 w-3" />
                <span className="tabular-nums">{liveStats.review}</span>
              </span>
            )}
            {liveStats.dm > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100 text-xs font-semibold" title="Diabetes">
                <Droplets className="h-3 w-3" />
                <span className="tabular-nums">{liveStats.dm}</span>
              </span>
            )}
            {liveStats.htn > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 text-xs font-semibold" title="Hypertension">
                <Heart className="h-3 w-3" />
                <span className="tabular-nums">{liveStats.htn}</span>
              </span>
            )}

            {/* Avg waiting time pinned to right */}
            {avgWaitingTime && avgWaitingTime.count > 0 && (
              <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                avgWaitingTime.color === 'green' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                avgWaitingTime.color === 'yellow' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                'bg-red-50 text-red-700 border-blue-100'
              }`} data-testid="avg-waiting-time-live" title="Average waiting time">
                <Timer className="h-3 w-3" />
                Avg <span className="tabular-nums">{avgWaitingTime.formatted}</span>
              </span>
            )}
          </div>

          {/* Fever Waiting Alert (shown when no one in cabin too) */}
          {inCabinPatients.length === 0 && patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next')).length > 0 && (
            <div
              onClick={scrollToFeverPatients}
              className="mt-2 flex items-center gap-2 bg-orange-500 text-white rounded-lg px-4 py-2.5 shadow-lg animate-pulse cursor-pointer hover:bg-orange-600 transition-colors"
              data-testid="fever-waiting-alert-empty"
              title="Click to find fever patients"
            >
              <Thermometer className="h-5 w-5 flex-shrink-0" />
              <span className="font-black text-sm">
                {patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next')).length} FEVER patient{patients.filter(p => p.fever && (p.status === 'waiting' || p.status === 'queued_next')).length > 1 ? 's' : ''} waiting
              </span>
              <span className="text-xs opacity-80 ml-auto">Tap to locate</span>
            </div>
          )}
          
          {/* ER Patients - Urgent cases needing immediate attention */}
          {inERPatients.length > 0 && (
            <Card className="mt-2 p-3 border-2 border-red-500 bg-gradient-to-r from-red-50 to-orange-50 shadow-lg" data-testid="er-patients-section">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-red-500 text-white p-1.5 rounded-full animate-pulse">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <span className="text-sm font-bold text-red-700 uppercase tracking-wide">
                  ER - Immediate Attention ({inERPatients.length})
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {inERPatients.map((patient) => (
                  <div 
                    key={patient.id} 
                    className="bg-white border border-blue-200 rounded-lg px-3 py-2 shadow-sm min-w-[280px]"
                  >
                    <div className="flex items-center gap-3">
                      {/* ER Bed Badge */}
                      {patient.er_bed && (
                        <div className={`flex items-center justify-center w-12 h-12 rounded-lg font-black text-xl text-white shadow-lg ${
                          patient.er_bed === 'OT' ? 'bg-purple-600' :
                          patient.er_bed === 'DB' ? 'bg-pink-600' :
                          patient.er_bed?.startsWith('C') ? 'bg-orange-500' :
                          'bg-blue-600'
                        }`} title={`ER Bed: ${patient.er_bed}`}>
                          {patient.er_bed}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-red-700" style={{ fontFamily: 'Arial, sans-serif' }}>
                            {patient.token_number}
                          </span>
                          <span className="font-medium text-red-600 text-sm">{patient.name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-red-500">
                          <span>{patient.age} • {patient.sex}</span>
                          {patient.fever && (
                            <span className="flex items-center font-bold text-orange-600 bg-orange-100 px-1 py-0.5 rounded ml-1">
                              <Thermometer className="h-2.5 w-2.5 mr-0.5" />
                              FEVER
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Fee display & edit + action buttons */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-blue-100 gap-2">
                      {/* Fee display/edit */}
                      <div className="flex items-center gap-1">
                        {editingErFeePatientId === patient.id ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-red-600 font-bold">₹</span>
                            <input
                              type="number"
                              value={editingErFeeValue}
                              onChange={(e) => setEditingErFeeValue(e.target.value)}
                              className="w-16 h-7 text-sm border border-blue-200 rounded px-1 text-center font-bold"
                              autoFocus
                              data-testid={`er-fee-input-${patient.id}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editingErFeeValue) updatePatientFee(patient.id, editingErFeeValue);
                                if (e.key === 'Escape') { setEditingErFeePatientId(null); setEditingErFeeValue(''); }
                              }}
                            />
                            <Button size="sm" className="h-7 px-2 bg-green-500 hover:bg-green-600 text-white text-xs"
                              onClick={() => editingErFeeValue && updatePatientFee(patient.id, editingErFeeValue)}
                              data-testid={`er-fee-save-${patient.id}`}>
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-1 text-red-500"
                              onClick={() => { setEditingErFeePatientId(null); setEditingErFeeValue(''); }}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingErFeePatientId(patient.id); setEditingErFeeValue(String(patient.consultation_fee || feeSettings.er_fee)); }}
                            className="flex items-center gap-1 text-sm font-bold text-red-700 bg-red-100 px-2 py-1 rounded border border-blue-100 hover:bg-red-200 transition-colors"
                            title="Click to change fee"
                            data-testid={`er-fee-display-${patient.id}`}
                          >
                            <IndianRupee className="h-3.5 w-3.5" />
                            {patient.consultation_fee || feeSettings.er_fee}
                          </button>
                        )}
                      </div>
                      {/* Action buttons */}
                      <div className="flex items-center gap-1">
                        <Button
                          onClick={() => handleBackToWaiting(patient.id)}
                          size="sm"
                          variant="outline"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50 text-xs h-8 px-2"
                          title="Send back to waiting"
                          data-testid={`er-back-to-waiting-${patient.id}`}
                        >
                          <Undo2 className="h-3.5 w-3.5 mr-1" />
                          Waiting
                        </Button>
                        <Button
                          onClick={() => handleComplete(patient.id)}
                          size="sm"
                          className="bg-red-600 hover:bg-blue-700 text-white text-xs h-8 px-3"
                          data-testid={`complete-er-${patient.id}`}
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          Close ER
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          
          {/* Tabs */}
          {!powerMode && (
          <div className="mt-2 flex gap-1 border-b border-border/60">
            <button
              onClick={() => setActiveTab('search')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'search'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-search"
            >
              Search
            </button>
            <button
              onClick={() => setActiveTab('queue')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'queue'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-queue"
            >
              Queue
            </button>
            <button
              onClick={() => setActiveTab('board')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'board'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-board"
            >
              Board
            </button>
            <button
              onClick={() => setActiveTab('summaries')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'summaries'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-summaries"
            >
              Summaries
            </button>
          </div>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto p-4 md:p-6 pt-2 md:pt-4">
        {/* Search Tab — ultra-compact: type token/name, press Enter to send to cabin */}
        {!powerMode && activeTab === 'search' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-testid="doc-search-tab">
            {(() => {
              const normalize = (s) => (s || '').toString().toLowerCase().trim();
              // Only consider live patients (waiting, in-cabin, queued-next). Completed/ER excluded from quick-call.
              const activeStatuses = new Set(['waiting', 'queued_next']);
              // Adult pool = A- (new) + legacy G-/W-/O- tokens (back-compat for the same day)
              const adultPool = patients.filter(p => {
                const t = (p.token_number || '').toUpperCase();
                return (t.startsWith('A-') || t.startsWith('G-') || t.startsWith('W-') || t.startsWith('O-')) && activeStatuses.has(p.status);
              });
              const pPatients = patients.filter(p => (p.token_number || '').toUpperCase().startsWith('P-') && activeStatuses.has(p.status));

              const bestMatch = (pool, q) => {
                const n = normalize(q);
                if (!n) return null;
                // Prefer exact token match, then name startswith, then includes
                const token = pool.find(p => (p.token_number || '').toLowerCase() === n);
                if (token) return token;
                const tokStart = pool.find(p => (p.token_number || '').toLowerCase().startsWith(n));
                if (tokStart) return tokStart;
                const nameStart = pool.find(p => (p.name || '').toLowerCase().startsWith(n));
                if (nameStart) return nameStart;
                return pool.find(p => (p.token_number || '').toLowerCase().includes(n) || (p.name || '').toLowerCase().includes(n)) || null;
              };

              const gHit = bestMatch(adultPool, gSearch);
              const pHit = bestMatch(pPatients, pSearch);

              const sendAndClear = (hit, clear) => {
                if (!hit) return;
                if (hit.status === 'in_cabin') { toast.info('Already in cabin'); return; }
                handleCallPatient(hit.id);
                clear();
              };

              const renderPanel = (opts) => (
                <Card className={`p-4 border-border/60 shadow-sm ${opts.ring}`} data-testid={opts.testid}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${opts.badgeBg} text-white text-xs font-bold px-2 py-0.5 rounded`}>{opts.prefix}</span>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{opts.label}</h3>
                    <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
                      {opts.active} active
                    </span>
                  </div>
                  <div className="relative">
                    <Input
                      placeholder={opts.placeholder}
                      value={opts.value}
                      onChange={(e) => opts.setValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); sendAndClear(opts.hit, () => opts.setValue('')); }
                        if (e.key === 'Escape') opts.setValue('');
                      }}
                      className={`h-11 pl-3 pr-24 text-base font-medium ${opts.hit ? 'border-emerald-400 ring-2 ring-emerald-100' : ''}`}
                      data-testid={opts.inputTestid}
                      autoComplete="off"
                    />
                    {/* Inline preview chip + Send button, overlaid inside input's right padding */}
                    <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      {opts.value && !opts.hit && (
                        <span className="text-[10px] text-slate-400">no match</span>
                      )}
                      {opts.hit && (
                        <Button
                          size="sm"
                          onClick={() => sendAndClear(opts.hit, () => opts.setValue(''))}
                          className={`h-8 px-2.5 ${opts.btnBg} text-white text-xs font-semibold shadow-sm`}
                          title={`Send ${opts.hit.token_number} to cabin`}
                          data-testid={opts.sendTestid}
                        >
                          <UserCheck className="h-3.5 w-3.5 mr-1" />
                          Call
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Minimal match hint line — single row, no list */}
                  <div className="mt-1.5 text-xs min-h-[18px] flex items-center gap-1.5 flex-wrap">
                    {opts.hit ? (
                      <>
                        <span className={`font-bold tabular-nums ${opts.hitColor}`}>{opts.hit.token_number}</span>
                        <span className="truncate max-w-[140px] font-medium">{opts.hit.name || 'Nameless'}</span>
                        <SourceBadge patient={opts.hit} />
                        <span className="text-muted-foreground">· {formatAge(opts.hit.age)}{opts.hit.sex ? ` · ${opts.hit.sex}` : ''}</span>
                        {opts.hit.place && <span className="inline-flex items-center gap-0.5 text-slate-500"><MapPin className="h-3 w-3" />{opts.hit.place}</span>}
                        {opts.hit.urgent && <span className="text-red-600 font-bold">URG</span>}
                        {opts.hit.fever && <Thermometer className="h-3 w-3 text-orange-600" />}
                        <span className="ml-auto text-[10px] uppercase font-bold text-emerald-600">↵ Enter to call</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground italic">Type token or name, press Enter to send to cabin</span>
                    )}
                  </div>
                </Card>
              );

              return (
                <>
                  {renderPanel({
                    prefix: 'A', label: 'Adult (12+)', active: adultPool.length,
                    placeholder: 'A-005 or Priya…',
                    badgeBg: 'bg-red-700', btnBg: 'bg-red-700 hover:bg-blue-800',
                    hitColor: 'text-blue-800', ring: '',
                    value: gSearch, setValue: setGSearch, hit: gHit,
                    testid: 'a-search-panel', inputTestid: 'a-search-input', sendTestid: 'a-send-btn'
                  })}
                  {renderPanel({
                    prefix: 'P', label: 'Paediatric (<12)', active: pPatients.length,
                    placeholder: 'P-002 or Arun…',
                    badgeBg: 'bg-pink-600', btnBg: 'bg-pink-600 hover:bg-pink-700',
                    hitColor: 'text-pink-700', ring: '',
                    value: pSearch, setValue: setPSearch, hit: pHit,
                    testid: 'p-search-panel', inputTestid: 'p-search-input', sendTestid: 'p-send-btn'
                  })}
                </>
              );
            })()}
          </div>
        )}

        {/* Queue Tab */}
        {!powerMode && activeTab === 'queue' && (
          <>
            {/* Queue Lists - Paeds (<12) + Adults (12+) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Paediatric Patients (<12 years) */}
          <Card className="p-6 border-border/60 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Baby className="h-5 w-5 mr-2 text-pink-600" />
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Paediatrics (&lt;12y) - Waiting ({paedsWaiting.length})
                </h2>
              </div>
              <Input
                type="text"
                placeholder="Search..."
                data-testid="search-input-paeds"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-[120px] h-8 text-sm"
              />
            </div>

            {filteredPaedsWaiting.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <p>{searchQuery ? 'No patients found' : 'No paediatric patients waiting'}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredPaedsWaiting.map((patient) => {
                  const waitInfo = getWaitingTimeInfo(patient);
                  return (
                  <div
                    key={patient.id}
                    data-testid={`waiting-patient-${patient.id}`}
                    {...(patient.fever ? {'data-fever-patient': 'true'} : {})}
                    className={`flex items-center justify-between p-2 bg-pink-50 border-2 rounded-lg transition-all duration-500 ${
                      waitInfo ? `${waitInfo.borderColor}` : 'border-pink-200'
                    } ${patient.review ? 'border-purple-400 bg-purple-50' : ''} ${highlightFever && patient.fever ? 'ring-4 ring-orange-400 bg-orange-50 scale-[1.02]' : ''}`}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="text-lg font-bold tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {patient.token_number}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          {getAgeSymbol(patient.age) && (
                            <span className={getAgeSymbol(patient.age).color} title={getAgeSymbol(patient.age).label}>
                              {getAgeSymbol(patient.age).symbol}
                            </span>
                          )}
                          <span className="font-medium text-sm truncate">{patient.name || <span className="italic text-muted-foreground">Nameless</span>}</span>
                          <SourceBadge patient={patient} />
                          {patient.urgent && <span className="text-red-600 text-xs font-bold">URGENT</span>}
                          {patient.fever && <Thermometer className="h-3 w-3 text-orange-600" />}
                          {patient.review && <ClipboardList className="h-3 w-3 text-purple-600" />}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{formatAge(patient.age)} • {patient.sex}</span>
                          {waitInfo && (
                            <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold text-xs ${waitInfo.bgColor} ${waitInfo.color}`}>
                              <Clock className="h-3 w-3" />
                              {waitInfo.display}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleCallPatient(patient.id)} className="bg-pink-600 hover:bg-pink-700 text-white text-sm md:text-base px-4 py-2.5 md:px-6 md:py-3 h-auto font-bold">
                      Send to Cabin
                    </Button>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Paeds Completed */}
            {filteredPaedsCompleted.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Completed ({paedsCompleted.length})</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {filteredPaedsCompleted.map((patient) => (
                    <div key={patient.id} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                      <span className="font-mono font-bold">{patient.token_number}</span>
                      <span className="truncate mx-2 flex-1">{patient.name || 'Nameless'}</span>
                      {patient.prescription_number && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded-full mr-1" data-testid={`completed-rx-${patient.id}`}>
                          <FileText className="h-2.5 w-2.5" /> {patient.prescription_number}
                        </span>
                      )}
                      {getConsultationDuration(patient) && (
                        <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mr-1">
                          <Timer className="h-3 w-3" />
                          {getConsultationDuration(patient)}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">({formatAge(patient.age)})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Adult Patients (>=12 years) */}
          <Card className="p-6 border-border/60 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <User className="h-5 w-5 mr-2 text-purple-600" />
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Adults (≥12y) - Waiting ({adultWaiting.length})
                </h2>
              </div>
              <Input
                type="text"
                placeholder="Search..."
                data-testid="search-input-adult"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-[120px] h-8 text-sm"
              />
            </div>

            {filteredAdultWaiting.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <p>{searchQuery ? 'No patients found' : 'No adult patients waiting'}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredAdultWaiting.map((patient) => {
                  const waitInfo = getWaitingTimeInfo(patient);
                  return (
                  <div
                    key={patient.id}
                    data-testid={`waiting-patient-${patient.id}`}
                    {...(patient.fever ? {'data-fever-patient': 'true'} : {})}
                    className={`flex items-center justify-between p-2 bg-purple-50 border-2 rounded-lg transition-all duration-500 ${
                      waitInfo ? `${waitInfo.borderColor}` : 'border-purple-200'
                    } ${patient.review ? 'border-purple-400 bg-purple-100' : ''} ${highlightFever && patient.fever ? 'ring-4 ring-orange-400 bg-orange-50 scale-[1.02]' : ''}`}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="text-lg font-bold tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {patient.token_number}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium text-sm truncate">{patient.name || <span className="italic text-muted-foreground">Nameless</span>}</span>
                          <SourceBadge patient={patient} />
                          {patient.urgent && <span className="text-red-600 text-xs font-bold">URGENT</span>}
                          {patient.fever && <Thermometer className="h-3 w-3 text-orange-600" />}
                          {patient.review && <ClipboardList className="h-3 w-3 text-purple-600" />}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{formatAge(patient.age)} • {patient.sex}</span>
                          {waitInfo && (
                            <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold text-xs ${waitInfo.bgColor} ${waitInfo.color}`}>
                              <Clock className="h-3 w-3" />
                              {waitInfo.display}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleCallPatient(patient.id)} className="bg-purple-600 hover:bg-purple-700 text-white text-sm md:text-base px-4 py-2.5 md:px-6 md:py-3 h-auto font-bold">
                      Send to Cabin
                    </Button>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Adult Completed */}
            {filteredAdultCompleted.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-sm font-semibold mb-2 text-muted-foreground">Completed ({adultCompleted.length})</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {filteredAdultCompleted.map((patient) => (
                    <div key={patient.id} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                      <span className="font-mono font-bold">{patient.token_number}</span>
                      <span className="truncate mx-2 flex-1">{patient.name || 'Nameless'}</span>
                      {patient.prescription_number && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded-full mr-1" data-testid={`completed-rx-${patient.id}`}>
                          <FileText className="h-2.5 w-2.5" /> {patient.prescription_number}
                        </span>
                      )}
                      {getConsultationDuration(patient) && (
                        <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded mr-1">
                          <Timer className="h-3 w-3" />
                          {getConsultationDuration(patient)}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">({formatAge(patient.age)})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
          </>
        )}

        {/* Board Tab — Paeds (2×8) + Adult (8×8) status grids */}
        {activeTab === 'board' && (
          <div className="space-y-4" data-testid="doc-board-tab">
            <Card className="p-4 md:p-6 border-border/60 shadow-sm">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Token Board · Live status at a glance
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Paeds &amp; Adult queue · grids grow automatically when tokens exceed default rows
                  </p>
                </div>
                <div className="flex items-center gap-3 text-[11px] font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-blue-600" />Pending
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />Done
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-slate-300 border border-slate-400" />Skipped
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-slate-100 border border-slate-300" />Not issued
                  </span>
                </div>
              </div>

              {(() => {
                // Reusable: build byNum map for a given prefix regex
                const buildMap = (prefixRe, maxN) => {
                  const m = new Map();
                  patients.forEach(p => {
                    const t = (p.token_number || '').toUpperCase();
                    const mm = t.match(prefixRe);
                    if (!mm) return;
                    const n = parseInt(mm[1], 10);
                    if (n >= 1 && n <= maxN) m.set(n, p);
                  });
                  return m;
                };
                const cellFor = (n, p, label, testidPrefix) => {
                  let cls = 'bg-slate-100 text-slate-400 border-slate-200';
                  let status = 'not-issued';
                  let title = `${label} — not issued`;
                  if (p) {
                    const s = (p.status || '').toLowerCase();
                    if (s === 'completed' || s === 'done_er') {
                      cls = 'bg-emerald-500 text-white border-emerald-600 shadow-sm hover:bg-emerald-600';
                      status = 'done';
                      title = `${label} · ${p.name || 'Nameless'} — Done`;
                    } else if (s === 'no_show' || s === 'cancelled' || s === 'expired_no_show') {
                      cls = 'bg-slate-300 text-slate-600 border-slate-400 line-through';
                      status = 'skipped';
                      title = `${label} · ${p.name || 'Nameless'} — Skipped/Cancelled`;
                    } else {
                      cls = 'bg-blue-600 text-white border-blue-700 shadow-sm hover:bg-blue-700';
                      status = 'pending';
                      title = `${label} · ${p.name || 'Nameless'} — ${s === 'in_cabin' ? 'In Cabin' : 'Waiting'}`;
                    }
                  }
                  return (
                    <div
                      key={`${testidPrefix}-${n}`}
                      title={title}
                      data-testid={`${testidPrefix}-${n}`}
                      data-status={status}
                      className={`aspect-square rounded-md border flex items-center justify-center text-[11px] md:text-xs font-bold tabular-nums select-none transition-colors ${cls}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {n}
                    </div>
                  );
                };

                const paedsMap = buildMap(/^P-(\d+)$/, 9999);
                const adultMap = buildMap(/^[AGWO]-(\d+)$/, 9999);
                // Dynamic row count: defaults are Paeds 2 rows (16 cells), Adult 8 rows (64 cells).
                // If a higher-numbered token has been issued, grow by full rows of 8 to accommodate.
                const paedsMaxIssued = paedsMap.size ? Math.max(...paedsMap.keys()) : 0;
                const adultMaxIssued = adultMap.size ? Math.max(...adultMap.keys()) : 0;
                const paedsRows = Math.max(2, Math.ceil(paedsMaxIssued / 8));
                const adultRows = Math.max(8, Math.ceil(adultMaxIssued / 8));
                const paedsTotal = paedsRows * 8;
                const adultTotal = adultRows * 8;
                const paedsCells = [];
                for (let n = 1; n <= paedsTotal; n++) {
                  paedsCells.push(cellFor(n, paedsMap.get(n), `P-${n.toString().padStart(3, '0')}`, 'board-paeds-cell'));
                }
                const adultCells = [];
                for (let n = 1; n <= adultTotal; n++) {
                  adultCells.push(cellFor(n, adultMap.get(n), `A-${n.toString().padStart(3, '0')}`, 'board-cell'));
                }
                return (
                  <div className="space-y-5">
                    {/* Paeds — 8 cols × N rows (horizontal layout) */}
                    <div>
                      <div className="text-xs font-bold text-pink-700 mb-2 inline-flex items-center gap-1">
                        <Baby className="h-3.5 w-3.5" />Paeds · P-001 → P-{paedsTotal.toString().padStart(3, '0')}
                      </div>
                      <div className="grid grid-cols-8 gap-1.5 md:gap-2 max-w-2xl" data-testid="board-paeds-grid">
                        {paedsCells}
                      </div>
                    </div>

                    {/* Adult — 8 cols × N rows */}
                    <div>
                      <div className="text-xs font-bold text-violet-700 mb-2 inline-flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />Adult · A-001 → A-{adultTotal.toString().padStart(3, '0')}
                      </div>
                      <div className="grid grid-cols-8 gap-1.5 md:gap-2 max-w-2xl" data-testid="board-grid">
                        {adultCells}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Compact tally below the grid */}
              {(() => {
                const adults = patients.filter(p => /^[AGWO]-/i.test(p.token_number || ''));
                const paeds = patients.filter(p => /^P-/i.test(p.token_number || ''));
                const doneOf = arr => arr.filter(p => ['completed', 'done_er'].includes((p.status || '').toLowerCase())).length;
                const pendingOf = arr => arr.filter(p => ['waiting', 'queued_next', 'in_cabin'].includes((p.status || '').toLowerCase())).length;
                return (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" data-testid="board-tally">
                    <div className="flex items-center justify-between p-2 bg-pink-50/60 border border-pink-100 rounded">
                      <span className="text-pink-700 font-semibold inline-flex items-center gap-1"><Baby className="h-3.5 w-3.5" />Paeds: <b className="tabular-nums">{paeds.length}</b></span>
                      <div className="flex gap-3">
                        <span className="inline-flex items-center gap-1 text-blue-700"><span className="h-2 w-2 rounded-full bg-blue-600" /><b className="tabular-nums">{pendingOf(paeds)}</b></span>
                        <span className="inline-flex items-center gap-1 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" /><b className="tabular-nums">{doneOf(paeds)}</b></span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-violet-50/60 border border-violet-100 rounded">
                      <span className="text-violet-700 font-semibold inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />Adult: <b className="tabular-nums">{adults.length}</b></span>
                      <div className="flex gap-3">
                        <span className="inline-flex items-center gap-1 text-blue-700"><span className="h-2 w-2 rounded-full bg-blue-600" /><b className="tabular-nums">{pendingOf(adults)}</b></span>
                        <span className="inline-flex items-center gap-1 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" /><b className="tabular-nums">{doneOf(adults)}</b></span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </Card>
          </div>
        )}

        {/* Summaries Tab */}
        {!powerMode && activeTab === 'summaries' && (
          <div className="space-y-6">
            {/* Today's Summary */}
            <Card className="p-6 border-border/60 shadow-sm bg-gradient-to-br from-white to-slate-50">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Today's Summary
              </h2>
              <div className="grid grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary" data-testid="summary-total">{daySummary.total}</div>
                  <div className="text-sm text-muted-foreground">Total Patients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600" data-testid="summary-completed">{daySummary.completed}</div>
                  <div className="text-sm text-muted-foreground">Patients Seen</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600" data-testid="summary-missed">{daySummary.missed}</div>
                  <div className="text-sm text-muted-foreground">Patients Missed</div>
                </div>
              </div>
              
              {/* Detailed Breakdown */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">Detailed Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex items-center gap-2 p-2 bg-pink-50 rounded border border-pink-200">
                    <Baby className="h-4 w-4 text-pink-600" />
                    <div>
                      <div className="font-bold text-pink-700">{liveStats.paeds}</div>
                      <div className="text-xs text-muted-foreground">Pediatric</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-purple-50 rounded border border-purple-200">
                    <User className="h-4 w-4 text-purple-600" />
                    <div>
                      <div className="font-bold text-purple-700">{liveStats.adults}</div>
                      <div className="text-xs text-muted-foreground">Adults</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-orange-50 rounded border border-orange-200">
                    <Thermometer className="h-4 w-4 text-orange-600" />
                    <div>
                      <div className="font-bold text-orange-700">{liveStats.fever}</div>
                      <div className="text-xs text-muted-foreground">Fever</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-blue-100">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <div>
                      <div className="font-bold text-red-700">{liveStats.urgent}</div>
                      <div className="text-xs text-muted-foreground">Urgent</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-violet-50 rounded border border-violet-200">
                    <ClipboardList className="h-4 w-4 text-violet-600" />
                    <div>
                      <div className="font-bold text-violet-700">{liveStats.review}</div>
                      <div className="text-xs text-muted-foreground">Review</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded border border-indigo-200">
                    <Droplets className="h-4 w-4 text-indigo-600" />
                    <div>
                      <div className="font-bold text-indigo-700">{liveStats.dm}</div>
                      <div className="text-xs text-muted-foreground">Diabetes</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-rose-50 rounded border border-rose-200">
                    <Heart className="h-4 w-4 text-rose-600" />
                    <div>
                      <div className="font-bold text-rose-700">{liveStats.htn}</div>
                      <div className="text-xs text-muted-foreground">HTN</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Previous Day Summary */}
            <Card className="p-6 border-border/60 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Previous Day Summary
                </h2>
                <Button
                  onClick={() => {
                    if (!previousDaySummary) {
                      fetchPreviousDaySummary();
                    }
                    setShowPreviousDaySummary(true);
                  }}
                  variant="outline"
                  size="sm"
                >
                  Load
                </Button>
              </div>
              {previousDaySummary && previousDaySummary.total > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded">
                    <div className="text-2xl font-bold text-slate-600">{previousDaySummary.total}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">{previousDaySummary.completed}</div>
                    <div className="text-xs text-muted-foreground">Seen</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded">
                    <div className="text-2xl font-bold text-red-600">{previousDaySummary.missed}</div>
                    <div className="text-xs text-muted-foreground">Missed</div>
                  </div>
                </div>
              ) : previousDaySummary ? (
                <p className="text-center text-sm text-muted-foreground py-4">No patients on previous day</p>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-4">Click "Load" to fetch previous day summary</p>
              )}
            </Card>

            {/* Check Any Day Summary */}
            <Card className="p-6 border-border/60 shadow-sm">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Check Any Day
              </h2>
              <div className="flex gap-2 mb-4">
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="date-picker"
                  className="flex-1"
                />
                <Button
                  onClick={() => fetchCustomDateSummary(selectedDate)}
                  disabled={!selectedDate}
                  data-testid="fetch-summary-button"
                >
                  Get Summary
                </Button>
              </div>
              
              {customDateSummary && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded">
                    <div className="text-2xl font-bold text-slate-600">{customDateSummary.total}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">{customDateSummary.completed}</div>
                    <div className="text-xs text-muted-foreground">Seen</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded">
                    <div className="text-2xl font-bold text-red-600">{customDateSummary.missed}</div>
                    <div className="text-xs text-muted-foreground">Missed</div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>

      {/* Doctor Arrival Settings Modal */}
      {showArrivalSettings && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowArrivalSettings(false)}
          data-testid="arrival-settings-modal"
        >
          <Card className="p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Clinic Arrival Time</h3>
                <p className="text-xs text-slate-500 mt-0.5">Doctor&apos;s scheduled clinic start time. Arrivals are compared against this.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowArrivalSettings(false)} data-testid="arrival-settings-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-xs font-medium text-slate-700">Scheduled clinic time (24-hour HH:MM)</div>
                <Input
                  type="time"
                  value={arrivalTimeInput}
                  onChange={(e) => setArrivalTimeInput(e.target.value)}
                  className="text-center font-mono text-lg h-12 mt-1"
                  data-testid="arrival-time-input"
                />
              </div>
              <div className="flex gap-2 flex-wrap text-xs">
                {['08:00', '09:00', '09:30', '10:00', '17:00', '18:00'].map(t => (
                  <button
                    key={t}
                    onClick={() => setArrivalTimeInput(t)}
                    className={`px-3 py-1.5 rounded-full font-semibold border transition-colors ${arrivalTimeInput === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`arrival-preset-${t.replace(':', '-')}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                <b>Status thresholds</b>: Early (&gt;10 min before) · On Time (≤target) · Slightly Late (≤15 min after) · Late (&gt;15 min). 2+ consecutive late days triggers a flashing red streak badge; 3+ late days within the week shows an orange tally.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowArrivalSettings(false)} data-testid="arrival-settings-cancel">Cancel</Button>
                <Button size="sm" onClick={saveArrivalSettings} className="bg-blue-600 hover:bg-blue-700" data-testid="arrival-settings-save">Save</Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Punch Log Modal — daily arrivals + Weekly vs Monthly */}
      {showPunchLog && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4"
          onClick={() => setShowPunchLog(false)}
          data-testid="punch-log-modal"
        >
          <Card className="p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4 flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <History className="h-5 w-5 text-blue-600" />
                  Doctor Punch-Time Log
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Daily arrival times compared against scheduled clinic time
                  <span className="ml-1 font-mono font-bold text-slate-700">
                    {punchLog?.target_time || opdSettings?.target_arrival_time || '18:00'}
                  </span>
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPunchLog(false)} data-testid="punch-log-close">
                <X className="h-4 w-4" />
              </Button>
            </div>

            {punchLogLoading ? (
              <div className="py-12 text-center text-slate-500">Loading punch log...</div>
            ) : !punchLog ? (
              <div className="py-12 text-center text-slate-400">No data available</div>
            ) : (
              <div className="overflow-y-auto flex-1 space-y-4">
                {/* Coaching message at top of modal */}
                {punchLog.coaching?.should_nudge && (
                  <div
                    className={`rounded-lg border-2 p-3 flex items-start gap-2 ${
                      punchLog.coaching.tone === 'danger'
                        ? 'bg-red-50 border-blue-200 text-blue-900'
                        : 'bg-amber-50 border-amber-300 text-amber-900'
                    }`}
                    data-testid="punch-log-coaching"
                  >
                    <span className="text-2xl leading-none">{punchLog.coaching.tone === 'danger' ? '🚨' : '⏰'}</span>
                    <div className="flex-1">
                      <p className="font-bold text-sm">{punchLog.coaching.headline}</p>
                      <p className="text-xs mt-0.5">{punchLog.coaching.message}</p>
                    </div>
                  </div>
                )}

                {/* Punctuality chart — last 14 days green/red zones */}
                {punchLog.daily.length > 0 && (() => {
                  const last14 = [...punchLog.daily].slice(0, 14).reverse(); // oldest → newest
                  const target = punchLog.target_time;
                  const [th, tm] = target.split(':').map(Number);
                  const targetMin = th * 60 + tm;
                  const W = 560, H = 110, padL = 36, padR = 12, padT = 8, padB = 26;
                  const plotW = W - padL - padR;
                  const plotH = H - padT - padB;
                  // Y axis range: target ± 90 minutes
                  const yMin = -90, yMax = 90;
                  const yPx = (v) => padT + ((yMax - Math.max(yMin, Math.min(yMax, v))) / (yMax - yMin)) * plotH;
                  const zeroY = yPx(0);
                  const barW = Math.max(8, plotW / last14.length - 6);
                  return (
                    <div className="rounded-xl border-2 border-slate-200 bg-white p-3" data-testid="punctuality-chart">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Punctuality — Last 14 Days</div>
                        <div className="flex items-center gap-3 text-[10px] text-slate-500">
                          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />on-time zone</span>
                          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-500" />late zone</span>
                        </div>
                      </div>
                      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
                        {/* Green (on-time) zone */}
                        <rect x={padL} y={padT} width={plotW} height={zeroY - padT} fill="#10b98122" />
                        {/* Red (late) zone */}
                        <rect x={padL} y={zeroY} width={plotW} height={H - padB - zeroY} fill="#ef444422" />
                        {/* Target line */}
                        <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#64748b" strokeWidth="1.5" strokeDasharray="4 3" />
                        <text x={padL - 4} y={zeroY + 3} textAnchor="end" fontSize="10" fill="#475569" fontWeight="700">{target}</text>
                        <text x={padL - 4} y={padT + 10} textAnchor="end" fontSize="9" fill="#10b981">early</text>
                        <text x={padL - 4} y={H - padB - 2} textAnchor="end" fontSize="9" fill="#ef4444">late</text>
                        {/* Bars */}
                        {last14.map((d, i) => {
                          const cx = padL + (i + 0.5) * (plotW / last14.length);
                          const x = cx - barW / 2;
                          const top = yPx(d.delta_min);
                          const isLate = d.delta_min > 0;
                          const fill = isLate ? '#ef4444' : '#10b981';
                          const y = isLate ? zeroY : top;
                          const h = Math.max(2, Math.abs(top - zeroY));
                          const dayLabel = (() => {
                            try { return new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
                            catch { return d.date.slice(5); }
                          })();
                          return (
                            <g key={d.date}>
                              <rect x={x} y={y} width={barW} height={h} fill={fill} rx="2" data-testid={`punctuality-bar-${d.date}`} />
                              <text x={cx} y={H - padB + 11} textAnchor="middle" fontSize="9" fill="#475569">{dayLabel}</text>
                              <text
                                x={cx}
                                y={isLate ? (zeroY + h + 12) : Math.max(padT + 8, top - 3)}
                                textAnchor="middle" fontSize="9" fontWeight="700"
                                fill={isLate ? '#7f1d1d' : '#065f46'}
                              >
                                {d.delta_min > 0 ? `+${d.delta_min}` : d.delta_min}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                      {(() => {
                        const aggregate = punchLog.daily.slice(0, 14);
                        const late = aggregate.filter(x => x.delta_min > 0).length;
                        const ontime = aggregate.length - late;
                        return (
                          <div className="text-[11px] text-slate-500 mt-1 flex justify-between">
                            <span>{ontime} day{ontime === 1 ? '' : 's'} in green · {late} day{late === 1 ? '' : 's'} in red (last 14)</span>
                            <span>Target line dashed</span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
                {/* Weekly vs Monthly cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="punch-log-summary">
                  {[
                    { key: 'weekly', label: 'This Week (last 7 days)', accent: 'from-blue-50 to-indigo-50 border-blue-200', text: 'text-blue-900' },
                    { key: 'monthly', label: 'This Month (last 30 days)', accent: 'from-purple-50 to-pink-50 border-purple-200', text: 'text-purple-900' },
                  ].map(({ key, label, accent, text }) => {
                    const s = punchLog[key];
                    return (
                      <div
                        key={key}
                        className={`rounded-xl border-2 bg-gradient-to-br ${accent} p-4`}
                        data-testid={`punch-log-${key}`}
                      >
                        <div className={`text-xs font-bold uppercase tracking-widest ${text} opacity-70`}>{label}</div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className={`text-3xl font-black ${text}`}>
                            {s.ontime_rate_pct === null ? '—' : `${s.ontime_rate_pct}%`}
                          </span>
                          <span className={`text-xs font-medium ${text} opacity-70`}>on-time rate</span>
                        </div>
                        <div className="grid grid-cols-4 gap-1 mt-3 text-center text-[11px] font-semibold">
                          <div className="rounded bg-emerald-100 text-emerald-800 py-1">
                            <div className="text-base font-black">{s.early}</div>
                            <div>Early</div>
                          </div>
                          <div className="rounded bg-green-100 text-green-800 py-1">
                            <div className="text-base font-black">{s.on_time}</div>
                            <div>On time</div>
                          </div>
                          <div className="rounded bg-amber-100 text-amber-800 py-1">
                            <div className="text-base font-black">{s.slightly_late}</div>
                            <div>Slight</div>
                          </div>
                          <div className="rounded bg-red-100 text-blue-800 py-1">
                            <div className="text-base font-black">{s.late}</div>
                            <div>Late</div>
                          </div>
                        </div>
                        <div className={`mt-3 text-xs ${text} opacity-80 flex justify-between`}>
                          <span>{s.total} day{s.total === 1 ? '' : 's'} recorded</span>
                          <span className="font-mono">
                            avg Δ {s.avg_delta_min === null ? '—' : `${s.avg_delta_min > 0 ? '+' : ''}${s.avg_delta_min}m`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Daily log table */}
                <div className="border rounded-xl overflow-hidden" data-testid="punch-log-daily">
                  <div className="bg-slate-100 px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-600">
                    Daily Punch Log (last 30 days)
                  </div>
                  {punchLog.daily.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-slate-400">No punch-ins recorded yet</div>
                  ) : (
                    <div className="divide-y">
                      <div className="grid grid-cols-12 px-3 py-2 text-[11px] font-bold text-slate-500 uppercase bg-slate-50">
                        <div className="col-span-3">Date</div>
                        <div className="col-span-2">Arrival</div>
                        <div className="col-span-2">Target</div>
                        <div className="col-span-2">Δ</div>
                        <div className="col-span-3">Status</div>
                      </div>
                      {punchLog.daily.map((d) => {
                        const map = {
                          early: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: '🌟', label: 'Early' },
                          on_time: { cls: 'bg-green-100 text-green-800 border-green-300', icon: '✓', label: 'On time' },
                          slightly_late: { cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: '⚠️', label: 'Slightly late' },
                          late: { cls: 'bg-red-100 text-blue-800 border-blue-200', icon: '🚨', label: 'Late' },
                        };
                        const st = map[d.status] || map.on_time;
                        const deltaStr = d.delta_min === 0 ? '0m' : (d.delta_min > 0 ? `+${d.delta_min}m` : `${d.delta_min}m`);
                        const deltaCls = d.delta_min > 15 ? 'text-red-700' : d.delta_min > 0 ? 'text-amber-700' : 'text-emerald-700';
                        const dayLabel = (() => {
                          try {
                            return new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
                          } catch { return d.date; }
                        })();
                        return (
                          <div
                            key={d.date}
                            className="grid grid-cols-12 px-3 py-2 text-sm items-center hover:bg-slate-50"
                            data-testid={`punch-log-row-${d.date}`}
                          >
                            <div className="col-span-3 font-medium text-slate-800 flex items-center gap-1">
                              {dayLabel}
                              {d.manual && (
                                <span className="text-[9px] font-bold px-1 py-0 rounded bg-blue-100 text-blue-700 uppercase">manual</span>
                              )}
                              {d.auto_detected && !d.manual && (
                                <span className="text-[9px] font-bold px-1 py-0 rounded bg-slate-100 text-slate-600 uppercase">auto</span>
                              )}
                            </div>
                            <div className="col-span-2 font-mono font-bold text-slate-900">{d.arrival_hhmm}</div>
                            <div className="col-span-2 font-mono text-slate-500">{d.target_time}</div>
                            <div className={`col-span-2 font-mono font-bold ${deltaCls}`}>{deltaStr}</div>
                            <div className="col-span-3 flex items-center justify-between gap-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${st.cls}`}>
                                <span>{st.icon}</span>
                                {st.label}
                              </span>
                              <button
                                onClick={() => { setPunchEditDay({ date: d.date, arrival_hhmm: d.arrival_hhmm }); setPunchEditValue(d.arrival_hhmm); }}
                                className="text-slate-400 hover:text-blue-600 transition"
                                title="Edit arrival time"
                                data-testid={`punch-log-edit-${d.date}`}
                                aria-label="Edit arrival"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-slate-400 px-1">
                  <b>Status thresholds</b>: Early (&gt;10 min before target) · On time (≤target) · Slightly late (≤15 min after) · Late (&gt;15 min after).
                  On-time rate counts Early + On time.
                </p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Edit Arrival Time Modal */}
      {punchEditDay && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[80] p-4"
          onClick={() => setPunchEditDay(null)}
          data-testid="punch-edit-modal"
        >
          <Card className="p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-bold text-slate-800">Edit Arrival</h4>
                <p className="text-xs text-slate-500">{punchEditDay.date}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPunchEditDay(null)}><X className="h-4 w-4" /></Button>
            </div>
            <label className="text-xs text-slate-700 font-medium">Actual arrival (clinic local, HH:MM)</label>
            <input
              type="time"
              value={punchEditValue}
              onChange={(e) => setPunchEditValue(e.target.value)}
              className="w-full mt-1 px-3 py-2 border-2 border-slate-200 rounded-md text-base font-mono focus:border-blue-500 outline-none"
              data-testid="punch-edit-time-input"
            />
            <p className="text-[11px] text-slate-400 mt-2">
              This overrides the auto-detected arrival for this day. Status &amp; deltas will be recomputed.
            </p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setPunchEditDay(null)} data-testid="punch-edit-cancel">Cancel</Button>
              <Button
                onClick={savePunchEdit}
                disabled={!punchEditValue || punchEditValue === punchEditDay.arrival_hhmm}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="punch-edit-save"
              >
                Save
              </Button>
            </div>
          </Card>
        </div>
      )}
      {showEndDaySummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEndDaySummary(false)}>
          <Card className="p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="text-center space-y-6">
              <div>
                <h2 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  End of Day Summary
                </h2>
                {onDutyDoctor ? (
                  <p className="text-sm text-muted-foreground mt-1">
                    {onDutyDoctor.name} {onDutyDoctor.qualification}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">DR. NAZAR MD(GEN. MED.)</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-6 py-6">
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-primary">{daySummary.total}</div>
                  <div className="text-sm text-muted-foreground">Total Patients</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-green-600">{daySummary.completed}</div>
                  <div className="text-sm text-muted-foreground">Patients Seen</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-red-600">{daySummary.missed}</div>
                  <div className="text-sm text-muted-foreground">Patients Missed</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 py-4 border-y">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-blue-600">{paedsWaiting.length + paedsCompleted.length}</div>
                  <div className="text-xs text-muted-foreground">Paediatric Patients (&lt;12)</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-purple-600">{adultWaiting.length + adultCompleted.length}</div>
                  <div className="text-xs text-muted-foreground">Adult Patients</div>
                </div>
              </div>
              <div className="pt-4">
                <p className="text-lg font-semibold text-primary">K.M. Multi Speciality Clinic, Koliyanur</p>
                <p className="text-sm text-muted-foreground mt-1">Day closed on {new Date().toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}</p>
              </div>
              <Button
                onClick={() => setShowEndDaySummary(false)}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="close-summary-button"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Doctor Settings Modal */}
      {showSettings && (
        <DoctorSettings
          token={token}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Medical Certificate Modal */}
      {showCertificateModal && (
        <MedicalCertificateModal
          patient={selectedPatientForDoc}
          token={token}
          onClose={() => {
            setShowCertificateModal(false);
            setSelectedPatientForDoc(null);
          }}
        />
      )}

      {/* Prescription Modal */}
      {showPrescriptionModal && (
        <PrescriptionModal
          patient={selectedPatientForDoc}
          token={token}
          onClose={() => {
            setShowPrescriptionModal(false);
            setSelectedPatientForDoc(null);
          }}
        />
      )}

      {/* TV Display Settings (footer lines, promo, doctor photo) */}
      {showTVSettings && (
        <PromoSettings token={token} onClose={() => setShowTVSettings(false)} />
      )}

      {/* Daily Rx Anchor Set/Edit Modal */}
      {showRxPrompt && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="rx-prompt-modal">
          <Card className="w-full max-w-md bg-white shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="h-6 w-6 text-emerald-700" />
              <h3 className="text-lg font-bold text-slate-900">Today's Rx Start</h3>
            </div>
            {rxAnchorInfo && (rxAnchorInfo.anchor || rxAnchorInfo.last_assigned) && (
              <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs space-y-1">
                {rxAnchorInfo.anchor && (
                  <div className="flex justify-between"><span className="text-slate-600">Anchor set:</span><b className="text-emerald-700">{rxAnchorInfo.anchor}</b></div>
                )}
                {rxAnchorInfo.last_assigned && (
                  <div className="flex justify-between"><span className="text-slate-600">Last assigned:</span><b>{rxAnchorInfo.last_assigned}</b></div>
                )}
                {rxAnchorInfo.next_to_assign && (
                  <div className="flex justify-between"><span className="text-slate-600">Next will be:</span><b className="text-rose-700">{rxAnchorInfo.next_to_assign}</b></div>
                )}
              </div>
            )}
            <p className="text-sm text-slate-600 mb-3">
              Enter the first Rx number from your pad. All today's regular &amp; exempt patients will auto-number from here.
            </p>
            <Input
              autoFocus
              value={rxPromptValue}
              onChange={(e) => setRxPromptValue(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') saveRxAnchor(); }}
              placeholder="e.g. A2571"
              className="text-lg font-bold tracking-wider text-center"
              maxLength={10}
              data-testid="rx-prompt-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Format: Letter(s) + 1–5 digits. Editing later won't change already-assigned numbers.
            </p>
            <div className="flex justify-between gap-2 mt-5">
              {rxAnchorInfo?.anchor && (
                <Button
                  variant="outline"
                  onClick={clearRxAnchor}
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                  data-testid="rx-prompt-clear"
                >
                  Clear
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  onClick={() => { setShowRxPrompt(false); }}
                  data-testid="rx-prompt-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveRxAnchor}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="rx-prompt-confirm"
                >
                  Save
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Productivity Modal */}
      {showProductivity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-blue-800" />
                  <h2 className="text-xl font-bold">Performance & Productivity</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowProductivity(false); setPerformanceTab('today'); setPerformanceData(null); setShowOTARSettings(false); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* OTAR Settings Sub-Modal */}
              {showOTARSettings && (
                <div className="mb-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-indigo-800">OPD Target Time Settings</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowOTARSettings(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Set the target arrival time for OPD. Doctor arrival will be compared against this time to calculate punctuality.
                  </p>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="time"
                      defaultValue={opdSettings.target_arrival_time}
                      id="target-time-input"
                      className="w-40"
                      data-testid="target-time-input"
                    />
                    <Button
                      onClick={() => {
                        const input = document.getElementById('target-time-input');
                        if (input?.value) {
                          handleUpdateTargetTime(input.value);
                        }
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="save-target-time-btn"
                    >
                      Save
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Current: {opdSettings.target_arrival_time} (24hr format)
                  </p>
                </div>
              )}

              {/* Overall Grade Banner - Today */}
              {performanceTab === 'today' && todayGrade && (
                <div className={`mb-4 p-4 rounded-xl bg-gradient-to-r ${todayGrade.color} text-white shadow-lg`} data-testid="productivity-grade-banner">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{todayGrade.icon}</span>
                      <div>
                        <div className="text-xs font-medium opacity-90">Today's Performance</div>
                        <div className="text-2xl font-black tracking-tight">{todayGrade.grade}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs opacity-90">Score</div>
                      <div className="text-2xl font-bold">{todayGrade.avgScore}/4.0</div>
                    </div>
                  </div>
                  {/* Breakdown Pills */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {todayGrade.breakdown.map((item, idx) => (
                      <span key={idx} className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.score >= 3 ? 'bg-white/30' : item.score >= 2 ? 'bg-white/20' : 'bg-white/10'
                      }`}>
                        {item.metric}: {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Period Tabs */}
              <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => { setPerformanceTab('today'); setPerformanceData(null); }}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    performanceTab === 'today' ? 'bg-white shadow text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => { setPerformanceTab('anyDay'); setPerformanceData(null); }}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    performanceTab === 'anyDay' ? 'bg-white shadow text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Any Day
                </button>
                <button
                  onClick={() => { setPerformanceTab('weekly'); fetchPerformanceData('weekly'); }}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    performanceTab === 'weekly' ? 'bg-white shadow text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Weekly
                </button>
                <button
                  onClick={() => { setPerformanceTab('monthly'); fetchPerformanceData('monthly'); }}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    performanceTab === 'monthly' ? 'bg-white shadow text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Monthly
                </button>
              </div>

              {/* Export Buttons */}
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={() => exportToCSV(performanceTab)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export CSV
                </Button>
                <Button
                  onClick={() => exportToPDF(performanceTab)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid="export-pdf-btn"
                >
                  <FileDown className="h-4 w-4 mr-1" />
                  Export PDF
                </Button>
              </div>

              {/* Printable Area */}
              <div id="performance-print-area">

              {/* TODAY TAB */}
              {performanceTab === 'today' && (
                <>
                  {/* OTAR Section - On-Time Arrival Rate */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-indigo-700">
                        <Award className="h-5 w-5" />
                        <span className="font-semibold">Punctuality - On-Time Arrival Rate (OTAR)</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowOTARSettings(true)}
                        className="text-indigo-600 hover:bg-indigo-100 h-7 px-2"
                        data-testid="otar-settings-btn"
                      >
                        <Settings className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      {/* Today's Arrival */}
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-muted-foreground mb-1">Today</div>
                        {opdStarted && opdArrival ? (
                          <>
                            <div className={`text-lg font-bold ${opdArrival.is_on_time ? 'text-green-600' : 'text-red-600'}`}>
                              {new Date(opdArrival.arrival_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div className={`text-xs font-medium ${opdArrival.is_on_time ? 'text-green-500' : 'text-red-500'}`}>
                              {opdArrival.is_on_time ? '✓ On Time' : '✗ Late'}
                            </div>
                            {opdAutoDetected && (
                              <div className="text-xs text-blue-500 mt-1">📍 Auto (1st patient)</div>
                            )}
                          </>
                        ) : (
                          <div className="text-sm text-muted-foreground">Not started</div>
                        )}
                      </div>
                      
                      {/* Target Time */}
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-muted-foreground mb-1">Target Time</div>
                        <div className="text-lg font-bold text-indigo-700">{opdSettings.target_arrival_time}</div>
                        <div className="text-xs text-muted-foreground">daily target</div>
                      </div>
                      
                      {/* OTAR Rate */}
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-muted-foreground mb-1">30-Day OTAR</div>
                        {otarData && otarData.total_days > 0 ? (
                          <>
                            <div className={`text-lg font-bold ${otarData.otar_percentage >= 80 ? 'text-green-600' : otarData.otar_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {otarData.otar_percentage}%
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {otarData.on_time_count}/{otarData.total_days} days
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-muted-foreground">No data</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Recent Arrivals */}
                    {otarData && otarData.recent_arrivals && otarData.recent_arrivals.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-indigo-200">
                        <div className="text-xs text-muted-foreground mb-2">Recent Arrivals</div>
                        <div className="flex gap-1.5 flex-wrap">
                          {otarData.recent_arrivals.slice(0, 7).map((arr, idx) => (
                            <div 
                              key={idx} 
                              className={`px-2 py-1 rounded text-xs font-medium ${arr.is_on_time ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                              title={`${arr.date}: ${new Date(arr.arrival_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`}
                            >
                              {new Date(arr.date).toLocaleDateString('en-IN', { weekday: 'short' })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Total Came & Missed Ratio */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
                      <div className="flex items-center gap-2 text-cyan-700 mb-1">
                        <Users className="h-4 w-4" />
                        <span className="text-xs font-medium">Total Pt Came</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-cyan-800" data-testid="total-came">
                          {productivityStats.totalCame}
                        </span>
                        {productivityStats.trendPercent !== null && (
                          <span className={`flex items-center text-xs font-bold px-1.5 py-0.5 rounded ${
                            productivityStats.trendDirection === 'up' 
                              ? 'bg-green-100 text-green-700' 
                              : productivityStats.trendDirection === 'down' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-gray-100 text-gray-600'
                          }`}>
                            {productivityStats.trendDirection === 'up' ? (
                              <ArrowUp className="h-3 w-3 mr-0.5" />
                            ) : productivityStats.trendDirection === 'down' ? (
                              <ArrowDown className="h-3 w-3 mr-0.5" />
                            ) : null}
                            {Math.abs(productivityStats.trendPercent)}%
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-cyan-600">
                        {productivityStats.weeklyAvg !== null && productivityStats.weeklyAvg > 0
                          ? `vs ${productivityStats.weeklyAvg.toFixed(0)} avg/day` 
                          : 'patients today'}
                      </div>
                    </div>
                    <div className={`rounded-lg p-3 border ${productivityStats.missedCount > 0 ? 'bg-red-50 border-blue-100' : 'bg-green-50 border-green-200'}`}>
                      <div className={`flex items-center gap-2 mb-1 ${productivityStats.missedCount > 0 ? 'text-red-700' : 'text-green-700'}`}>
                        <Clock className="h-4 w-4" />
                        <span className="text-xs font-medium">Missed Pt Ratio</span>
                      </div>
                      <div className={`text-2xl font-bold ${productivityStats.missedCount > 0 ? 'text-blue-800' : 'text-green-800'}`}>
                        {productivityStats.missedRatio}%
                      </div>
                      <div className={`text-xs ${productivityStats.missedCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {productivityStats.missedCount} waiting
                      </div>
                    </div>
                  </div>

                  {productivityStats.totalPatients === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No completed patients yet today</p>
                      <p className="text-sm mt-1">Productivity stats will appear after first consultation</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="flex items-center gap-2 text-blue-700 mb-1">
                            <Clock className="h-4 w-4" />
                            <span className="text-xs font-medium">First Patient</span>
                          </div>
                          <div className="text-lg font-bold text-blue-800">
                            {productivityStats.firstPatientTime?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) || '-'}
                          </div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                          <div className="flex items-center gap-2 text-purple-700 mb-1">
                            <Clock className="h-4 w-4" />
                            <span className="text-xs font-medium">Last Patient</span>
                          </div>
                          <div className="text-lg font-bold text-purple-800">
                            {productivityStats.lastPatientTime?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) || '-'}
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-lg p-4 border border-blue-100">
                        <div className="flex items-center gap-2 text-blue-800 mb-2">
                          <Target className="h-5 w-5" />
                          <span className="font-semibold">Average Consultation Time</span>
                        </div>
                        <div className="text-3xl font-bold text-blue-900">
                          {formatTimer(productivityStats.averageTime)}
                        </div>
                        <div className="text-sm text-red-700 mt-1">
                          per patient ({productivityStats.totalPatients} seen)
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-red-50 rounded-lg p-3 border border-blue-100">
                          <div className="flex items-center gap-2 text-red-700 mb-1">
                            <ArrowUp className="h-4 w-4" />
                            <span className="text-xs font-medium">Longest</span>
                          </div>
                          <div className="text-xl font-bold text-blue-800">
                            {formatTimer(productivityStats.highestTime)}
                          </div>
                          {productivityStats.highestPatient && (
                            <div className="text-xs text-red-600 mt-1 truncate">
                              {productivityStats.highestPatient.token_number} - {productivityStats.highestPatient.name || 'Unnamed'}
                            </div>
                          )}
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                          <div className="flex items-center gap-2 text-amber-700 mb-1">
                            <ArrowDown className="h-4 w-4" />
                            <span className="text-xs font-medium">Shortest</span>
                          </div>
                          <div className="text-xl font-bold text-amber-800">
                            {formatTimer(productivityStats.lowestTime)}
                          </div>
                          {productivityStats.lowestPatient && (
                            <div className="text-xs text-amber-600 mt-1 truncate">
                              {productivityStats.lowestPatient.token_number} - {productivityStats.lowestPatient.name || 'Unnamed'}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="flex items-center gap-2 text-slate-700 mb-2">
                          <Zap className="h-5 w-5" />
                          <span className="font-semibold">Efficiency Metrics</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Patients/Hour:</span>
                            <span className="font-bold ml-2 text-slate-800">{productivityStats.patientsPerHour}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Total Time:</span>
                            <span className="font-bold ml-2 text-slate-800">{formatTimer(productivityStats.totalConsultationTime)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Patient Waiting Metrics Section */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-700 mb-3">
                          <Clock className="h-5 w-5" />
                          <span className="font-semibold">Patient Waiting Metrics</span>
                        </div>
                        
                        {productivityStats.waitingMetrics?.totalWithWaitData > 0 ? (
                          <>
                            {/* Wait Time Distribution */}
                            <div className="grid grid-cols-3 gap-2 mb-3">
                              <div className="bg-green-100 rounded-lg p-2 border border-green-300 text-center">
                                <div className="text-2xl font-bold text-green-700" data-testid="wait-green-count">
                                  {productivityStats.waitingMetrics.greenCount}
                                </div>
                                <div className="text-xs font-medium text-green-600">&lt;30 min</div>
                                <div className="text-xs text-green-500">On Time</div>
                              </div>
                              <div className="bg-yellow-100 rounded-lg p-2 border border-yellow-300 text-center">
                                <div className="text-2xl font-bold text-yellow-700" data-testid="wait-yellow-count">
                                  {productivityStats.waitingMetrics.yellowCount}
                                </div>
                                <div className="text-xs font-medium text-yellow-600">31-60 min</div>
                                <div className="text-xs text-yellow-500">Delayed</div>
                              </div>
                              <div className="bg-red-100 rounded-lg p-2 border border-blue-200 text-center">
                                <div className="text-2xl font-bold text-red-700" data-testid="wait-red-count">
                                  {productivityStats.waitingMetrics.redCount}
                                </div>
                                <div className="text-xs font-medium text-red-600">&gt;60 min</div>
                                <div className="text-xs text-red-500">Critical</div>
                              </div>
                            </div>
                            
                            {/* Average Wait Time */}
                            <div className="bg-white rounded-lg p-3 border border-amber-200 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Timer className="h-4 w-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">Average Wait Time</span>
                              </div>
                              <div className="text-right">
                                <span className={`text-xl font-bold ${
                                  productivityStats.waitingMetrics.avgWaitingMins <= 30 ? 'text-green-600' :
                                  productivityStats.waitingMetrics.avgWaitingMins <= 60 ? 'text-yellow-600' : 'text-red-600'
                                }`} data-testid="avg-wait-time">
                                  {productivityStats.waitingMetrics.avgWaitingMins < 60 
                                    ? `${productivityStats.waitingMetrics.avgWaitingMins} min`
                                    : `${Math.floor(productivityStats.waitingMetrics.avgWaitingMins / 60)}h ${productivityStats.waitingMetrics.avgWaitingMins % 60}m`
                                  }
                                </span>
                                <div className="text-xs text-muted-foreground">
                                  based on {productivityStats.waitingMetrics.totalWithWaitData} patients
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground text-sm">
                            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>No waiting data available yet</p>
                            <p className="text-xs mt-1">Metrics will appear after patients enter cabin</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ANY DAY TAB */}
              {performanceTab === 'anyDay' && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex-1 justify-start text-left font-normal"
                          data-testid="performance-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {performanceDate ? format(new Date(performanceDate), 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={performanceDate ? new Date(performanceDate) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              setPerformanceDate(format(date, 'yyyy-MM-dd'));
                            }
                          }}
                          initialFocus
                          toDate={new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    <Button
                      onClick={() => fetchPerformanceData('anyDay', performanceDate)}
                      disabled={!performanceDate || performanceLoading}
                    >
                      {performanceLoading ? 'Loading...' : 'Get Stats'}
                    </Button>
                  </div>
                  
                  {performanceData && (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-center">{performanceDate}</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                          <div className="text-2xl font-bold text-blue-800">{performanceData.total || 0}</div>
                          <div className="text-xs text-blue-600">Total Patients</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                          <div className="text-2xl font-bold text-green-800">{performanceData.completed || 0}</div>
                          <div className="text-xs text-green-600">Completed</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 border border-blue-100 text-center">
                          <div className="text-2xl font-bold text-blue-800">{performanceData.missed || 0}</div>
                          <div className="text-xs text-red-600">Missed</div>
                        </div>
                      </div>
                      {performanceData.total > 0 && (
                        <div className="bg-slate-50 rounded-lg p-3 border text-center">
                          <span className="text-sm text-muted-foreground">Completion Rate: </span>
                          <span className="font-bold text-green-700">
                            {((performanceData.completed / performanceData.total) * 100).toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!performanceData && !performanceLoading && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Select a date to view performance</p>
                    </div>
                  )}
                </div>
              )}

              {/* WEEKLY TAB */}
              {performanceTab === 'weekly' && (
                <div className="space-y-4">
                  {/* Week Picker */}
                  <div className="flex gap-2 items-center">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex-1 justify-start text-left font-normal"
                          data-testid="week-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedWeekStart ? `Week of ${format(new Date(selectedWeekStart), 'MMM d, yyyy')}` : 'Select week start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={selectedWeekStart ? new Date(selectedWeekStart) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              setSelectedWeekStart(format(date, 'yyyy-MM-dd'));
                            }
                          }}
                          initialFocus
                          toDate={new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    <Button
                      onClick={() => {
                        if (selectedWeekStart) {
                          fetchPerformanceData('weekly', selectedWeekStart);
                        } else {
                          fetchPerformanceData('weekly');
                        }
                      }}
                      disabled={performanceLoading}
                    >
                      {performanceLoading ? 'Loading...' : selectedWeekStart ? 'Get Week' : 'This Week'}
                    </Button>
                  </div>
                  
                  {performanceLoading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : performanceData ? (
                    <>
                      {/* Weekly Grade Banner */}
                      {getPerformanceDataGrade(performanceData) && (
                        <div className={`p-4 rounded-xl bg-gradient-to-r ${getPerformanceDataGrade(performanceData).color} text-white shadow-lg`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{getPerformanceDataGrade(performanceData).icon}</span>
                              <div>
                                <div className="text-xs font-medium opacity-90">Weekly Performance</div>
                                <div className="text-xl font-black">{getPerformanceDataGrade(performanceData).grade}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs opacity-90">Score</div>
                              <div className="text-xl font-bold">{getPerformanceDataGrade(performanceData).avgScore}/4.0</div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {getPerformanceDataGrade(performanceData).breakdown.map((item, idx) => (
                              <span key={idx} className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                item.score >= 3 ? 'bg-white/30' : 'bg-white/20'
                              }`}>
                                {item.metric}: {item.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <h3 className="font-semibold text-center text-sm text-muted-foreground">{performanceData.period}</h3>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                          <div className="text-2xl font-bold text-blue-800">{performanceData.total_patients || 0}</div>
                          <div className="text-xs text-blue-600">Total Patients</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                          <div className="text-2xl font-bold text-green-800">{performanceData.total_completed || 0}</div>
                          <div className="text-xs text-green-600">Completed</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 border border-blue-100 text-center">
                          <div className="text-2xl font-bold text-blue-800">{performanceData.total_missed || 0}</div>
                          <div className="text-xs text-red-600">Missed</div>
                        </div>
                      </div>
                      
                      {/* Daily Breakdown */}
                      <div className="bg-slate-50 rounded-lg p-4 border">
                        <h4 className="font-semibold mb-3 text-sm">Daily Breakdown</h4>
                        <div className="space-y-2">
                          {performanceData.daily_breakdown?.map((day, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-slate-200 last:border-0">
                              <span className="font-medium">{day.day_name} ({day.date})</span>
                              <div className="flex gap-3">
                                <span className="text-blue-600">{day.total} total</span>
                                <span className="text-green-600">{day.completed} done</span>
                                <span className="text-red-600">{day.missed} missed</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {performanceData.total_patients > 0 && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 text-center">
                            <div className="text-lg font-bold text-purple-800">
                              {(performanceData.total_patients / 7).toFixed(1)}
                            </div>
                            <div className="text-xs text-purple-600">Avg Patients/Day</div>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
                            <div className="text-lg font-bold text-green-800">
                              {((performanceData.total_completed / performanceData.total_patients) * 100).toFixed(1)}%
                            </div>
                            <div className="text-xs text-green-600">Completion Rate</div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}

              {/* MONTHLY TAB */}
              {performanceTab === 'monthly' && (
                <div className="space-y-4">
                  {/* Month Picker */}
                  <div className="flex gap-2 items-center">
                    <Input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="flex-1"
                      max={format(new Date(), 'yyyy-MM')}
                      data-testid="month-picker"
                    />
                    <Button
                      onClick={() => {
                        if (selectedMonth) {
                          const [year, month] = selectedMonth.split('-');
                          fetchPerformanceData('monthly', null, parseInt(year), parseInt(month));
                        } else {
                          fetchPerformanceData('monthly');
                        }
                      }}
                      disabled={performanceLoading}
                    >
                      {performanceLoading ? 'Loading...' : selectedMonth ? 'Get Month' : 'This Month'}
                    </Button>
                  </div>
                  
                  {performanceLoading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : performanceData ? (
                    <>
                      {/* Monthly Grade Banner */}
                      {getPerformanceDataGrade(performanceData) && (
                        <div className={`p-4 rounded-xl bg-gradient-to-r ${getPerformanceDataGrade(performanceData).color} text-white shadow-lg`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{getPerformanceDataGrade(performanceData).icon}</span>
                              <div>
                                <div className="text-xs font-medium opacity-90">Monthly Performance</div>
                                <div className="text-xl font-black">{getPerformanceDataGrade(performanceData).grade}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs opacity-90">Score</div>
                              <div className="text-xl font-bold">{getPerformanceDataGrade(performanceData).avgScore}/4.0</div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {getPerformanceDataGrade(performanceData).breakdown.map((item, idx) => (
                              <span key={idx} className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                item.score >= 3 ? 'bg-white/30' : 'bg-white/20'
                              }`}>
                                {item.metric}: {item.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <h3 className="font-semibold text-center">{performanceData.month}</h3>
                      <p className="text-xs text-center text-muted-foreground">{performanceData.period}</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                          <div className="text-2xl font-bold text-blue-800">{performanceData.total_patients || 0}</div>
                          <div className="text-xs text-blue-600">Total Patients</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                          <div className="text-2xl font-bold text-green-800">{performanceData.total_completed || 0}</div>
                          <div className="text-xs text-green-600">Completed</div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 border border-blue-100 text-center">
                          <div className="text-2xl font-bold text-blue-800">{performanceData.total_missed || 0}</div>
                          <div className="text-xs text-red-600">Missed</div>
                        </div>
                      </div>
                      
                      {/* Weekly Breakdown */}
                      <div className="bg-slate-50 rounded-lg p-4 border">
                        <h4 className="font-semibold mb-3 text-sm">Weekly Breakdown</h4>
                        <div className="space-y-2">
                          {performanceData.weekly_breakdown?.map((week, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-slate-200 last:border-0">
                              <span className="font-medium">Week {week.week}</span>
                              <div className="flex gap-3">
                                <span className="text-blue-600">{week.total} total</span>
                                <span className="text-green-600">{week.completed} done</span>
                                <span className="text-red-600">{week.missed} missed</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {performanceData.total_patients > 0 && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 text-center">
                            <div className="text-lg font-bold text-purple-800">
                              {(performanceData.total_patients / new Date().getDate()).toFixed(1)}
                            </div>
                            <div className="text-xs text-purple-600">Avg Patients/Day</div>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
                            <div className="text-lg font-bold text-green-800">
                              {((performanceData.total_completed / performanceData.total_patients) * 100).toFixed(1)}%
                            </div>
                            <div className="text-xs text-green-600">Completion Rate</div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}

              </div> {/* End printable area */}

              <Button
                onClick={() => { setShowProductivity(false); setPerformanceTab('today'); setPerformanceData(null); }}
                className="w-full mt-4 bg-blue-800 hover:bg-blue-900"
                data-testid="close-productivity-button"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Token System Settings Modal */}
      {showTokenSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Hash className="h-6 w-6 text-blue-600" />
                  <h2 className="text-xl font-bold">Token System Settings</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowTokenSettings(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                Choose how tokens are assigned to patients. This setting affects all new tokens generated.
              </p>

              <div className="space-y-3">
                {/* Common Token System */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    tokenSystemMode === 'common' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleUpdateTokenSystem('common')}
                  data-testid="token-mode-common"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800">Common Token System</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Linear numbering for all patients
                      </div>
                      <div className="flex gap-2 mt-2">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">A-001</span>
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">A-002</span>
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">A-003</span>
                        <span className="text-muted-foreground">...</span>
                      </div>
                    </div>
                    {tokenSystemMode === 'common' && (
                      <CheckCircle className="h-6 w-6 text-blue-500" />
                    )}
                  </div>
                </div>

                {/* Segregated Token System */}
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    tokenSystemMode === 'segregated' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleUpdateTokenSystem('segregated')}
                  data-testid="token-mode-segregated"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800">Segregated Token System</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Separate numbering by age group
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-mono">G-001</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-mono">G-002</span>
                          <span className="text-xs text-green-600">General (12+ yrs)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-sm font-mono">P-001</span>
                          <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-sm font-mono">P-002</span>
                          <span className="text-xs text-pink-600">Paediatrics (&lt;12 yrs)</span>
                        </div>
                      </div>
                    </div>
                    {tokenSystemMode === 'segregated' && (
                      <CheckCircle className="h-6 w-6 text-blue-500" />
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700">
                  <strong>Note:</strong> Changes take effect immediately for new tokens. Existing tokens will remain unchanged.
                </p>
              </div>

              <Button
                onClick={() => setShowTokenSettings(false)}
                className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                data-testid="close-token-settings-button"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Past Records Modal */}
      {showPastRecords && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-4xl bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <History className="h-6 w-6 text-blue-800" />
                  <h2 className="text-lg font-bold text-blue-900">Past Patient Records</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowPastRecords(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Date Navigation */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => changePastDate(-1)}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-800" />
                  <input
                    type="date"
                    value={pastRecordsDate}
                    onChange={(e) => {
                      setPastRecordsDate(e.target.value);
                      fetchPastRecords(e.target.value);
                    }}
                    max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => changePastDate(1)}
                  className="h-8"
                  disabled={new Date(pastRecordsDate) >= new Date(Date.now() - 86400000)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {loadingPastRecords ? (
                <div className="text-center py-8 text-gray-500">Loading records...</div>
              ) : (
                <>
                  {/* Summary Cards */}
                  {pastSummary && (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-red-50 p-3 rounded-lg border border-blue-100 text-center">
                        <div className="text-2xl font-bold text-blue-900">{pastSummary.total}</div>
                        <div className="text-xs text-red-700">Total Patients</div>
                      </div>
                      <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200 text-center">
                        <div className="text-2xl font-bold text-emerald-700">{pastSummary.completed}</div>
                        <div className="text-xs text-emerald-600">Completed</div>
                      </div>
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-center">
                        <div className="text-2xl font-bold text-amber-700">{pastSummary.missed}</div>
                        <div className="text-xs text-amber-600">Pending/Missed</div>
                      </div>
                    </div>
                  )}

                  {/* Patient List */}
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    {pastPatients.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No patients found for {new Date(pastRecordsDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-semibold">Token</th>
                            <th className="text-left p-2 font-semibold">Name</th>
                            <th className="text-left p-2 font-semibold">Age/Sex</th>
                            <th className="text-left p-2 font-semibold">Status</th>
                            <th className="text-left p-2 font-semibold">Time</th>
                            <th className="text-left p-2 font-semibold">Flags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pastPatients.map((p, idx) => (
                            <tr key={p.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="p-2 font-bold text-blue-900">{p.token_number}</td>
                              <td className="p-2">{p.name || <span className="text-gray-400 italic">No name</span>}</td>
                              <td className="p-2">{p.age} {p.sex ? `/ ${p.sex}` : ''}</td>
                              <td className="p-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  p.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  p.status === 'in_er' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {p.status === 'completed' ? 'Done' : p.status === 'in_er' ? 'ER' : p.status}
                                </span>
                              </td>
                              <td className="p-2 text-xs text-gray-600">
                                {p.created_at && new Date(p.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-2">
                                <div className="flex gap-1 flex-wrap">
                                  {p.urgent && <span className="text-xs bg-red-500 text-white px-1 rounded">URG</span>}
                                  {p.fever && <span className="text-xs bg-orange-500 text-white px-1 rounded">FVR</span>}
                                  {p.review && <span className="text-xs bg-purple-500 text-white px-1 rounded">REV</span>}
                                  {p.diabetes && <span className="text-xs bg-indigo-500 text-white px-1 rounded">DM</span>}
                                  {p.htn && <span className="text-xs bg-rose-500 text-white px-1 rounded">HTN</span>}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setShowPastRecords(false)}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Nursing Revenue Modal */}
      {showNursingRevenue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Banknote className="h-6 w-6 text-purple-600" />
                  <h2 className="text-lg font-bold text-purple-700">Nursing Revenue</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowNursingRevenue(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Period Selection Tabs */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  onClick={() => { setNursingRevenueTab('today'); fetchNursingRevenue('today', null); }}
                  variant={nursingRevenueTab === 'today' ? 'default' : 'outline'}
                  size="sm"
                  className={nursingRevenueTab === 'today' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  Today
                </Button>
                <Button
                  onClick={() => { setNursingRevenueTab('day'); fetchNursingRevenue('day', nursingDate); }}
                  variant={nursingRevenueTab === 'day' ? 'default' : 'outline'}
                  size="sm"
                  className={nursingRevenueTab === 'day' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  Any Day
                </Button>
                <Button
                  onClick={() => { 
                    setNursingRevenueTab('week'); 
                    const weekStart = getWeekStartDate(new Date().toISOString().split('T')[0]);
                    setNursingWeekStart(weekStart);
                    fetchNursingRevenue('week', weekStart); 
                  }}
                  variant={nursingRevenueTab === 'week' ? 'default' : 'outline'}
                  size="sm"
                  className={nursingRevenueTab === 'week' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  Weekly
                </Button>
                <Button
                  onClick={() => { setNursingRevenueTab('month'); fetchNursingRevenue('month', nursingMonth); }}
                  variant={nursingRevenueTab === 'month' ? 'default' : 'outline'}
                  size="sm"
                  className={nursingRevenueTab === 'month' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                >
                  Monthly
                </Button>
              </div>

              {/* Date Navigation */}
              {nursingRevenueTab === 'day' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(nursingDate);
                    d.setDate(d.getDate() - 1);
                    const newDate = d.toISOString().split('T')[0];
                    setNursingDate(newDate);
                    fetchNursingRevenue('day', newDate);
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="date"
                    value={nursingDate}
                    onChange={(e) => {
                      setNursingDate(e.target.value);
                      fetchNursingRevenue('day', e.target.value);
                    }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                  />
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(nursingDate);
                    d.setDate(d.getDate() + 1);
                    const newDate = d.toISOString().split('T')[0];
                    setNursingDate(newDate);
                    fetchNursingRevenue('day', newDate);
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {nursingRevenueTab === 'week' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(nursingWeekStart);
                    d.setDate(d.getDate() - 7);
                    const newWeek = d.toISOString().split('T')[0];
                    setNursingWeekStart(newWeek);
                    fetchNursingRevenue('week', newWeek);
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-bold text-purple-700">
                    Week of {nursingWeekStart && new Date(nursingWeekStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(nursingWeekStart);
                    d.setDate(d.getDate() + 7);
                    const newWeek = d.toISOString().split('T')[0];
                    setNursingWeekStart(newWeek);
                    fetchNursingRevenue('week', newWeek);
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {nursingRevenueTab === 'month' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const [y, m] = nursingMonth.split('-').map(Number);
                    const newDate = m === 1 ? `${y-1}-12` : `${y}-${String(m-1).padStart(2, '0')}`;
                    setNursingMonth(newDate);
                    fetchNursingRevenue('month', newDate);
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="month"
                    value={nursingMonth}
                    onChange={(e) => {
                      setNursingMonth(e.target.value);
                      fetchNursingRevenue('month', e.target.value);
                    }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                  />
                  <Button variant="outline" size="sm" onClick={() => {
                    const [y, m] = nursingMonth.split('-').map(Number);
                    const newDate = m === 12 ? `${y+1}-01` : `${y}-${String(m+1).padStart(2, '0')}`;
                    setNursingMonth(newDate);
                    fetchNursingRevenue('month', newDate);
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {nursingLoading && (
                <div className="text-center py-8 text-gray-500">Loading nursing data...</div>
              )}

              {/* Data Display */}
              {!nursingLoading && nursingData && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-gradient-to-r from-red-100 to-rose-100 p-4 rounded-xl text-center">
                      <div className="text-3xl font-black text-blue-900">{nursingData.total_revenue ? `₹${nursingData.total_revenue}` : '₹0'}</div>
                      <div className="text-sm text-red-700">Total Revenue</div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-xl text-center">
                      <div className="text-3xl font-black text-purple-700">{nursingData.total_entries || 0}</div>
                      <div className="text-sm text-purple-600">Patients Served</div>
                    </div>
                  </div>

                  {/* Service Breakdown */}
                  {nursingData.services && Object.keys(nursingData.services).length > 0 && (
                    <div className="bg-white rounded-lg border p-4 mb-4">
                      <h3 className="font-bold text-gray-700 mb-3">Service Breakdown</h3>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {Object.entries(nursingData.services).map(([type, data]) => (
                          <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <Syringe className="h-4 w-4 text-purple-600" />
                              <span className="text-sm font-medium">{data.name}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded mr-2">{data.count} done</span>
                              <span className="font-bold text-green-600">₹{data.revenue}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Daily breakdown for week/month */}
                  {(nursingRevenueTab === 'week' || nursingRevenueTab === 'month') && nursingData.daily_breakdown && Object.keys(nursingData.daily_breakdown).length > 0 && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="font-bold text-gray-700 mb-3">Daily Breakdown</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {Object.entries(nursingData.daily_breakdown).sort().map(([dateStr, data]) => (
                          <div key={dateStr} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="font-medium text-sm">{new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-600">{data.entries} patients</span>
                              <span className="font-bold text-green-600">₹{data.revenue}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No data message */}
                  {(!nursingData.services || Object.keys(nursingData.services).length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No nursing entries for this period
                    </div>
                  )}
                </>
              )}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setShowNursingRevenue(false)}
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Daily Finance Modal */}
      {showFinance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wallet className="h-6 w-6 text-blue-800" />
                  <h2 className="text-lg font-bold text-blue-900" data-testid="finance-modal-title">Daily Finance</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowFinance(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Period Selection Tabs */}
              <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'day', label: 'Any Day' },
                  { key: 'week', label: 'Weekly' },
                  { key: 'month', label: 'Monthly' },
                  { key: 'year', label: 'Annual' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setFinanceTab(tab.key);
                      if (tab.key === 'today') fetchFinanceData('today', null);
                      else if (tab.key === 'week') {
                        const ws = getWeekStartDate(new Date().toISOString().split('T')[0]);
                        setFinanceWeekStart(ws);
                        fetchFinanceData('week', ws);
                      } else if (tab.key === 'month') fetchFinanceData('month', financeMonth);
                      else if (tab.key === 'year') fetchFinanceData('year', financeYear);
                    }}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      financeTab === tab.key ? 'bg-white shadow text-blue-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`finance-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* PDF download button */}
              <div className="flex justify-end mb-3">
                <Button
                  onClick={printFinancePDF}
                  variant="outline"
                  size="sm"
                  className="text-red-700 border-blue-200 hover:bg-red-50"
                  disabled={!financeData || financeLoading}
                  data-testid="finance-pdf-btn"
                >
                  <FileDown className="h-4 w-4 mr-1" /> Print / PDF
                </Button>
              </div>

              {/* Annual year picker */}
              {financeTab === 'year' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const y = financeYear - 1;
                    setFinanceYear(y);
                    fetchFinanceData('year', y);
                  }} data-testid="finance-year-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-lg font-bold text-blue-900 tabular-nums" data-testid="finance-year-label">{financeYear}</span>
                  <Button variant="outline" size="sm" onClick={() => {
                    const now = new Date().getFullYear();
                    if (financeYear >= now) return;
                    const y = financeYear + 1;
                    setFinanceYear(y);
                    fetchFinanceData('year', y);
                  }} disabled={financeYear >= new Date().getFullYear()} data-testid="finance-year-next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Date Navigation for Any Day */}
              {financeTab === 'day' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(financeDate);
                    d.setDate(d.getDate() - 1);
                    const newDate = d.toISOString().split('T')[0];
                    setFinanceDate(newDate);
                    fetchFinanceData('day', newDate);
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="date"
                    value={financeDate}
                    onChange={(e) => {
                      setFinanceDate(e.target.value);
                      fetchFinanceData('day', e.target.value);
                    }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                    data-testid="finance-date-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(financeDate);
                    d.setDate(d.getDate() + 1);
                    const newDate = d.toISOString().split('T')[0];
                    setFinanceDate(newDate);
                    fetchFinanceData('day', newDate);
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Week Navigation */}
              {financeTab === 'week' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(financeWeekStart);
                    d.setDate(d.getDate() - 7);
                    const nw = d.toISOString().split('T')[0];
                    setFinanceWeekStart(nw);
                    fetchFinanceData('week', nw);
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-bold text-blue-900">
                    Week of {financeWeekStart && new Date(financeWeekStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => {
                    const d = new Date(financeWeekStart);
                    d.setDate(d.getDate() + 7);
                    const nw = d.toISOString().split('T')[0];
                    setFinanceWeekStart(nw);
                    fetchFinanceData('week', nw);
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Month Navigation */}
              {financeTab === 'month' && (
                <div className="flex items-center justify-center gap-4 mb-4 bg-gray-50 p-3 rounded-lg">
                  <Button variant="outline" size="sm" onClick={() => {
                    const [y, m] = financeMonth.split('-').map(Number);
                    const nm = m === 1 ? `${y-1}-12` : `${y}-${String(m-1).padStart(2, '0')}`;
                    setFinanceMonth(nm);
                    fetchFinanceData('month', nm);
                  }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="month"
                    value={financeMonth}
                    onChange={(e) => {
                      setFinanceMonth(e.target.value);
                      fetchFinanceData('month', e.target.value);
                    }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                    data-testid="finance-month-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => {
                    const [y, m] = financeMonth.split('-').map(Number);
                    const nm = m === 12 ? `${y+1}-01` : `${y}-${String(m+1).padStart(2, '0')}`;
                    setFinanceMonth(nm);
                    fetchFinanceData('month', nm);
                  }}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {financeLoading && (
                <div className="text-center py-8 text-gray-500">Loading finance data...</div>
              )}

              {/* Finance Data Display */}
              {!financeLoading && financeData && (
                <div className="space-y-4">
                  {/* Grand Total Banner */}
                  <div className="bg-gradient-to-r from-blue-900 to-emerald-700 rounded-xl p-4 text-white text-center shadow-lg" data-testid="finance-grand-total">
                    <div className="text-xs font-medium opacity-80 uppercase tracking-wider">Net Collection (excl. ECG)</div>
                    <div className="text-4xl font-black mt-1">₹{(financeData.doctor_revenue?.total_revenue || 0) + (financeData.nursing_revenue?.net_revenue || 0)}</div>
                  </div>

                  {/* Nurse-Reconciled Tally Banner (Today / Any Day only) */}
                  {financeData.tally && (financeTab === 'today' || financeTab === 'day') && (
                    <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm" data-testid="finance-tally-card">
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <Check className="h-5 w-5 text-emerald-700" />
                          <span className="font-bold text-emerald-900 text-sm">Nurse-reconciled tally</span>
                          {financeData.tally.locked && (
                            <span className="text-[10px] font-bold text-white bg-emerald-600 px-2 py-0.5 rounded uppercase">Locked</span>
                          )}
                        </div>
                        {financeData.tally.signed_by && (
                          <div className="flex items-center gap-1.5 text-sm">
                            <span className="text-emerald-700 text-xs">Signed by</span>
                            <span className="font-bold text-emerald-900 px-2 py-0.5 bg-white rounded border border-emerald-200" style={{ fontFamily: 'cursive, Brush Script MT, sans-serif' }}>
                              {financeData.tally.signed_by}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-white rounded-lg p-2 border border-emerald-100 text-center">
                          <div className="text-[10px] uppercase text-slate-500 tracking-wide">Consultation</div>
                          <div className="text-lg font-black text-emerald-700 tabular-nums">₹{financeData.tally.final_override?.consultation_total ?? 0}</div>
                        </div>
                        <div className="bg-white rounded-lg p-2 border border-emerald-100 text-center">
                          <div className="text-[10px] uppercase text-slate-500 tracking-wide">Nursing</div>
                          <div className="text-lg font-black text-emerald-700 tabular-nums">₹{financeData.tally.final_override?.nursing_total ?? 0}</div>
                        </div>
                        <div className="bg-emerald-700 rounded-lg p-2 text-white text-center">
                          <div className="text-[10px] uppercase opacity-80 tracking-wide">Grand Total</div>
                          <div className="text-lg font-black tabular-nums">₹{financeData.tally.final_override?.grand_total ?? 0}</div>
                        </div>
                      </div>

                      {/* Discrepancy vs system */}
                      {(() => {
                        const sysCons = financeData.doctor_revenue?.total_revenue || 0;
                        const sysNur = financeData.nursing_revenue?.net_revenue || 0;
                        const fCons = financeData.tally.final_override?.consultation_total ?? 0;
                        const fNur = financeData.tally.final_override?.nursing_total ?? 0;
                        const dCons = fCons - sysCons;
                        const dNur = fNur - sysNur;
                        if (dCons === 0 && dNur === 0) return null;
                        return (
                          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2 flex items-center gap-3 flex-wrap">
                            <span className="font-bold">vs System:</span>
                            {dCons !== 0 && <span>Cons {dCons > 0 ? '+' : ''}{dCons}</span>}
                            {dNur !== 0 && <span>Nursing {dNur > 0 ? '+' : ''}{dNur}</span>}
                          </div>
                        );
                      })()}

                      {financeData.tally.discrepancy_note && (
                        <div className="text-xs text-slate-700 bg-white rounded p-2 border border-slate-200">
                          <span className="font-bold text-slate-600">Note: </span>{financeData.tally.discrepancy_note}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-500 mt-2 text-right">
                        Submitted by <b>{financeData.tally.submitted_by}</b> at {new Date(financeData.tally.submitted_at).toLocaleString('en-IN')}
                      </div>
                    </div>
                  )}

                  {/* Doctor + Nursing Revenue Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Doctor Revenue */}
                    <div className="bg-red-50 rounded-xl p-4 border border-blue-100" data-testid="finance-doctor-card">
                      <div className="flex items-center gap-2 mb-3">
                        <Receipt className="h-5 w-5 text-blue-800" />
                        <span className="font-bold text-blue-900 text-sm">Consultation</span>
                      </div>
                      <div className="text-2xl font-black text-blue-900">₹{financeData.doctor_revenue?.total_revenue || 0}</div>
                      <div className="text-xs text-red-700 mt-1">{financeData.doctor_revenue?.total_patients || 0} patients</div>
                      <div className="mt-3 space-y-1.5">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Regular (₹{financeData.fee_settings?.regular_fee || feeSettings.regular_fee})</span>
                          <span className="font-bold text-blue-800">{financeData.doctor_revenue?.regular_count || 0}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Review (₹{financeData.fee_settings?.review_fee || feeSettings.review_fee})</span>
                          <span className="font-bold text-amber-700">{financeData.doctor_revenue?.review_count || 0}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">ER (₹{financeData.fee_settings?.er_fee || feeSettings.er_fee})</span>
                          <span className="font-bold text-orange-700">{financeData.doctor_revenue?.er_count || 0}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Exempted (₹0)</span>
                          <span className="font-bold text-gray-500">{financeData.doctor_revenue?.exempted_count || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Nursing Revenue */}
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-200" data-testid="finance-nursing-card">
                      <div className="flex items-center gap-2 mb-3">
                        <Syringe className="h-5 w-5 text-purple-700" />
                        <span className="font-bold text-purple-900 text-sm">Nursing</span>
                      </div>
                      <div className="text-2xl font-black text-purple-900">₹{financeData.nursing_revenue?.net_revenue || 0}</div>
                      <div className="text-xs text-purple-700 mt-1">{financeData.nursing_revenue?.total_entries || 0} entries {financeData.nursing_revenue?.ecg_revenue ? `(ECG ₹${financeData.nursing_revenue.ecg_revenue} excl.)` : ''}</div>
                      {financeData.nursing_revenue?.services && Object.keys(financeData.nursing_revenue.services).length > 0 && (
                        <div className="mt-3 space-y-1 max-h-24 overflow-y-auto">
                          {Object.entries(financeData.nursing_revenue.services).map(([key, svc]) => (
                            <div key={key} className={`flex justify-between items-center text-xs ${key === 'ecg' ? 'opacity-50 line-through' : ''}`}>
                              <span className="text-gray-600 truncate mr-2">{svc.name}</span>
                              <span className="font-bold text-purple-700 whitespace-nowrap">₹{svc.revenue}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Daily/Weekly Breakdown (for week and month tabs) */}
                  {financeData.daily_breakdown && (financeTab === 'week') && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="font-bold text-gray-700 mb-3 text-sm">Daily Breakdown</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {Object.entries(financeData.daily_breakdown).sort().map(([dateStr, data]) => (
                          <div key={dateStr} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <span className="font-medium">{new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-red-700">Dr: ₹{data.doctor_revenue}</span>
                              <span className="text-purple-700">Nr: ₹{data.nursing_revenue}</span>
                              <span className="font-bold text-gray-900">₹{data.total}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {financeData.weekly_breakdown && financeTab === 'month' && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="font-bold text-gray-700 mb-3 text-sm">Weekly Breakdown</h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {Object.entries(financeData.weekly_breakdown).map(([weekLabel, data]) => (
                          <div key={weekLabel} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <div>
                              <span className="font-medium">{weekLabel}</span>
                              <span className="text-xs text-gray-500 ml-2">{data.period}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-red-700">Dr: ₹{Math.round(data.doctor_revenue)}</span>
                              <span className="text-purple-700">Nr: ₹{Math.round(data.nursing_revenue)}</span>
                              <span className="font-bold text-gray-900">₹{Math.round(data.total)}</span>
                              {data.reconciled_days !== undefined && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${data.reconciled_days > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} title="Reconciled days">
                                  ✓ {data.reconciled_days}/{data.days || 7}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {financeData.monthly_breakdown && financeTab === 'year' && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="font-bold text-gray-700 mb-3 text-sm">Monthly Breakdown</h3>
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {Object.entries(financeData.monthly_breakdown).map(([monthKey, data]) => (
                          <div key={monthKey} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <div>
                              <span className="font-semibold text-slate-800">{data.month_name}</span>
                              <span className="text-xs text-gray-500 ml-2">{monthKey}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-red-700">Dr: ₹{Math.round(data.doctor_revenue)}</span>
                              <span className="text-purple-700">Nr: ₹{Math.round(data.nursing_revenue)}</span>
                              <span className="text-slate-500">Pts: {data.patients}</span>
                              <span className="font-bold text-gray-900 tabular-nums">₹{Math.round(data.total)}</span>
                              {data.reconciled_days !== undefined && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${data.reconciled_days > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} title="Reconciled days">
                                  ✓ {data.reconciled_days}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!financeLoading && !financeData && (
                <div className="text-center py-8 text-gray-500">
                  <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Select a period to view finance data</p>
                </div>
              )}

              {/* PDF / Print Button — handled by top button printFinancePDF */}

              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={() => setShowFinance(false)}
                data-testid="close-finance-btn"
              >
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* P&L Modal — full-screen, printable */}
      {showPnl && (
        <>
          {/* Print stylesheet — override modal positioning so full content prints */}
          <style>{`
            @media print {
              @page { size: A4; margin: 10mm; }
              /* Hide everything by default when printing */
              body > * { visibility: hidden !important; }
              /* Show only the P&L modal contents */
              [data-testid="pnl-modal"], [data-testid="pnl-modal"] * { visibility: visible !important; }
              [data-testid="pnl-modal"] {
                position: static !important;
                inset: auto !important;
                width: 100% !important;
                height: auto !important;
                overflow: visible !important;
                box-shadow: none !important;
                background: white !important;
              }
              [data-testid="pnl-modal"] .print\\:hidden { display: none !important; }
            }
          `}</style>
          <div
            className="fixed inset-0 bg-black/50 z-[45] print:hidden"
            onClick={() => setShowPnl(false)}
            data-testid="pnl-backdrop"
          />
          <div
            className="fixed inset-2 md:inset-4 z-[46] overflow-hidden flex flex-col"
            data-testid="pnl-modal"
          >
            <Card className="w-full h-full flex flex-col bg-white shadow-2xl border-blue-200 print:shadow-none print:border-0">
              <div className="p-4 md:p-5 flex-shrink-0">
                <div className="flex items-center justify-between mb-4 border-b border-blue-100 pb-3 print:hidden">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-blue-800" />
                    <h2 className="text-lg md:text-xl font-bold text-blue-900" data-testid="pnl-modal-title">Profit &amp; Loss</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.print()}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="pnl-print-btn"
                    >
                      <Printer className="h-4 w-4 mr-1" /> Print
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowPnl(false)} data-testid="pnl-close-btn">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                {/* Print-only header */}
                <div className="hidden print:block mb-4">
                  <h1 className="text-2xl font-black text-blue-900">K.M Multi-Speciality Clinic · Profit &amp; Loss</h1>
                  <p className="text-sm text-slate-600 mt-1">Generated {new Date().toLocaleString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto px-4 md:px-5 pb-5 print:overflow-visible print:!h-auto print:!flex-none">

              {/* View switcher: Summary vs Attended List */}
              <div className="flex gap-1 mb-3 bg-blue-50 p-1 rounded-lg border border-blue-200 print:hidden">
                <button
                  onClick={() => setPnlView('summary')}
                  className={`flex-1 px-3 py-2 text-xs font-bold rounded transition-colors ${pnlView === 'summary' ? 'bg-blue-700 text-white shadow' : 'text-blue-700 hover:bg-blue-100'}`}
                  data-testid="pnl-view-summary"
                >
                  P&amp;L Summary
                </button>
                <button
                  onClick={() => {
                    setPnlView('attended');
                    if (!attendedList || attendedList.date !== attendedDate) fetchAttendedList(attendedDate);
                  }}
                  className={`flex-1 px-3 py-2 text-xs font-bold rounded transition-colors ${pnlView === 'attended' ? 'bg-blue-700 text-white shadow' : 'text-blue-700 hover:bg-blue-100'}`}
                  data-testid="pnl-view-attended"
                >
                  Attended List (Pharmacy Reconcile)
                </button>
              </div>

              {/* ============ ATTENDED LIST VIEW ============ */}
              {pnlView === 'attended' && (
                <div className="space-y-3" data-testid="attended-list-panel">
                  <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-2 rounded-lg">
                    <input
                      type="date"
                      value={attendedDate}
                      onChange={(e) => { setAttendedDate(e.target.value); fetchAttendedList(e.target.value); }}
                      className="h-9 px-2 border rounded font-bold"
                      data-testid="attended-date-input"
                    />
                    <Input
                      placeholder="Search Rx / token / name…"
                      value={attendedSearch}
                      onChange={(e) => setAttendedSearch(e.target.value)}
                      className="h-9 flex-1 min-w-[160px]"
                      data-testid="attended-search-input"
                    />
                    <select
                      value={attendedFilter}
                      onChange={(e) => setAttendedFilter(e.target.value)}
                      className="h-9 px-2 border rounded text-sm font-medium"
                      data-testid="attended-filter-select"
                    >
                      <option value="all">All rows</option>
                      <option value="with_rx">With Rx only</option>
                      <option value="no_rx">Rx-eligible but no Rx</option>
                      <option value="with_nursing">With nursing services</option>
                      <option value="nursing_walkin">Nursing walk-ins</option>
                    </select>
                    <Button
                      onClick={printAttendedList}
                      size="sm"
                      variant="outline"
                      className="text-rose-700 border-rose-300"
                      disabled={!attendedList?.rows?.length}
                      data-testid="attended-print-btn"
                    >
                      <Printer className="h-4 w-4 mr-1" /> Print
                    </Button>
                  </div>

                  {attendedLoading && (
                    <div className="text-center py-8 text-gray-500">Loading attended list…</div>
                  )}

                  {!attendedLoading && attendedList && (
                    <>
                      {/* Summary banner */}
                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                          <div className="text-[10px] uppercase text-gray-500">Doctor pts</div>
                          <div className="text-lg font-black text-emerald-700">{attendedList.summary?.doctor_patients || 0}</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded p-2">
                          <div className="text-[10px] uppercase text-gray-500">Nursing walk-ins</div>
                          <div className="text-lg font-black text-blue-700">{attendedList.summary?.nursing_walkins || 0}</div>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded p-2">
                          <div className="text-[10px] uppercase text-gray-500">With Rx</div>
                          <div className="text-lg font-black text-purple-700">{attendedList.summary?.with_rx || 0}</div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded p-2">
                          <div className="text-[10px] uppercase text-gray-500">Eligible, no Rx</div>
                          <div className="text-lg font-black text-amber-700">{attendedList.summary?.rx_eligible_no_rx || 0}</div>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="max-h-[55vh] overflow-y-auto">
                          <table className="w-full text-xs" data-testid="attended-table">
                            <thead className="sticky top-0 bg-rose-50 z-10">
                              <tr className="text-rose-900">
                                <th className="text-left py-1.5 px-2">#</th>
                                <th className="text-left py-1.5 px-2">Rx</th>
                                <th className="text-left py-1.5 px-2">Token</th>
                                <th className="text-left py-1.5 px-2">Patient</th>
                                <th className="text-center py-1.5 px-2">Type</th>
                                <th className="text-right py-1.5 px-2">Fee</th>
                                <th className="text-left py-1.5 px-2">Nursing</th>
                                <th className="text-right py-1.5 px-2">Nur ₹</th>
                                <th className="text-right py-1.5 px-2">Total ₹</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const q = attendedSearch.trim().toLowerCase();
                                const filtered = (attendedList.rows || []).filter(r => {
                                  if (attendedFilter === 'with_rx' && !r.prescription_number) return false;
                                  if (attendedFilter === 'no_rx' && !r.rx_skipped) return false;
                                  if (attendedFilter === 'with_nursing' && !(r.nursing_services?.length)) return false;
                                  if (attendedFilter === 'nursing_walkin' && r.kind !== 'nursing_walkin') return false;
                                  if (q && !`${r.prescription_number || ''} ${r.token_number || ''} ${r.name || ''}`.toLowerCase().includes(q)) return false;
                                  return true;
                                });
                                if (!filtered.length) {
                                  return <tr><td colSpan={9} className="text-center py-6 text-gray-400 italic">No rows match the filter</td></tr>;
                                }
                                return filtered.map((r, i) => {
                                  const rowBg = r.kind === 'nursing_walkin' ? 'bg-blue-50' : (r.is_er ? 'bg-rose-50' : 'hover:bg-gray-50');
                                  return (
                                    <tr key={`${r.kind}-${r.token_number || i}-${i}`} className={`${rowBg} border-b border-gray-100`} data-testid={`attended-row-${i}`}>
                                      <td className="py-1.5 px-2 text-gray-500 tabular-nums">{i + 1}</td>
                                      <td className="py-1.5 px-2 font-mono font-bold">
                                        {r.prescription_number ? (
                                          <span className="text-emerald-700">{r.prescription_number}</span>
                                        ) : r.rx_skipped ? (
                                          <span className="text-rose-700">— skipped</span>
                                        ) : (
                                          <span className="text-gray-300">—</span>
                                        )}
                                      </td>
                                      <td className="py-1.5 px-2 font-mono">{r.token_number || (r.kind === 'nursing_walkin' ? <span className="text-blue-700 font-bold">Walk-in</span> : '—')}</td>
                                      <td className="py-1.5 px-2 truncate max-w-[160px]">
                                        <span className="font-medium">{r.name}</span>
                                        {r.age && <span className="text-gray-400 text-[11px]"> ({r.age}{r.sex ? '/' + r.sex : ''})</span>}
                                      </td>
                                      <td className="text-center py-1.5 px-2 uppercase font-bold text-[10px]">
                                        <span className={`px-1.5 py-0.5 rounded ${
                                          r.consultation_type === 'er' ? 'bg-rose-100 text-rose-800' :
                                          r.consultation_type === 'review' ? 'bg-blue-100 text-blue-800' :
                                          r.consultation_type === 'exempted' ? 'bg-amber-100 text-amber-800' :
                                          r.consultation_type === 'nursing_only' ? 'bg-purple-100 text-purple-800' :
                                          'bg-slate-100 text-slate-700'
                                        }`}>
                                          {r.consultation_type === 'nursing_only' ? 'NURSE' : (r.consultation_type || '').toUpperCase()}
                                        </span>
                                      </td>
                                      <td className="text-right py-1.5 px-2 tabular-nums">{r.consultation_fee ? `₹${Math.round(r.consultation_fee)}` : '—'}</td>
                                      <td className="py-1.5 px-2 text-[11px]">
                                        {r.nursing_services?.length ? (
                                          <div className="space-y-0.5">
                                            {r.nursing_services.map((s, j) => (
                                              <div key={j} className="text-blue-700">
                                                {s.name}{s.qty > 1 ? `×${s.qty}` : ''} <span className="text-gray-400">(₹{Math.round(s.cost * s.qty)})</span>
                                              </div>
                                            ))}
                                          </div>
                                        ) : <span className="text-gray-300">—</span>}
                                      </td>
                                      <td className="text-right py-1.5 px-2 tabular-nums text-blue-700">{r.nursing_total ? `₹${Math.round(r.nursing_total)}` : '—'}</td>
                                      <td className="text-right py-1.5 px-2 tabular-nums font-bold">₹{Math.round(r.total_billed || 0)}</td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                            <tfoot className="bg-rose-50 sticky bottom-0">
                              <tr className="font-bold text-rose-900">
                                <td colSpan={5} className="py-1.5 px-2 text-right">Day Totals</td>
                                <td className="text-right py-1.5 px-2 tabular-nums">₹{Math.round(attendedList.summary?.consultation_total || 0)}</td>
                                <td></td>
                                <td className="text-right py-1.5 px-2 tabular-nums">₹{Math.round(attendedList.summary?.nursing_total || 0)}</td>
                                <td className="text-right py-1.5 px-2 tabular-nums">₹{Math.round(attendedList.summary?.grand_total || 0)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>

                      <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 rounded p-2">
                        💡 <b>Pharmacy reconcile tip:</b> Print this list and tick each Rx number as you find it in the pharmacy register. Unticked Rx numbers = patients lost to pharmacy. ER patients are excluded from Rx tracking.
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ============ ORIGINAL P&L SUMMARY VIEW ============ */}
              {pnlView === 'summary' && (
              <div>
              {/* Period selector */}
              <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg">
                {[
                  { key: 'daily', label: 'Daily' },
                  { key: 'weekly', label: 'Weekly' },
                  { key: 'monthly', label: 'Monthly' },
                  { key: 'annual', label: 'Annual' }
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => { setPnlPeriod(p.key); setPnlDateStr(''); fetchPnlData(p.key, null); }}
                    className={`flex-1 px-3 py-2 text-sm font-bold rounded-md transition-colors ${
                      pnlPeriod === p.key ? 'bg-white shadow text-rose-700' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`pnl-period-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Date picker for selected period */}
              <div className="flex items-center justify-center gap-2 mb-4 bg-gray-50 p-2 rounded-lg">
                {pnlPeriod === 'daily' && (
                  <input
                    type="date"
                    value={pnlDateStr || new Date().toISOString().split('T')[0]}
                    onChange={(e) => { setPnlDateStr(e.target.value); fetchPnlData('daily', e.target.value); }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                    data-testid="pnl-date-input"
                  />
                )}
                {pnlPeriod === 'weekly' && (
                  <input
                    type="date"
                    value={pnlDateStr || getWeekStartDate(new Date().toISOString().split('T')[0])}
                    onChange={(e) => { setPnlDateStr(e.target.value); fetchPnlData('weekly', e.target.value); }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                    data-testid="pnl-week-input"
                  />
                )}
                {pnlPeriod === 'monthly' && (
                  <input
                    type="month"
                    value={pnlDateStr || new Date().toISOString().slice(0,7)}
                    onChange={(e) => { setPnlDateStr(e.target.value); fetchPnlData('monthly', e.target.value); }}
                    className="w-40 h-8 text-center font-bold border rounded px-2"
                    data-testid="pnl-month-input"
                  />
                )}
                {pnlPeriod === 'annual' && (
                  <input
                    type="number"
                    min="2020" max="2100"
                    value={pnlDateStr || new Date().getFullYear()}
                    onChange={(e) => { setPnlDateStr(e.target.value); fetchPnlData('annual', e.target.value); }}
                    className="w-32 h-8 text-center font-bold border rounded px-2"
                    data-testid="pnl-year-input"
                  />
                )}
                <span className="text-xs text-gray-500">{pnlData?.label}</span>
              </div>

              {pnlLoading && (
                <div className="text-center py-8 text-gray-500">Calculating P&amp;L...</div>
              )}

              {!pnlLoading && pnlData && (
                <div className="space-y-4">
                  {/* Card 1: System Revenue (cases + nursing) */}
                  <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4" data-testid="pnl-system-revenue-card">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-5 w-5 text-emerald-700" />
                        <span className="font-bold text-emerald-900 text-sm">1. System Revenue</span>
                      </div>
                      <span className="text-[11px] text-emerald-700">Cases seen + Nursing (excl. ECG)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-white rounded p-2 text-center border border-emerald-200">
                        <div className="text-[10px] uppercase text-gray-500">Consultation</div>
                        <div className="text-lg font-black text-emerald-700 tabular-nums">₹{Math.round(pnlData.system_revenue?.consultation_total || 0)}</div>
                        <div className="text-[10px] text-gray-500">{pnlData.system_revenue?.consultation_patients || 0} cases</div>
                      </div>
                      <div className="bg-white rounded p-2 text-center border border-emerald-200">
                        <div className="text-[10px] uppercase text-gray-500">Nursing (net)</div>
                        <div className="text-lg font-black text-emerald-700 tabular-nums">₹{Math.round(pnlData.system_revenue?.nursing_net || 0)}</div>
                        <div className="text-[10px] text-gray-500">ECG: ₹{Math.round(pnlData.system_revenue?.nursing_ecg || 0)}</div>
                      </div>
                      <div className="bg-emerald-700 rounded p-2 text-center text-white">
                        <div className="text-[10px] uppercase opacity-80">Total</div>
                        <div className="text-lg font-black tabular-nums">₹{Math.round(pnlData.system_revenue?.grand_total_net || 0)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Pharmacy Reconciled Money */}
                  <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4" data-testid="pnl-pharmacy-reconciled-card">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Pill className="h-5 w-5 text-purple-700" />
                        <span className="font-bold text-purple-900 text-sm">2. Pharmacy Reconciled Money</span>
                      </div>
                      <span className="text-[11px] text-purple-700">
                        {pnlData.pharmacy_reconciled?.reconciled_days || 0}/{pnlData.pharmacy_reconciled?.total_days || 0} days locked
                      </span>
                    </div>
                    {(pnlData.pharmacy_reconciled?.reconciled_days || 0) === 0 ? (
                      <div className="text-center py-3 text-sm text-purple-700 bg-white rounded border border-purple-200">
                        Nurse hasn&apos;t locked any tally for this period yet.
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div className="bg-white rounded p-2 text-center border border-purple-200">
                            <div className="text-[10px] uppercase text-gray-500">Consultation</div>
                            <div className="text-lg font-black text-purple-700 tabular-nums">₹{Math.round(pnlData.pharmacy_reconciled?.consultation_total || 0)}</div>
                          </div>
                          <div className="bg-white rounded p-2 text-center border border-purple-200">
                            <div className="text-[10px] uppercase text-gray-500">Nursing</div>
                            <div className="text-lg font-black text-purple-700 tabular-nums">₹{Math.round(pnlData.pharmacy_reconciled?.nursing_total || 0)}</div>
                          </div>
                          <div className="bg-purple-700 rounded p-2 text-center text-white">
                            <div className="text-[10px] uppercase opacity-80">Grand Total</div>
                            <div className="text-lg font-black tabular-nums">₹{Math.round(pnlData.pharmacy_reconciled?.grand_total || 0)}</div>
                          </div>
                        </div>
                        {/* Vs System diff */}
                        {(() => {
                          const sys = pnlData.system_revenue?.grand_total_net || 0;
                          const rec = pnlData.pharmacy_reconciled?.grand_total || 0;
                          const diff = rec - sys;
                          if (diff === 0) return null;
                          return (
                            <div className={`text-[11px] rounded p-2 mt-2 flex items-center justify-between ${diff < 0 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`}>
                              <span className="font-bold">vs System Revenue:</span>
                              <span className="tabular-nums font-bold">{diff > 0 ? '+' : ''}₹{Math.round(diff)}</span>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  {/* Card 3: Reception (Waiting Patient) Loss */}
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="pnl-reception-loss-card">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-700" />
                        <span className="font-bold text-amber-900 text-sm">3. Waiting Patient Loss (Reception)</span>
                      </div>
                      <span className="text-[11px] text-amber-700">Tokens issued − consulted</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-white rounded p-2 text-center border border-amber-200">
                        <div className="text-[10px] uppercase text-gray-500">Issued</div>
                        <div className="text-xl font-black text-gray-900 tabular-nums" data-testid="pnl-issued">{pnlData.reception_loss?.tokens_issued || 0}</div>
                      </div>
                      <div className="bg-white rounded p-2 text-center border border-amber-200">
                        <div className="text-[10px] uppercase text-gray-500">Consulted</div>
                        <div className="text-xl font-black text-emerald-700 tabular-nums" data-testid="pnl-consulted">{pnlData.reception_loss?.tokens_consulted || 0}</div>
                      </div>
                      <div className="bg-amber-700 rounded p-2 text-center text-white">
                        <div className="text-[10px] uppercase opacity-80">Lost</div>
                        <div className="text-xl font-black tabular-nums" data-testid="pnl-total-lost">{pnlData.reception_loss?.total_lost || 0}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white rounded p-2 border border-amber-200">
                        <div className="flex justify-between"><span className="text-gray-600">Stranded (waiting at EOD)</span><b>{pnlData.reception_loss?.stranded || 0}</b></div>
                        <div className="flex justify-between text-[11px] text-gray-500 mt-0.5">
                          <span>OP {pnlData.reception_loss?.stranded_breakdown?.op || 0} · ER {pnlData.reception_loss?.stranded_breakdown?.er || 0} · Rev {pnlData.reception_loss?.stranded_breakdown?.review || 0}</span>
                          <span className="font-bold text-rose-700">₹{Math.round(pnlData.reception_loss?.stranded_value || 0)}</span>
                        </div>
                      </div>
                      <div className="bg-white rounded p-2 border border-amber-200">
                        <div className="flex justify-between"><span className="text-gray-600">Deleted (token gaps)</span><b>{pnlData.reception_loss?.deleted || 0}</b></div>
                        <div className="flex justify-between text-[11px] text-gray-500 mt-0.5">
                          <span>@ ₹{pnlData.fees?.regular_fee || 200} each</span>
                          <span className="font-bold text-rose-700">₹{Math.round(pnlData.reception_loss?.deleted_value || 0)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-right text-sm">
                      <span className="text-gray-600">Projected loss: </span>
                      <span className="font-black text-rose-700 text-lg">₹{Math.round(pnlData.reception_loss?.loss_amount || 0)}</span>
                    </div>
                  </div>

                  {/* Pharmacy Missed Table (granular) */}
                  <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-4" data-testid="pnl-pharmacy-missed-card">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-rose-700" />
                        <span className="font-bold text-rose-900 text-sm">Pharmacy Missed (System vs Pharmacy)</span>
                      </div>
                    </div>
                    {((pnlData.pharmacy_reconciled?.reconciled_days || 0) === 0) ? (
                      <div className="text-center py-3 text-sm text-rose-700 bg-white rounded border border-rose-200">
                        No locked tallies — pharmacy loss can&apos;t be calculated.
                      </div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-rose-100 text-rose-900">
                            <th className="text-left py-1 px-2">Type</th>
                            <th className="text-center py-1 px-2">System</th>
                            <th className="text-center py-1 px-2">Pharmacy</th>
                            <th className="text-center py-1 px-2">Missed</th>
                            <th className="text-right py-1 px-2">Loss</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { key: 'op', label: `OP (₹${pnlData.fees?.regular_fee || 200})` },
                            { key: 'er', label: `ER (₹${pnlData.fees?.er_fee || 250})` },
                            { key: 'review', label: `Review (₹${pnlData.fees?.review_fee || 50})` }
                          ].map(r => (
                            <tr key={r.key} className="border-b border-rose-100">
                              <td className="py-1 px-2 font-medium">{r.label}</td>
                              <td className="text-center py-1 px-2 tabular-nums">{pnlData.pharmacy_missed?.[r.key]?.system || 0}</td>
                              <td className="text-center py-1 px-2 tabular-nums">{pnlData.pharmacy_missed?.[r.key]?.pharmacy || 0}</td>
                              <td className="text-center py-1 px-2 tabular-nums font-bold text-rose-700">{pnlData.pharmacy_missed?.[r.key]?.missed || 0}</td>
                              <td className="text-right py-1 px-2 tabular-nums font-bold text-rose-700">₹{Math.round(pnlData.pharmacy_missed?.[r.key]?.loss || 0)}</td>
                            </tr>
                          ))}
                          <tr className="bg-rose-200 font-bold text-rose-900">
                            <td className="py-1 px-2">Total</td>
                            <td className="text-center py-1 px-2 tabular-nums">{(pnlData.pharmacy_missed?.op?.system || 0) + (pnlData.pharmacy_missed?.er?.system || 0) + (pnlData.pharmacy_missed?.review?.system || 0)}</td>
                            <td className="text-center py-1 px-2 tabular-nums">{(pnlData.pharmacy_missed?.op?.pharmacy || 0) + (pnlData.pharmacy_missed?.er?.pharmacy || 0) + (pnlData.pharmacy_missed?.review?.pharmacy || 0)}</td>
                            <td className="text-center py-1 px-2 tabular-nums">{pnlData.pharmacy_missed?.total_missed || 0}</td>
                            <td className="text-right py-1 px-2 tabular-nums">₹{Math.round(pnlData.pharmacy_missed?.total_loss || 0)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Bottom Summary Banner */}
                  <div className="bg-gradient-to-r from-rose-700 to-red-800 rounded-xl p-4 text-white shadow-lg" data-testid="pnl-bottom-summary">
                    <div className="text-xs font-medium opacity-80 uppercase tracking-wider text-center">Total Projected Loss for {pnlData.label}</div>
                    <div className="text-4xl font-black mt-1 text-center">₹{Math.round(pnlData.grand_total_loss || 0)}</div>
                    <div className="text-[11px] opacity-80 mt-1 text-center">
                      Reception: ₹{Math.round(pnlData.reception_loss?.loss_amount || 0)} · Pharmacy: ₹{Math.round(pnlData.pharmacy_missed?.total_loss || 0)}
                    </div>
                  </div>

                  {/* Day-by-day breakdown for multi-day periods */}
                  {pnlPeriod !== 'daily' && (pnlData.daily_breakdown?.length || 0) > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200" data-testid="pnl-daily-breakdown">
                      <div className="p-2 border-b bg-gray-50 text-xs font-bold text-gray-700">
                        Day-by-day breakdown ({pnlData.daily_breakdown.length} days with activity)
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-gray-100 text-gray-700">
                            <tr>
                              <th className="text-left py-1 px-2">Date</th>
                              <th className="text-right py-1 px-2">Sys ₹</th>
                              <th className="text-right py-1 px-2">Recon ₹</th>
                              <th className="text-center py-1 px-2">Wait Lost</th>
                              <th className="text-center py-1 px-2">Pharm Miss</th>
                              <th className="text-right py-1 px-2">Loss ₹</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pnlData.daily_breakdown.map((row) => (
                              <tr key={row.date} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-1 px-2 font-mono">{row.date}</td>
                                <td className="text-right py-1 px-2 tabular-nums text-emerald-700">₹{Math.round((row.system_consultation || 0) + (row.system_nursing_net || 0))}</td>
                                <td className="text-right py-1 px-2 tabular-nums text-purple-700">{row.reconciled ? `₹${Math.round(row.reconciled_grand)}` : '—'}</td>
                                <td className="text-center py-1 px-2 tabular-nums text-amber-700 font-bold">{row.reception?.total_lost || 0}</td>
                                <td className="text-center py-1 px-2 tabular-nums text-rose-700 font-bold">{row.pharmacy ? (row.pharmacy.total_missed || 0) : '—'}</td>
                                <td className="text-right py-1 px-2 tabular-nums font-bold text-rose-700">₹{Math.round((row.reception?.loss_amount || 0) + (row.pharmacy?.total_loss || 0))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* end of P&L summary view */}
              </div>
              )}

              <Button
                variant="outline"
                className="w-full mt-4 print:hidden"
                onClick={() => setShowPnl(false)}
                data-testid="pnl-close-bottom"
              >
                Close
              </Button>
              </div>{/* /scrollable body */}
            </Card>
          </div>
        </>
      )}


      {/* Diabetic Registry Modal */}
      {showDiabeticRegistry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-purple-700" />
                  <h2 className="text-lg font-bold text-purple-900" data-testid="dm-registry-title">Diabetic Registry</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowDiabeticRegistry(false); setSelectedDiabetic(null); setDiabeticSearch(''); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="relative mb-4">
                <input
                  type="text"
                  value={diabeticSearch}
                  onChange={(e) => { setDiabeticSearch(e.target.value); fetchDiabeticRegistry(e.target.value); }}
                  placeholder="Search by name, ID, or phone..."
                  className="w-full h-9 pl-3 pr-3 border rounded-lg text-sm"
                  data-testid="dm-search-input"
                />
              </div>

              {selectedDiabetic ? (
                <div className="space-y-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDiabetic(null)} className="text-purple-700">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back to list
                  </Button>
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-black text-purple-900">{selectedDiabetic.diabetic_id}</span>
                      <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full font-bold">Diabetic</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Name</span><span className="font-bold">{selectedDiabetic.name}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Age</span><span className="font-bold">{selectedDiabetic.age || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Sex</span><span className="font-bold">{selectedDiabetic.sex || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Contact</span><span className="font-bold">{selectedDiabetic.contact_number || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Place</span><span className="font-bold">{selectedDiabetic.place || '-'}</span></div>
                    </div>
                    <div className="mt-4">
                      <h4 className="font-bold text-purple-800 text-sm mb-2">Visit History ({selectedDiabetic.visit_dates?.length || 0} visits)</h4>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {(selectedDiabetic.visit_dates || []).sort().reverse().map((vd, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs bg-white px-3 py-2 rounded border">
                            <Calendar className="h-3 w-3 text-purple-500" />
                            <span>{new Date(vd).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 mb-2">Total: {diabeticList.length} patients</div>
                  {diabeticLoading && <div className="text-center py-6 text-gray-500">Loading...</div>}
                  {!diabeticLoading && (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {diabeticList.length === 0 ? (
                        <p className="text-center text-gray-500 py-6">No diabetic patients registered</p>
                      ) : (
                        diabeticList.map(dm => (
                          <div
                            key={dm.diabetic_id}
                            onClick={() => setSelectedDiabetic(dm)}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-black text-purple-800 text-sm bg-purple-100 px-2 py-1 rounded">{dm.diabetic_id}</span>
                              <div>
                                <span className="font-bold text-gray-800 text-sm">{dm.name}</span>
                                <div className="text-xs text-gray-500">{dm.place || ''} {dm.contact_number ? `| ${dm.contact_number}` : ''}</div>
                              </div>
                            </div>
                            <span className="text-xs font-bold text-purple-700">{dm.visit_dates?.length || 0} visits</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}

              <Button variant="outline" className="w-full mt-4" onClick={() => { setShowDiabeticRegistry(false); setSelectedDiabetic(null); setDiabeticSearch(''); }}>
                Close
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Fee Settings Modal (Separate) */}
      {showFeeSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <IndianRupee className="h-6 w-6 text-blue-800" />
                  <h2 className="text-lg font-bold text-blue-900" data-testid="fee-settings-title">Consultation Fee Settings</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowFeeSettings(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Regular Consultation Fee (₹)</label>
                  <Input
                    type="number"
                    defaultValue={feeSettings.regular_fee}
                    id="regular-fee-input"
                    className="h-10"
                    data-testid="regular-fee-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Review Fee (₹)</label>
                  <Input
                    type="number"
                    defaultValue={feeSettings.review_fee}
                    id="review-fee-input"
                    className="h-10"
                    data-testid="review-fee-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">ER Fee (₹)</label>
                  <Input
                    type="number"
                    defaultValue={feeSettings.er_fee}
                    id="er-fee-input"
                    className="h-10"
                    data-testid="er-fee-input"
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-500">
                  <p>Fee Exempted is always ₹0</p>
                  <p className="mt-1">These settings are typically updated every 6 months.</p>
                </div>
                <Button
                  onClick={() => {
                    const regular = parseFloat(document.getElementById('regular-fee-input').value);
                    const review = parseFloat(document.getElementById('review-fee-input').value);
                    const er = parseFloat(document.getElementById('er-fee-input').value);
                    if (!isNaN(regular) && !isNaN(review) && !isNaN(er)) {
                      updateFeeSettings(regular, review, er);
                    }
                  }}
                  className="w-full bg-blue-800 hover:bg-blue-900 text-white"
                  data-testid="save-fee-settings-btn"
                >
                  Save Fee Settings
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Add to DM Modal - for adding existing patients to DM registry */}
      {showAddToDmModal && addToDmPatient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-md bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-6 w-6 text-purple-700" />
                  <h2 className="text-base font-bold text-purple-900" data-testid="doctor-add-to-dm-modal-title">Add to DM Registry</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddToDmModal(false); setAddToDmPatient(null); setAddToDmSearch(''); setAddToDmResults([]); }}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="bg-red-50 rounded-lg p-3 border border-blue-100 mb-4">
                <div className="flex items-center gap-2">
                  <span className="font-black text-red-700 text-sm">{addToDmPatient.token_number}</span>
                  <span className="font-bold text-slate-800">{addToDmPatient.name}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{addToDmPatient.age} &bull; {addToDmPatient.sex}</div>
              </div>

              <label className="text-xs font-bold text-purple-700 block mb-1">Search existing DM patient (ID / Phone / Name)</label>
              <Input
                type="text"
                value={addToDmSearch}
                onChange={(e) => searchAddToDm(e.target.value)}
                placeholder="Search by DM ID, phone, or name..."
                className="h-9 text-sm mb-2"
                data-testid="doctor-add-to-dm-search"
              />

              {addToDmLoading && <div className="text-xs text-purple-500 py-1">Searching...</div>}
              {addToDmResults.length > 0 && (
                <div className="max-h-40 overflow-y-auto space-y-1 mb-3">
                  {addToDmResults.map(dm => (
                    <div
                      key={dm.diabetic_id}
                      onClick={() => linkPatientToDm(addToDmPatient.id, dm.diabetic_id)}
                      className="flex items-center justify-between p-2 bg-white rounded border cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                      data-testid={`doctor-dm-result-${dm.diabetic_id}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-black text-purple-800 text-xs bg-purple-100 px-1.5 py-0.5 rounded">{dm.diabetic_id}</span>
                        <div>
                          <span className="font-bold text-gray-800 text-xs">{dm.name}</span>
                          <div className="text-xs text-gray-500">{dm.contact_number || ''} {dm.place ? `| ${dm.place}` : ''}</div>
                        </div>
                      </div>
                      <span className="text-xs text-purple-600 font-bold">Link &rarr;</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-3 mt-2">
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  onClick={() => registerPatientAsDm(addToDmPatient.id)}
                  data-testid="doctor-register-new-dm-button"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Register as New DM Patient
                </Button>
                <p className="text-xs text-gray-500 text-center mt-1">Creates a new DM registry entry with auto-generated ID</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Camp Patients Modal (doctor view with filter + PDF) */}
      {showCampEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-xl bg-white shadow-2xl my-4">
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-700" />
                  <h2 className="text-base font-bold text-green-900" data-testid="doctor-camp-title">Camp Patients</h2>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">{campPatients.length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-7 px-2 text-green-700 border-green-300 hover:bg-green-50 text-xs" onClick={downloadCampPDF} data-testid="doctor-camp-pdf">
                    <Download className="h-3 w-3 mr-1" /> PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowCampEntry(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex flex-wrap gap-2 mb-3 items-center">
                <select value={campFilterType} onChange={(e) => { setCampFilterType(e.target.value); fetchCampPatients(e.target.value, campStartDate, campEndDate); }}
                  className="h-7 rounded border px-2 text-xs">
                  <option value="">All Camps</option>
                  {CAMP_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <input type="date" value={campStartDate} onChange={(e) => { setCampStartDate(e.target.value); if (campEndDate) fetchCampPatients(campFilterType, e.target.value, campEndDate); }}
                  className="h-7 rounded border px-2 text-xs" />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" value={campEndDate} onChange={(e) => { setCampEndDate(e.target.value); if (campStartDate) fetchCampPatients(campFilterType, campStartDate, e.target.value); }}
                  className="h-7 rounded border px-2 text-xs" />
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => { setCampFilterType(''); setCampStartDate(''); setCampEndDate(''); fetchCampPatients('', '', ''); }}>
                  Reset
                </Button>
              </div>

              <div className="max-h-64 overflow-y-auto space-y-1">
                {campPatients.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No camp patients found</p>
                ) : (
                  campPatients.map(cp => (
                    <div key={cp.id} className="flex items-center justify-between p-1.5 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="font-black text-green-700 bg-green-100 px-1.5 py-0.5 rounded text-xs flex-shrink-0">{cp.serial_number}</span>
                        <span className="font-bold text-gray-800 truncate">{cp.name || 'Unnamed'}</span>
                        <span className="text-xs text-gray-500 flex-shrink-0">{cp.age} {cp.sex}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {cp.date !== new Date().toISOString().split('T')[0] && <span className="text-xs text-gray-400">{cp.date}</span>}
                        {cp.place && <span className="text-xs text-gray-400">{cp.place}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center">No consultation fee, separate from census</p>
              <Button variant="outline" className="w-full mt-3" onClick={() => setShowCampEntry(false)}>Close</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Notifications/Approvals Modal */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-yellow-600" />
                <h2 className="text-base font-bold text-gray-900" data-testid="notif-modal-title">Pending Approvals</h2>
                {notifCount > 0 && <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">{notifCount}</span>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowNotifications(false)}><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Pending Leave */}
              {pendingLeave.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-orange-700 uppercase mb-2 flex items-center gap-1">
                    <UserCheck className="h-3.5 w-3.5" /> Staff Leave ({pendingLeave.length})
                  </h4>
                  <div className="space-y-2">
                    {pendingLeave.map(r => (
                      <div key={r.id} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-gray-800">{r.staff_name}</span>
                            <span className="text-xs text-gray-500">{r.leave_date}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              r.day_type === 'half' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {r.day_type === 'half' ? `Half · ${r.half_session || ''}` : 'Full Day'}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 px-2 bg-green-500 hover:bg-green-600 text-white text-xs"
                              onClick={() => handleApproveReject('leave', r.id, 'approved')} data-testid={`approve-leave-${r.id}`}>
                              <Check className="h-3 w-3 mr-0.5" /> OK
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-red-500 border-blue-200 text-xs"
                              onClick={() => handleApproveReject('leave', r.id, 'rejected')} data-testid={`reject-leave-${r.id}`}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {r.reason && <span>Reason: {r.reason}</span>}
                          {r.replacement && <span className="ml-2 text-blue-600">Replacement: {r.replacement}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Inventory */}
              {pendingInventory.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-teal-700 uppercase mb-2 flex items-center gap-1">
                    <ClipboardList className="h-3.5 w-3.5" /> Inventory Items ({pendingInventory.length})
                  </h4>
                  <div className="space-y-2">
                    {pendingInventory.map(r => (
                      <div key={r.id} className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-bold text-gray-800">{r.item_name}</span>
                            <span className="text-xs text-teal-700 ml-2 font-bold">x{r.quantity}</span>
                            {r.notes && <span className="text-xs text-gray-400 ml-1">({r.notes})</span>}
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 px-2 bg-green-500 hover:bg-green-600 text-white text-xs"
                              onClick={() => handleApproveReject('inventory', r.id, 'approved')} data-testid={`approve-inv-${r.id}`}>
                              <Check className="h-3 w-3 mr-0.5" /> OK
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-red-500 border-blue-200 text-xs"
                              onClick={() => handleApproveReject('inventory', r.id, 'rejected')} data-testid={`reject-inv-${r.id}`}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Requested by: {r.created_by}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pendingLeave.length === 0 && pendingInventory.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Bell className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>No pending approvals</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Leave & Inventory Summary Modal */}
      {showLISummary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-3xl bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col" data-testid="li-summary-modal">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-amber-700" />
                <h2 className="text-base font-bold text-gray-900">Leave & Inventory Summary</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowLISummary(false)} data-testid="close-li-summary"><X className="h-5 w-5" /></Button>
            </div>

            {/* Period selector + actions */}
            <div className="p-3 border-b flex items-center justify-between gap-2 flex-wrap bg-gray-50">
              <div className="flex gap-1" role="tablist">
                {[
                  { key: 'day', label: 'Daily' },
                  { key: 'week', label: 'Weekly' },
                  { key: 'month', label: 'Monthly' },
                ].map(opt => (
                  <Button
                    key={opt.key}
                    size="sm"
                    variant={liSummaryPeriod === opt.key ? 'default' : 'outline'}
                    className={liSummaryPeriod === opt.key ? 'bg-amber-700 hover:bg-amber-800 text-white' : ''}
                    onClick={() => { setLISummaryPeriod(opt.key); fetchLISummary(opt.key); }}
                    data-testid={`li-period-${opt.key}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <Button
                size="sm"
                onClick={printLISummaryPDF}
                disabled={!liSummaryData || liSummaryLoading}
                className="bg-red-700 hover:bg-blue-800 text-white"
                data-testid="li-summary-print"
              >
                <FileDown className="h-4 w-4 mr-1" /> Print / PDF
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {liSummaryLoading && (
                <div className="text-center text-gray-500 py-8">Loading summary...</div>
              )}

              {!liSummaryLoading && liSummaryData && (
                <>
                  <div className="text-xs text-gray-600">
                    From <b>{liSummaryData.from_date}</b> to <b>{liSummaryData.to_date}</b>
                  </div>

                  {/* Leave section */}
                  <div>
                    <h3 className="text-sm font-bold text-orange-700 flex items-center gap-1 mb-2 border-b border-orange-200 pb-1">
                      <UserCheck className="h-4 w-4" /> Staff Leave Requests
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                      <div className="bg-gray-100 rounded p-2 text-xs"><div className="text-gray-500">Total</div><div className="text-base font-bold">{liSummaryData.leave_stats?.total || 0}</div></div>
                      <div className="bg-green-50 rounded p-2 text-xs"><div className="text-green-700">Approved</div><div className="text-base font-bold text-green-700">{liSummaryData.leave_stats?.approved || 0}</div></div>
                      <div className="bg-red-50 rounded p-2 text-xs"><div className="text-red-700">Rejected</div><div className="text-base font-bold text-red-700">{liSummaryData.leave_stats?.rejected || 0}</div></div>
                      <div className="bg-yellow-50 rounded p-2 text-xs"><div className="text-yellow-700">Pending</div><div className="text-base font-bold text-yellow-700">{liSummaryData.leave_stats?.pending || 0}</div></div>
                    </div>
                    {liSummaryData.leaves?.length > 0 ? (
                      <div className="overflow-x-auto border rounded">
                        <table className="min-w-full text-xs">
                          <thead className="bg-orange-100 text-orange-900">
                            <tr>
                              <th className="px-2 py-1 text-left">Staff</th>
                              <th className="px-2 py-1 text-left">Date</th>
                              <th className="px-2 py-1 text-left">Duration</th>
                              <th className="px-2 py-1 text-left">Reason</th>
                              <th className="px-2 py-1 text-left">Replacement</th>
                              <th className="px-2 py-1 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liSummaryData.leaves.map(r => (
                              <tr key={r.id} className="border-t">
                                <td className="px-2 py-1 font-semibold">{r.staff_name}</td>
                                <td className="px-2 py-1">{r.leave_date}</td>
                                <td className="px-2 py-1">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.day_type === 'half' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {r.day_type === 'half' ? `Half · ${r.half_session || ''}` : 'Full'}
                                  </span>
                                </td>
                                <td className="px-2 py-1">{r.reason || '-'}</td>
                                <td className="px-2 py-1">{r.replacement || '-'}</td>
                                <td className={`px-2 py-1 capitalize font-bold ${r.status === 'approved' ? 'text-green-700' : r.status === 'rejected' ? 'text-red-700' : 'text-yellow-700'}`}>{r.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 italic text-xs py-3">No leave requests in this period.</div>
                    )}
                  </div>

                  {/* Inventory section */}
                  <div>
                    <h3 className="text-sm font-bold text-teal-700 flex items-center gap-1 mb-2 border-b border-teal-200 pb-1">
                      <ClipboardList className="h-4 w-4" /> Inventory Requests
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                      <div className="bg-gray-100 rounded p-2 text-xs"><div className="text-gray-500">Total</div><div className="text-base font-bold">{liSummaryData.inventory_stats?.total || 0}</div></div>
                      <div className="bg-blue-50 rounded p-2 text-xs"><div className="text-blue-700">Total Qty</div><div className="text-base font-bold text-blue-700">{liSummaryData.inventory_stats?.total_qty || 0}</div></div>
                      <div className="bg-green-50 rounded p-2 text-xs"><div className="text-green-700">Approved</div><div className="text-base font-bold text-green-700">{liSummaryData.inventory_stats?.approved || 0}</div></div>
                      <div className="bg-red-50 rounded p-2 text-xs"><div className="text-red-700">Rejected</div><div className="text-base font-bold text-red-700">{liSummaryData.inventory_stats?.rejected || 0}</div></div>
                      <div className="bg-yellow-50 rounded p-2 text-xs"><div className="text-yellow-700">Pending</div><div className="text-base font-bold text-yellow-700">{liSummaryData.inventory_stats?.pending || 0}</div></div>
                    </div>
                    {liSummaryData.inventory?.length > 0 ? (
                      <div className="overflow-x-auto border rounded">
                        <table className="min-w-full text-xs">
                          <thead className="bg-teal-100 text-teal-900">
                            <tr>
                              <th className="px-2 py-1 text-left">Item</th>
                              <th className="px-2 py-1 text-center">Qty</th>
                              <th className="px-2 py-1 text-left">Notes</th>
                              <th className="px-2 py-1 text-left">Requested On</th>
                              <th className="px-2 py-1 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liSummaryData.inventory.map(r => (
                              <tr key={r.id} className="border-t">
                                <td className="px-2 py-1 font-semibold">{r.item_name}</td>
                                <td className="px-2 py-1 text-center">{r.quantity}</td>
                                <td className="px-2 py-1">{r.notes || '-'}</td>
                                <td className="px-2 py-1">{(r.created_at || '').slice(0, 10)}</td>
                                <td className={`px-2 py-1 capitalize font-bold ${r.status === 'approved' ? 'text-green-700' : r.status === 'rejected' ? 'text-red-700' : 'text-yellow-700'}`}>{r.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 italic text-xs py-3">No inventory requests in this period.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Review Reminders Modal */}
      {showReviews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-lg bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-600" />
                <h2 className="text-base font-bold text-gray-900" data-testid="doctor-review-title">Review Reminders</h2>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2 text-orange-700 border-orange-300 hover:bg-orange-50 text-xs"
                  onClick={printReviewPDF} data-testid="doctor-review-pdf">
                  <Download className="h-3 w-3 mr-1" /> PDF
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowReviews(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {reviewLoading && <div className="text-center py-8 text-gray-400">Loading...</div>}
              {!reviewLoading && reviewData && (
                <div className="space-y-4">
                  {reviewData.overdue.length > 0 && (
                    <div>
                      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">OVERDUE ({reviewData.overdue_count})</span>
                      <div className="space-y-1 mt-2">
                        {reviewData.overdue.map((p, i) => (
                          <div key={p.id || i} className="flex items-center justify-between p-2 bg-red-50 border border-blue-100 rounded-lg text-xs">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-bold text-blue-800 truncate">{p.name || 'Unknown'}</span>
                              <span className="text-gray-500 flex-shrink-0">{p.age} {p.sex}</span>
                              {p.diabetic_id && <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded font-bold flex-shrink-0">{p.diabetic_id}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {p.contact_number && <span className="text-blue-600">{p.contact_number}</span>}
                              <span className="font-bold text-red-600">{p.review_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {reviewData.upcoming.length > 0 && (
                    <div>
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-bold">UPCOMING — next 3 days ({reviewData.upcoming_count})</span>
                      <div className="space-y-1 mt-2">
                        {reviewData.upcoming.map((p, i) => (
                          <div key={p.id || i} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-bold text-blue-800 truncate">{p.name || 'Unknown'}</span>
                              <span className="text-gray-500 flex-shrink-0">{p.age} {p.sex}</span>
                              {p.diabetic_id && <span className="bg-purple-100 text-purple-700 px-1 py-0.5 rounded font-bold flex-shrink-0">{p.diabetic_id}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {p.contact_number && <span className="text-blue-600">{p.contact_number}</span>}
                              <span className="font-bold text-blue-700">{p.review_date}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {reviewData.upcoming.length === 0 && reviewData.overdue.length === 0 && (
                    <p className="text-center text-gray-400 py-8">No upcoming reviews</p>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* MRD - Medical Records Modal */}
      {showMRD && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl bg-white shadow-2xl my-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-800" />
                <h2 className="text-base font-bold text-blue-900" data-testid="mrd-title">Medical Records (MRD)</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowMRD(false); setMrdResults(null); setMrdSelectedProfile(null); setMrdQuery(''); }}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-4 border-b flex-shrink-0">
              <div className="flex gap-2">
                <Input
                  value={mrdQuery}
                  onChange={(e) => setMrdQuery(e.target.value)}
                  placeholder="Search by name, phone, or DM ID..."
                  className="h-9 text-sm flex-1"
                  data-testid="mrd-search-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') searchMRD(); }}
                />
                <Button onClick={searchMRD} disabled={mrdLoading} className="bg-blue-800 hover:bg-blue-900 text-white h-9 px-4" data-testid="mrd-search-button">
                  {mrdLoading ? '...' : 'Search'}
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {mrdLoading && <div className="text-center py-8 text-gray-400">Searching...</div>}

              {!mrdLoading && mrdResults && !mrdSelectedProfile && (
                <div className="space-y-2">
                  {mrdResults.profiles.length === 0 && <p className="text-center text-gray-400 py-6">No records found</p>}
                  {mrdResults.profiles.map((prof, i) => (
                    <div
                      key={`${prof.name}-${prof.contact_number}-${i}`}
                      onClick={() => setMrdSelectedProfile(prof)}
                      className="p-3 bg-white border rounded-lg cursor-pointer hover:bg-red-50 hover:border-blue-200 transition-colors"
                      data-testid={`mrd-profile-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-blue-900">{prof.name}</span>
                          <span className="text-xs text-gray-500">{prof.age} {prof.sex}</span>
                          {prof.diabetic_id && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold">{prof.diabetic_id}</span>}
                          {prof.diabetes && !prof.diabetic_id && <span className="text-xs bg-purple-100 text-purple-700 px-1 py-0.5 rounded">DM</span>}
                          {prof.htn && <span className="text-xs bg-rose-100 text-rose-700 px-1 py-0.5 rounded">HTN</span>}
                        </div>
                        <span className="text-xs font-bold text-red-700">{prof.total_visits} visit{prof.total_visits > 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {prof.contact_number && <span>{prof.contact_number}</span>}
                        {prof.place && <span>{prof.place}</span>}
                        <span>First: {prof.first_visit}</span>
                        <span>Last: {prof.last_visit}</span>
                      </div>
                    </div>
                  ))}

                  {mrdResults.dm_entries.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-xs font-bold text-purple-700 mb-2">DM Registry Matches</div>
                      {mrdResults.dm_entries.map((dm, i) => (
                        <div key={dm.diabetic_id || i} className="p-2 bg-purple-50 border border-purple-200 rounded-lg mb-1 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-black text-purple-800 bg-purple-100 px-1.5 py-0.5 rounded">{dm.diabetic_id}</span>
                            <span className="font-bold">{dm.name}</span>
                            <span className="text-gray-500">{dm.age} {dm.sex}</span>
                            {dm.contact_number && <span className="text-blue-500">{dm.contact_number}</span>}
                          </div>
                          {dm.visit_dates && <div className="text-gray-400 mt-1">Visits: {dm.visit_dates.join(', ')}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Profile - Visit History Detail */}
              {mrdSelectedProfile && (
                <div>
                  <button onClick={() => setMrdSelectedProfile(null)} className="text-xs text-blue-600 hover:text-blue-800 mb-3 flex items-center gap-1" data-testid="mrd-back">
                    &larr; Back to results
                  </button>

                  <div className="bg-gradient-to-r from-blue-900 to-emerald-700 rounded-xl p-4 text-white mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-black">{mrdSelectedProfile.name}</div>
                        <div className="text-sm opacity-80">{mrdSelectedProfile.age} {mrdSelectedProfile.sex}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black">{mrdSelectedProfile.total_visits}</div>
                        <div className="text-xs opacity-80">Total Visits</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs opacity-80">
                      {mrdSelectedProfile.contact_number && <span>Ph: {mrdSelectedProfile.contact_number}</span>}
                      {mrdSelectedProfile.place && <span>Place: {mrdSelectedProfile.place}</span>}
                      {mrdSelectedProfile.diabetic_id && <span>DM ID: {mrdSelectedProfile.diabetic_id}</span>}
                      <span>First: {mrdSelectedProfile.first_visit}</span>
                      <span>Last: {mrdSelectedProfile.last_visit}</span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {mrdSelectedProfile.diabetes && <span className="bg-purple-500 text-white px-1.5 py-0.5 rounded text-xs font-bold">DM</span>}
                      {mrdSelectedProfile.htn && <span className="bg-rose-500 text-white px-1.5 py-0.5 rounded text-xs font-bold">HTN</span>}
                    </div>
                  </div>

                  <h4 className="font-bold text-gray-700 text-sm mb-2">Visit History</h4>
                  <div className="space-y-1">
                    {mrdSelectedProfile.visits.map((v, i) => (
                      <div key={`${v.date}-${v.token_number}-${i}`} className="flex items-center justify-between p-2 bg-white border rounded text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-700 w-20">{v.date}</span>
                          <span className="font-black text-red-700">{v.token_number}</span>
                          {v.fever && <span className="text-orange-600 font-bold">FEVER</span>}
                          {v.review && <span className="text-indigo-600 font-bold">REVIEW</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 capitalize">{v.consultation_type || 'regular'}</span>
                          {v.consultation_fee > 0 && <span className="font-bold text-green-700">₹{v.consultation_fee}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard;